import React, { useState, useEffect, useRef, useMemo } from 'react';
import nlp from 'compromise';
import { AuthUser, getStoredToken, postLog } from '../utils/authApi';
import { ClientAccount, InvoiceRecord, ActiveTemplate, DiscountRule, CanvasElement, PAGE_PRESETS } from '../types';
import CanvasWorkspace from './CanvasWorkspace';
import { getCurrencyDisplay } from '../utils/currency';
import { getBestDiscountRule, computeAutoDiscount } from '../utils/discount';
import { getDefaultSchemeForCountryAndRegion, computeTaxBreakdown, buildCustomScheme, getSchemeById as getSchemeByIdBase, CustomTaxSchemeConfig } from '../utils/taxSchemes';
import { amountToWords } from '../utils/numberToWords';
import { TEMPLATE_PRESETS } from '../data/templates';
import { THERMAL_PRESETS } from '../data/thermalTemplates';
import {
  Plus, Trash2, Zap, RotateCcw, CheckCircle, Search,
  ShoppingCart, User, Tag, ChevronDown, ChevronUp,
  Package, Scan, CreditCard, Banknote, Smartphone, X,
  Minus, Receipt, TrendingUp, Clock, AlertCircle,
  Monitor, Printer, Settings
} from 'lucide-react';
import QuickScannerModal from './QuickScannerModal';
import { TemplatePreviewCanvas } from './QuickInvoiceView';
import { inspectTaxId, TaxIdInfo } from '../utils/taxId';
import StatePicker from './StatePicker';
import { getPermissions } from '../utils/permissions';
import POSReceiptRenderer from './POSReceiptRenderer';
import BilingualInvoiceRenderer from './BilingualInvoiceRenderer';
import { printElementUSB, printReceiptUSB, isUSBPrinterConnected, connectUSBPrinter, tryAutoReconnectUSB, getUSBPrinterName } from '../utils/usbPrinter';
import type { PrintJob } from '../utils/bluetoothPrinter';
import { COUNTRY_CODES as WORLD_COUNTRY_DIAL_CODES, POPULAR_COUNTRIES as POPULAR_COUNTRIES_LIST } from '../utils/countryCodes';


// ── Helper: Values that must never reach the translation backend ──────────────
export function isLiteralFieldValue(v: string): boolean {
  const s = (v || '').trim();
  if (!s) return true;
  if (/^[\d\s+\-–—().#:/\\%,]+$/.test(s)) return true;
  if (/^\d[\d\-/.]*\s+(to|till|until|-|–)\s+\d[\d\-/.]*$/i.test(s)) return true;
  if (/^[A-Za-z0-9][A-Za-z0-9\-_/.#]{2,}$/.test(s) && /\d/.test(s)) return true;
  return false;
}

const ARABIC_INDIC_DIGITS: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
function toArabicIndicDigits(text: string): string {
  return text.replace(/[0-9]/g, d => ARABIC_INDIC_DIGITS[d]);
}

export const STAFF_FIELD_KEYS = new Set(['staff_name', 'staff', 'cashier']);
export function isStaffField(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[\s\-_.]+/g, '_');
  return STAFF_FIELD_KEYS.has(normalized);
}

function omitStaffKeys(values: Record<string, string>): Record<string, string> {
  const cleaned = { ...values };
  Array.from(STAFF_FIELD_KEYS).forEach(k => delete cleaned[k]);
  return cleaned;
}

// ── Dynamic template field patcher ──────────────────────────────────────────
function patchDynamicFields(
  text: string,
  dynamicValues?: Record<string, string>
): string {
  if (!dynamicValues || !Object.keys(dynamicValues).length) {
    return text;
  }

  let result = text;

  for (const [key, value] of Object.entries(dynamicValues)) {
    if (!value?.trim()) continue;

    const cleanValue = value.trim();
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    result = result.replace(
      new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'gi'),
      cleanValue
    );

    const labelPattern = key
      .split('_')
      .filter(Boolean)
      .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('[\\s\\/\\.\\(\\)#_-]*');

    result = result.replace(
      new RegExp(`(${labelPattern})\\s*:\\s*\\[[^\\]]*\\]`, 'gi'),
      (_, label) => `${label}: ${cleanValue}`
    );

    result = result.replace(
      new RegExp(`(${labelPattern})\\s*:\\s*_{2,}`, 'gi'),
      (_, label) => `${label}: ${cleanValue}`
    );

    result = result.replace(
      new RegExp(`(${labelPattern})\\s*:\\s*[-—]{1,3}`, 'gi'),
      (_, label) => `${label}: ${cleanValue}`
    );

    result = result.replace(
      new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*\\.{2,}`, 'gi'),
      (_, label) => `${label}: ${cleanValue}`
    );

    result = result.replace(
      new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*\\{[^}]*\\}`, 'gi'),
      (_, label) => `${label}: ${cleanValue}`
    );

    result = result.replace(
      new RegExp(
        `\\b(${labelPattern})\\b\\s*:(?=\\s*\\n|\\s{2,}[A-Za-z][A-Za-z\\s]{1,20}:|\\s*$)`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );
  }

  return result;
}

const RECEIPT_TYPE_MAP: Record<string, string> = {
  'Grocery Supermarket — Thermal Receipt': 'GRC',
  'Clinic & Pharmacy — Thermal Receipt': 'CLN',
  'Restaurant & Food — Thermal Receipt': 'RST',
  'Retail Shop — Thermal Receipt': 'RTL',
  'Cafe & Coffee Shop — Thermal Receipt': 'CAF',
  'Pharmacy / Medical Store — Thermal Receipt': 'PHR',
  'Fast Food / QSR — Thermal Receipt': 'QSR',
  'Salon & Beauty Parlour — Thermal Receipt': 'SLN',
  'Petrol Pump / Fuel Station — Thermal Receipt': 'FUL',
  'Gym & Fitness Center — Thermal Receipt': 'GYM',
  'Hotel & Lodge — Thermal Receipt': 'HTL',
  'Electronics Store — Thermal Receipt': 'ELC',
  'Hardware & Tools Shop — Thermal Receipt': 'HRD',
  'Mobile Recharge & DTH — Thermal Receipt': 'RCH',
  'Stationery & Book Shop — Thermal Receipt': 'STN',
  'Laundry & Dry Cleaning — Thermal Receipt': 'LDR',
  'Auto Workshop / Garage — Thermal Receipt': 'GRG',
  'Vegetable & Fruit Vendor — Thermal Receipt': 'VEG',
  'Optician & Eyewear — Thermal Receipt': 'OPT',
  'Courier & Logistics — Thermal Receipt': 'CRR',
  'Bakery & Sweet Shop — Thermal Receipt': 'BKR',
  'Tailor & Boutique — Thermal Receipt': 'ORD',
  'Parking Lot & Valet — Thermal Receipt': 'PKG',
  'School & Coaching Centre — Thermal Receipt': 'SCH',
  'Jewelry Store — Thermal Receipt': 'JWL',
  'Swimming Pool & Sports Club — Thermal Receipt': 'SPT',
  'Veterinary Clinic — Thermal Receipt': 'VET',
  'Florist & Gift Shop — Thermal Receipt': 'FLR',
  'Printing & Xerox Shop — Thermal Receipt': 'PRT',
  'Event Ticket & Cinema — Thermal Receipt': 'EVT',
};

// Cleaned up: All templates now use the universal {staff_name} placeholder

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

// Built-in catalog fields that can become table columns (both Tax choices show the header "Tax")
const BUILTIN_CATALOG_COLS: { key: string; label: string; header?: string; get: (p: any) => string }[] = [
  { key: 'cat_barcode',     label: 'Barcode',     get: p => p.barcode || '' },
  { key: 'cat_brand',       label: 'Brand',       get: p => p.brand || '' },
  { key: 'cat_category',    label: 'Category',    get: p => p.category || '' },
  { key: 'cat_unit',        label: 'Unit',        get: p => p.unit || '' },
  { key: 'cat_tax_b2c',     label: 'Tax (B2C)',   header: 'Tax', get: p => p.tax_rate ? `${p.tax_rate}%` : '' },
  { key: 'cat_tax_b2b',     label: 'Tax (B2B)',   header: 'Tax', get: p => { const r = (p.b2b_tax_rate !== undefined && p.b2b_tax_rate !== null && p.b2b_tax_rate !== '') ? p.b2b_tax_rate : p.tax_rate; return r ? `${r}%` : ''; } },
  { key: 'cat_tax_name',    label: 'Tax Name',    get: p => p.tax_name || '' },
  { key: 'cat_tax_type',    label: 'Tax Type',    get: p => p.tax_inclusive ? 'Inclusive' : 'Exclusive' },
  { key: 'cat_stock',       label: 'Stock',       get: p => p.stock_quantity != null ? String(p.stock_quantity) : '' },
  { key: 'cat_description', label: 'Description', get: p => p.description || '' },
];

// Puts each added column right after the template column the user chose
// (no choice = at the end). Added columns are always centered.
function placePosColumns(baseCols: any[], newCols: any[]) {
  const result = [...baseCols];
  const newKeys = new Set(newCols.map((c: any) => c.key));
  newCols.forEach((nc: any) => {
    const { after, ...col } = nc;
    col.align = 'center';
    let idx = after ? result.findIndex((c: any) => c.key === after) : -1;
    if (idx >= 0) {
      while (idx + 1 < result.length && newKeys.has(result[idx + 1].key)) idx++;
      result.splice(idx + 1, 0, col);
    } else {
      result.push(col);
    }
  });
  return result;
}

// On thermal paper, keep Qty/Rate/Amt wide enough to avoid "Q.." and "₹550.\n00";
// the space comes out of the Item column.
function fitThermalCols(cols: any[], pageWidth: number) {
  if (pageWidth > 320) return cols;
  const MIN: Record<string, number> = { qty: 14, rate: 22, total: 24 };
  const out = cols.map((c: any) => ({ ...c, width: c.width || 12 }));
  let deficit = 0;
  out.forEach((c: any) => {
    const m = MIN[c.key];
    if (m && c.width < m) { deficit += m - c.width; c.width = m; }
  });
  const desc = out.find((c: any) => c.key === 'description');
  if (desc) {
    const give = Math.min(deficit, Math.max(0, desc.width - 28));
    desc.width -= give;
  }
  const total = out.reduce((s: number, c: any) => s + c.width, 0) || 100;
  return out.map((c: any) => ({ ...c, width: (c.width * 100) / total }));
}

// Master set of countries with sub-national tax regions/states/provinces —
// mirrors QuickInvoiceView's REGIONAL_TAX_COUNTRIES exactly.
const REGIONAL_TAX_COUNTRIES = new Set([
  'IN', 'US', 'CA', 'AU', 'BR', 'AR', 'PK', 'ES', 'PT', 'CH', 'NG',
  'MX', 'CO', 'ID', 'PE', 'MY', 'PH', 'CL', 'CN', 'RU', 'EC', 'VN',
  'DE', 'IT', 'FI', 'NO', 'DK', 'AT', 'BA', 'KR', 'TH', 'IL',
]);




// Recomputes subtotal/tax/discount from ALREADY-SAVED elements instead of
// live matchedAccount/autoDiscountRule state. Once an invoice is saved,
// the account's visitCount/totalSpend can change (loyalty tier bump) —
// re-deriving from live state after that point silently changes the
// invoice's numbers on every re-render. Read back what was actually
// frozen into the table at save time instead.
function getFrozenTotals(elements: any[] | null | undefined) {
  const tableEl = elements?.find((el: any) => el.type === 'table');
  if (!tableEl) return null;

  let subtotal = 0;
  const lineBases: { base: number; taxRate: number }[] = [];
  (tableEl.rows || []).forEach((r: any) => {
    const qty = parseFloat(String(r.values.qty)) || 0;
    const rate = parseFloat(String(r.values.rate)) || 0;
    const lineTotal = qty * rate;
    const rawB2b = r.values.b2bTaxRate ?? r.values.b2b_tax_rate;
    const taxRate = rawB2b !== undefined && rawB2b !== null
      ? parseFloat(String(rawB2b)) || 0
      : parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
    const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
    const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
    lineBases.push({ base, taxRate });
    subtotal += base;
  });

  const discounts = tableEl.discounts || [];
  const loyaltyLine = discounts.find((d: any) => d._isAuto || d.label === 'Loyalty Discount' || /loyalty/i.test(d.label || ''));
  const manualDiscount = discounts
    .filter((d: any) => d !== loyaltyLine)
    .reduce((s: number, d: any) => s + subtotal * (d.rate / 100), 0);
  const autoDiscountAmount = loyaltyLine 
    ? (loyaltyLine.amount ?? (subtotal * (loyaltyLine.rate / 100))) 
    : 0;
  const totalDiscount = manualDiscount + autoDiscountAmount;

  const discountRatio = subtotal > 0 ? Math.min(1, totalDiscount / subtotal) : 0;
  const productTaxTotal = lineBases.reduce((s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0);
  const taxableValue = subtotal - totalDiscount;
  const taxTotal = (tableEl.taxes || []).reduce((s: number, t: any) => s + taxableValue * (t.rate / 100), 0);
  const shipping = tableEl.shippingCost || 0;

  return {
    subtotal,
    productTaxTotal,
    taxTotal,
    shipping,
    autoDiscountAmount,
    autoDiscountLabel: loyaltyLine?.label,
    grandTotal: taxableValue + productTaxTotal + taxTotal + shipping,
  };
}

// Read-only totals for the A4/Letter CanvasWorkspace preview — reuses the
// exact same frozen-totals math already used for print/PDF (getFrozenTotals)
// instead of a second, possibly-drifting calculation just for this preview.
function computeCanvasTotalsForElement(el: CanvasElement) {
  const frozen = getFrozenTotals([el]) || {
    subtotal: 0, productTaxTotal: 0, taxTotal: 0, shipping: 0, autoDiscountAmount: 0, grandTotal: 0,
  };
  return {
    subtotal: frozen.subtotal,
    productTax: frozen.productTaxTotal,
    tax: frozen.taxTotal,
    discount: frozen.autoDiscountAmount,
    shipping: frozen.shipping,
    total: frozen.grandTotal,
  };
}
// Auto-expands any background card/shape behind the customer text block
// and pushes the table down so the text never spills out or collides with the table.
function reflowClientBlockAndShapes(elements: any[]): any[] {
  if (!elements || elements.length === 0) return elements;

  // 1. Locate the customer/Bill-To block
  const clientTextEl = elements.find(
    (el: any) =>
      el.type === 'text' &&
      el.textContent &&
      (/bill\s*to/i.test(el.textContent) ||
       /client\s*details/i.test(el.textContent) ||
       /tax\s*id/i.test(el.textContent) ||
       /patient/i.test(el.textContent) ||
       /customer/i.test(el.textContent))
  );

  let updatedElements = [...elements];
  let clientBottom = 0;

  if (clientTextEl) {
    const textY = clientTextEl.y || 0;
    const textH = clientTextEl.height || 0;
    const textX = clientTextEl.x || 0;
    const textW = clientTextEl.width || 0;
    clientBottom = textY + textH;

    // 2. Expand any shape/card/box sitting behind this customer text element
    updatedElements = updatedElements.map((el: any) => {
      if (el === clientTextEl) return el;

      const isShape = el.type === 'shape' || el.type === 'box' || el.type === 'rectangle' || el.shapeType === 'rectangle';
      if (!isShape) return el;

      const shapeX = el.x || 0;
      const shapeY = el.y || 0;
      const shapeW = el.width || 0;
      const shapeH = el.height || 0;

      // Check if shape encompasses the text element
      const xOverlap = shapeX <= textX + 25 && (shapeX + shapeW) >= (textX + textW - 25);
      const yStartsAbove = shapeY <= textY + 15;
      const isBehindOrContains = xOverlap && yStartsAbove && (shapeY + shapeH >= textY);

      if (isBehindOrContains) {
        const paddingBottom = 16;
        const neededShapeHeight = Math.max(shapeH, (clientBottom - shapeY) + paddingBottom);
        const newShape = { ...el, height: neededShapeHeight };
        clientBottom = Math.max(clientBottom, shapeY + neededShapeHeight);
        return newShape;
      }
      return el;
    });
  }

  // 3. If the expanded client block/shape reaches close to the table, push the table down
  const table = updatedElements.find((el: any) => el.type === 'table');
  if (table && clientBottom > 0) {
    const tableY = table.y || 0;
    const MIN_GAP_BEFORE_TABLE = 18; // clean breathing gap above table

    if (tableY < clientBottom + MIN_GAP_BEFORE_TABLE) {
      const shiftDown = (clientBottom + MIN_GAP_BEFORE_TABLE) - tableY;

      updatedElements = updatedElements.map((el: any) => {
        if ((el.y || 0) >= tableY - 2) {
          return { ...el, y: (el.y || 0) + shiftDown };
        }
        return el;
      });
    }
  }

  return updatedElements;
}

// Shrinks/grows the table box to fit its real rows, reflows customer blocks & shapes,
// and moves everything below it up/down to preserve consistent spacing.
function tightenTable(elements: any[]): any[] {
  // Step A: Reflow customer box + background shape + push table if needed
  const preAdjusted = reflowClientBlockAndShapes(elements);

  const table = preAdjusted.find((el: any) => el.type === 'table');
  if (!table) return preAdjusted;

  const HEADER_H = 26, ROW_H = 22, LINE_H = 16, PAD = 24;
  const rows = (table.rows || []).length;
  const totalLines = 3 + (table.taxes || []).length + (table.discounts || []).length + (table.shippingCost ? 1 : 0);
  const needed = HEADER_H + rows * ROW_H + totalLines * LINE_H + PAD;
  const GAP_AFTER_TABLE = 12; // px between table end and next element
  const tableY = table.y || 0;
  const oldBottom = tableY + (table.height || 0);
  const newBottom = tableY + needed;

  const below = preAdjusted.filter((el: any) => el !== table && (el.y || 0) >= oldBottom - 5);
  const minY = below.length ? Math.min(...below.map((el: any) => el.y || 0)) : 0;
  const originalGap = Math.max(0, minY - oldBottom);
  const desiredGap = Math.min(GAP_AFTER_TABLE, originalGap);
  const shift = below.length ? newBottom + desiredGap - minY : 0;

  return preAdjusted.map((el: any) => {
    if (el === table) return { ...el, height: needed };
    if (below.includes(el)) return { ...el, y: (el.y || 0) + shift };
    return el;
  });
}
// POS receipts don't currently author 'serial' elements, but CanvasWorkspace
// requires this prop — safe fallback so it never throws if one shows up.
function formatCanvasSerialNumber(settings: any): string {
  if (!settings) return '';
  const digits = settings.digits ?? 4;
  const num = String(settings.current ?? 1).padStart(digits, '0');
  return `${settings.prefix || ''}${num}${settings.suffix || ''}`;
}
// "Subtotal", "TOTAL", "Item", etc. translate to the same thing every time for
// a given language — cache per-language so repeat translate runs (every cart
// edit while Bilingual is ON) reuse the result instantly instead of paying
// 12 sequential network round trips again.
const staticLabelCache: Record<string, Record<string, string>> = {};

const APP_URL = window.location.origin;
function detectCountryCode(): string {
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && WORLD_COUNTRY_DIAL_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && WORLD_COUNTRY_DIAL_CODES[cached]) return cached;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Tokyo':'JP','Asia/Seoul':'KR',
    'Asia/Shanghai':'CN','Asia/Hong_Kong':'HK','Asia/Taipei':'TW',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Paris':'FR','Europe/Berlin':'DE',
    'Europe/Amsterdam':'NL','Europe/Madrid':'ES','Europe/Rome':'IT','Europe/Zurich':'CH',
    'America/New_York':'US','America/Chicago':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Sao_Paulo':'BR','America/Mexico_City':'MX',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

// Universal 240+ country phone parser: handles all dial code and national number lengths
function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && WORLD_COUNTRY_DIAL_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (WORLD_COUNTRY_DIAL_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  // 1. Explicit International Number (+1..., +91..., +65..., +971..., etc.)
  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(WORLD_COUNTRY_DIAL_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  // 2. Domestic Trunk Zero check (e.g. UK "07...", Australia "04...") — except Italy (+39) where 0 is required
  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  // 3. Number typed without '+' that already includes dial code (only if exceeding 10 digits)
  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length > 10) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  // 4. Standard local number
  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

// Universal normalizer for duplicate prevention: matches full international E.164 digits without arbitrary slicing
export function normalizePhoneForMatching(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const cleanNum = parsed.nationalNumber.replace(/\D/g, '');
  if (!cleanNum) return '';
  return `${parsed.dialCode}${cleanNum}`;
}

// Validates what the cashier actually typed in the box and returns clean +country+number
export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const total = `${parsed.dialCode}${national}`;

  // Cashier must type at least 4 digits in the box (world's shortest domestic numbers)
  // Total length with dial code must be 7 to 15 digits (ITU world standard)
  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${parsed.dialCode}${national}`;
}

function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allCodes = Object.keys(WORLD_COUNTRY_DIAL_CODES);
  const filtered = search
    ? allCodes.filter(c => c.toLowerCase().includes(search.toLowerCase()) || WORLD_COUNTRY_DIAL_CODES[c]?.includes(search.replace('+','')))
    : [...POPULAR_COUNTRIES_LIST, ...allCodes.filter(c => !POPULAR_COUNTRIES_LIST.includes(c))];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} type="button" style={{
        display: 'flex', alignItems: 'center', gap: 4, background: '#f8fafc',
        border: '1.5px solid #cbd5e1', borderRadius: '8px 0 0 8px',
        padding: '9px 10px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
        color: '#0f172a', minWidth: 72, whiteSpace: 'nowrap',
      }}>
        +{WORLD_COUNTRY_DIAL_CODES[value] || '91'} <span style={{ fontSize: 9, opacity: 0.5 }}>▾</span>
      </button>

      {open && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)', width: 220, maxHeight: 260,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8 }}>
            <input autoFocus placeholder="Search country or +code..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(c => (
              <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: c === value ? '#f0f9ff' : 'transparent',
                  border: 'none', padding: '7px 12px', cursor: 'pointer', fontSize: 12, color: '#374151', textAlign: 'left',
                }}>
                <span style={{ fontWeight: 700, color: '#6366f1', minWidth: 40 }}>+{WORLD_COUNTRY_DIAL_CODES[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PhoneInput({ value, onChange, inputStyle, defaultCountry }: { value: string; onChange: (v: string) => void; inputStyle?: React.CSSProperties; defaultCountry?: string }) {
  const parsed = parsePhoneValue(value, defaultCountry);
  const [country, setCountry] = React.useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    if (defaultCountry && WORLD_COUNTRY_DIAL_CODES[defaultCountry]) return defaultCountry;
    return detectCountryCode();
  });
  const [number, setNumber] = React.useState(() => parsed.nationalNumber);
  const lastEmitted = React.useRef(value);
  const isSyncing = React.useRef(false);

  React.useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    isSyncing.current = true;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry);
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = WORLD_COUNTRY_DIAL_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    const cleanNum = raw.replace(/\D/g, '');
    const dialCode = (WORLD_COUNTRY_DIAL_CODES[country] || '91').replace(/\D/g, '');

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        style={{
          flex: 1, border: '1.5px solid #cbd5e1', borderLeft: 'none',
          borderRadius: '0 8px 8px 0', padding: '9px 12px',
          fontSize: 13, outline: 'none', background: '#fff',
          fontWeight: 600, color: '#0f172a', boxSizing: 'border-box',
          ...inputStyle,
        }}
      />
    </div>
  );
}
// ─── Types ────────────────────────────────────────────────────────────────────

interface CartItem {
  id: string;
  productId?: number;
  barcode?: string;
  name: string;
  rate: number;
  qty: number;
  unit?: string;
  taxRate?: number;
  taxName?: string;
  b2bTaxRate?: number;
  b2bTaxName?: string;
  taxInclusive?: boolean;
  customColAr?: Record<string, string>;
}

interface Product {
  id: number;
  name: string;
  barcode: string;
  rate: number;
  unit?: string;
  category?: string;
  brand?: string;
  stock_quantity?: number;
  tax_rate?: number;
  tax_name?: string;
  b2b_tax_rate?: number;
  b2b_tax_name?: string;
  tax_inclusive?: boolean;
  tax_scheme_id?: string;
}

interface TaxLine { label: string; rate: number; }
interface DiscountLine { label: string; rate: number; }

interface POSViewProps {
  authUser: AuthUser | null;
  outletId?: number | null;
  moduleStatuses?: Partial<Record<string, string>>;
  accounts: ClientAccount[];
  bills: InvoiceRecord[];
  setBills: React.Dispatch<React.SetStateAction<InvoiceRecord[]>>;
  customTemplates: ActiveTemplate[];
  activeCurrency: string;
  onAddLog: (action: string, details: string) => void;
  onPostSave?: (invoice: InvoiceRecord) => void;
  onExportPDF?: (invoice: InvoiceRecord, returnBlobInsteadOfDownload?: boolean) => Promise<any>;
  onSendInvoiceWhatsApp?: (invoice: InvoiceRecord, phone: string) => Promise<void>;
  onAddAccount?: (account: Omit<ClientAccount, 'id' | 'createdAt'>) => void;
  discountRules?: DiscountRule[];
  onUpdateAccount?: (account: ClientAccount) => void;
  /** Preloads the cart + table number when handed off from TableLayoutView's "Send to POS" */
  initialCart?: { tableNumber: string; items: { productId?: number; name: string; rate: number; qty: number }[] };
}

// ─── Shared styles (matching QuickInvoice) ───────────────────────────────────

const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 800, color: '#475569',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 5,
};

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1.5px solid #cbd5e1', fontSize: 13, color: '#0f172a',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: '16px 18px',
};

const posToolbarDivider: React.CSSProperties = {
  width: 1,
  height: 24,
  alignSelf: 'center',
  background: 'rgba(255,255,255,0.25)',
  margin: '0 3px',
};

const posToolbarBtn = (active = false, disabled = false): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  height: 36,
  padding: '0 12px',
  borderRadius: 10,
  border: active ? '1px solid rgba(255,255,255,0.55)' : '1px solid rgba(255,255,255,0.18)',
  background: active ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.14)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'background 0.12s ease',
});

// ─── Payment mode badge ───────────────────────────────────────────────────────

const PAY_MODES = [
  { key: 'cash', label: 'Cash', icon: <Banknote size={13} /> },
  { key: 'card', label: 'Card', icon: <CreditCard size={13} /> },
  { key: 'upi',  label: 'UPI',  icon: <Smartphone size={13} /> },
] as const;

// ─── Main Component ───────────────────────────────────────────────────────────

export default function POSView({
  authUser,outletId = null, moduleStatuses, accounts, bills, setBills, customTemplates,
  activeCurrency, onAddLog, onPostSave, onExportPDF, onSendInvoiceWhatsApp, onAddAccount,
  discountRules, onUpdateAccount, initialCart,
}: POSViewProps) {
const [registerId] = useState(() => {
  let id = localStorage.getItem('cloudairambo_register_id');
  if (!id) {
    id = 'reg_' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('cloudairambo_register_id', id);
  }
  return id;
});
const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth < 768);
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);
const [mobileTab, setMobileTab] = useState<'products' | 'cart' | 'preview'>('products');
const isPanning = useRef(false);
const panStart = useRef({ x: 0, y: 0 });
const panOffset = useRef({ x: 0, y: 0 });
const [panPos, setPanPos] = useState({ x: 0, y: 0 });
const [handToolActive, setHandToolActive] = useState(false);
  // ── Product catalog ──────────────────────────────────────────────────────
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // ── Staff / Cashier ───────────────────────────────────────────────────────
  const [waiterName, setWaiterName] = useState(() => authUser?.name?.trim() || 'Staff');
  useEffect(() => {
    setWaiterName(authUser?.name?.trim() || 'Staff');
  }, [authUser?.id, authUser?.name]);

  // ── Template selection: Synchronously loads from localStorage so it renders on frame 1 ──
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() => {
    const cachedDefault = outletId ? localStorage.getItem(`cloudairambo_pos_default_tpl_${outletId}`) : null;
    return cachedDefault || localStorage.getItem('cloudairambo_pos_template') || THERMAL_PRESETS[0]?.id || THERMAL_PRESETS[0]?.name || '';
  });

  const allTemplates: ActiveTemplate[] = useMemo(() => [
    ...THERMAL_PRESETS.map(t => ({ ...t, id: t.id || t.name })),
    ...TEMPLATE_PRESETS.filter(t => t.pageSize !== 'THERMAL_80MM').map(t => ({ ...t, id: t.id || t.name })),
    ...customTemplates.map(t => ({ ...t, pageSize: (t as any).pageSize || (t as any).page_size || 'A4' })),
  ], [customTemplates]);

  // ── Template Fields State ─────────────────────────────────────────────────
  const [dynamicValues, setDynamicValues] = useState<Record<string, string>>({});
  const [dynamicFieldLabelsAr, setDynamicFieldLabelsAr] = useState<Record<string, string>>({});

  const selectedTemplateObj = useMemo(() => {
    return allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
  }, [allTemplates, selectedTemplateId]);

  const dynamicFields = useMemo(() => {
    if (!selectedTemplateObj) return [];
    const fields: { key: string; label: string; placeholder: string }[] = [];
    const seen = new Set<string>();

    const SKIP = /^(bill to$|client$|customer$|date$|due date|time|invoice|receipt|notes|terms|po$|bill no|from|sender|currency|gstin|dl number|scan|thank|total|subtotal|amount|tax|discount|shipping|payment|bank|account|ifsc|swift|upi|vat|website|http|fssai|www|reg no|license|waiter|cashier|server|operator|staff|doctor|barista)/i;

    const allText = selectedTemplateObj.elements
      .filter((el: any) => el.type === 'text' && el.textContent && !/[\u0600-\u06FF]/.test(el.textContent))
      .map((el: any) => el.textContent!)
      .join('\n');

    const normalized = allText
      .replace(/[A-Za-z\s\/\.\(\)#]{1,25}?:\s*\{staff_name\}/gi, '')
      .replace(/^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*[-:]+\s*$/gm, '$1:')
      .replace(/^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:-\s*/gm, '$1: ')
      .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:]{0,40}?)\s{2,}(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n')
      .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:|]{0,40}?)\s*\|\s*(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n')
      .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:•]{0,40}?)\s*•\s*(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n');

    let m;
    const bracketPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\[([^\]]{0,40})\]/gm;
    while ((m = bracketPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = m[2].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 1 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` }); }
    }

    const underscorePattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{0,25}?)\s*:\s*_{2,}/gm;
    while ((m = underscorePattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 1 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    const dashPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*[-—]{1,3}\s*$/gm;
    while ((m = dashPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    const ellipsisPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\.{2,}|…\s*$/gm;
    while ((m = ellipsisPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    const curlyPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\{([^}]{0,40})\}/gm;
    while ((m = curlyPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = m[2].trim();
      if (/^(staff_name|invoice_number|date|time)$/i.test(rawValue)) continue;
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` }); }
    }

    const barePattern = /^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*$/gm;
    while ((m = barePattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    const KNOWN = ['deliver to','deliver','ship to','recipient','from','sender','age','gender','table no','table',
      'reg','ref no','ref','ward','bed','diagnosis','order no','order','seat','batch','expiry','mrp','hsn',
      'serial','token','ticket','room','floor no','section','zone','counter','shift','vehicle','driver','captain',
      'member','loyalty','points','coupon','voucher','booking','reservation','check in','check out','pax','covers','delivery','rider'
    ];
    for (const keyword of KNOWN) {
      const keywordRegex = new RegExp(`\\b(${keyword.replace(/\s/g, '[\\s\\.]*')})\\s*:\\s*([^\\n:]{0,40}?)(?=\\s{2,}[A-Za-z]|\\n|$)`, 'gi');
      while ((m = keywordRegex.exec(normalized)) !== null) {
        const rawLabel = m[1].trim();
        const rawValue = (m[2] || '').trim();
        if (SKIP.test(rawLabel)) continue;
        const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
        if (!seen.has(key)) {
          seen.add(key);
          const displayLabel = rawLabel.replace(/\b\w/g, c => c.toUpperCase());
          fields.push({ key, label: displayLabel, placeholder: rawValue && !/^\[|^_+$/.test(rawValue) ? rawValue : `Enter ${displayLabel}` });
        }
      }
    }

    try {
      const doc = nlp(normalized);
      const nounPhrases: string[] = doc.nouns().out('array') as string[];
      for (const phrase of nounPhrases) {
        const clean = phrase.trim().toLowerCase();
        if (clean.split(' ').length > 2) continue;
        const labelRegex = new RegExp(`\\b${clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i');
        if (!labelRegex.test(allText)) continue;
        if (SKIP.test(clean)) continue;
        const key = clean.replace(/\s+/g, '_');
        if (!seen.has(key)) {
          seen.add(key);
          const displayLabel = phrase.trim().replace(/\b\w/g, c => c.toUpperCase());
          fields.push({ key, label: displayLabel, placeholder: `Enter ${displayLabel}` });
        }
      }
    } catch {}

    return fields;
  }, [selectedTemplateObj]);

  useEffect(() => {
    if (!selectedTemplateObj) return;
    const prefilled: Record<string, string> = {};
    const allText = selectedTemplateObj.elements
      .filter((el: any) => el.type === 'text' && el.textContent)
      .map((el: any) => el.textContent!)
      .join('\n');

    const pairPattern = /([A-Za-z][A-Za-z\s\/\.\(\)#]{1,25}?)\s*:\s*([^:\n]{0,40}?)(?=\s{2,}[A-Za-z]|\n|$)/g;
    let match;
    while ((match = pairPattern.exec(allText)) !== null) {
      const rawLabel = match[1].trim();
      const rawValue = (match[2] || '').trim();
      const skip = /^(bill to|client|date|due date|time|invoice|receipt|notes|terms|po|bill no|from|sender|attn|attention|currency|settlement|creation|gstin|dl|scan|thank|hsr|layout|http|www|total|subtotal|amount|vat|tax|discount|shipping|payment|bank|account|ifsc|swift|upi)/i;
      if (skip.test(rawLabel) || rawLabel.length < 1 || /\d/.test(rawLabel)) continue;
      if (rawValue.split(/\s+/).filter(Boolean).length > 5) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\/\.\(\)#]+/g, '_');
      if (rawValue && !/^\[|^__|^\{/.test(rawValue)) {
        prefilled[key] = rawValue;
      }
    }
    const loggedInStaff = waiterName || authUser?.name?.trim() || 'Staff';
    prefilled['staff_name'] = loggedInStaff;
    setDynamicValues(prefilled);
  }, [selectedTemplateObj, waiterName, authUser?.name]);

  // ── Table columns (Custom + Hidden Base Columns with localStorage persistence) ──
  const [posColumns, setPosColumns] = useState<{ id: string; header: string; key: string; align: 'left' | 'center' | 'right'; after?: string }[]>(() => {
    try {
      const saved = localStorage.getItem('pos_custom_columns');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('pos_hidden_columns');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Automatically save column preferences to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('pos_custom_columns', JSON.stringify(posColumns));
      localStorage.setItem('pos_hidden_columns', JSON.stringify(hiddenColumns));
    } catch {}
  }, [posColumns, hiddenColumns]);

  const [showAddPosCol, setShowAddPosCol] = useState(false);
  const [newPosColName, setNewPosColName] = useState('');

  const catalogColumnOptions = useMemo(() => {
    const customKeys = new Set<string>();
    products.forEach((p: any) => Object.keys(p.custom_fields || {}).forEach(k => customKeys.add(k)));
    const builtinLabels = new Set(BUILTIN_CATALOG_COLS.map(c => c.label.toLowerCase()));
    return [
      ...BUILTIN_CATALOG_COLS.map(c => ({ key: c.key, label: c.label, header: c.header || c.label })),
      ...Array.from(customKeys)
        .filter(k => !builtinLabels.has(k.toLowerCase()))
        .map(k => ({ key: k.toLowerCase().replace(/\s+/g, '_'), label: k, header: k })),
    ];
  }, [products]);

  // Value of a column for one cart item, read from its catalog product
  const getPosColValue = (item: CartItem, col: { key: string; header: string }): string => {
    // 1. Loose matching (String conversion) to prevent ID type mismatch (number vs string)
    const p: any = products.find(pr => String(pr.id) === String(item.productId))
      || products.find(pr => item.barcode && pr.barcode === item.barcode)
      || products.find(pr => pr.name.trim().toLowerCase() === item.name.trim().toLowerCase());
    if (!p) return '';

    const builtin = BUILTIN_CATALOG_COLS.find(c => c.key === col.key);
    if (builtin) return builtin.get(p);

    // 2. Parse custom_fields safely in case the API returned a JSON string
    let fields: Record<string, any> = {};
    if (typeof p.custom_fields === 'string') {
      try { fields = JSON.parse(p.custom_fields); } catch {}
    } else if (p.custom_fields && typeof p.custom_fields === 'object') {
      fields = p.custom_fields;
    }

    // 3. Direct key or header check
    if (fields[col.key] != null && fields[col.key] !== '') return String(fields[col.key]);
    if (fields[col.header] != null && fields[col.header] !== '') return String(fields[col.header]);

    // 4. Case-insensitive & space-insensitive fallback (e.g., "shelf no" vs "Shelf_No")
    const targetNorm = col.header.toLowerCase().replace(/[\s_-]/g, '');
    for (const [k, v] of Object.entries(fields)) {
      if (k.toLowerCase().replace(/[\s_-]/g, '') === targetNorm && v != null && v !== '') {
        return String(v);
      }
    }

    return '';
  };

  // Maps values to both col.key ('shelf_no') and col.header ('Shelf No')
  // so whatever property the table renderer queries, it finds the value.
  const posColValues = (item: CartItem) =>
    Object.fromEntries(
      posColumns.flatMap(col => {
        const val = getPosColValue(item, col);
        return [
          [col.key, val],
          [col.header, val],
        ];
      })
    );

  // Template columns of the active template's table
  const templateTableCols: any[] = useMemo(() => {
    const t = allTemplates.find(t => t.id === selectedTemplateId);
    return (((t as any)?.elements || (t as any)?.layout?.elements || []).find((el: any) => el.type === 'table') as any)?.columns || [];
  }, [allTemplates, selectedTemplateId]);

  const mainColKey = (templateTableCols.find((c: any) => c.key === 'description') || templateTableCols[0])?.key || 'description';

  // Toggleable base columns detected dynamically from template
  const toggleableBase: { key: string; label: string }[] = useMemo(() => {
    return templateTableCols.length > 0
      ? templateTableCols
          .filter((c: any) => c.key && c.key !== mainColKey)
          .map((c: any) => ({ key: c.key, label: c.originalHeader || c.header || c.key }))
      : [
          { key: 'qty', label: 'Qty' },
          { key: 'rate', label: 'Rate' },
          { key: 'total', label: 'Amt' },
        ];
  }, [templateTableCols, mainColKey]);

  // Adds custom columns and filters out hidden base columns
  const buildPosColumns = (baseCols: any[] = []) => {
    const visibleBase = baseCols.filter((c: any) => c.key === mainColKey || !hiddenColumns.includes(c.key));
    const baseKeys = new Set(visibleBase.map((c: any) => c.key));
    const add = posColumns.filter(c => !baseKeys.has(c.key));
    if (add.length === 0) return visibleBase;
    const isThermalSel = (allTemplates.find(t => t.id === selectedTemplateId)?.pageSize || '').startsWith('THERMAL');
    const perCol = isThermalSel ? 14 : 12;
    const totalNew = perCol * add.length;
    const baseTotal = visibleBase.reduce((s: number, c: any) => s + (c.width || 0), 0) || 100;
    const shrink = Math.max(0, 100 - totalNew) / baseTotal;
    const rebalanced = visibleBase.map((c: any) => ({ ...c, width: (c.width || 0) * shrink }));
    return fitThermalCols(
      placePosColumns(rebalanced, add.map(c => ({ id: c.id, key: c.key, header: c.header, align: c.align, width: perCol, after: c.after }))),
      isThermalSel ? 302 : 794
    );
  };
// Adds the extra POS columns to a (possibly translated) template's table.
// Safe to call repeatedly: buildPosColumns skips keys already present.
const withPosColumns = (tpl: any) => tpl ? {
  ...tpl,
  elements: (tpl.elements || []).map((el: any) =>
    el.type === 'table' ? { ...el, columns: buildPosColumns((el.columns || []).filter((c: any) => !posColumns.some(p => p.key === c.key && !c.arabicHeader))) } : el
  ),
} : tpl;

// ── Universal Customer Details Formatter ────────────────────────────────────
function buildClientBlock(
  data: { name: string; phone: string; email: string; address: string; taxId: string },
  inc: { name: boolean; phone: boolean; email: boolean; address: boolean; taxId: boolean },
  isBilingual = false
): { en: string; tr?: string } {
  const isReal = Boolean(data.name && data.name.trim() && data.name.toLowerCase() !== 'walk-in customer');
  const enName = inc.name ? (isReal ? data.name.trim() : 'Walk-in Customer') : '';
  const trName = inc.name
    ? (isReal ? ((window as any).__pos_client_name_ar || data.name.trim()) : ((window as any).__pos_walkin_label || 'Walk-in Customer'))
    : '';

  const enLines: string[] = [];
  const trLines: string[] = [];

  if (enName) {
    enLines.push(enName);
    if (isBilingual) trLines.push(trName);
  }
  if (inc.address && data.address?.trim()) {
    enLines.push(data.address.trim());
    if (isBilingual) trLines.push((window as any).__pos_client_address_ar || data.address.trim());
  }
  if (inc.taxId && data.taxId?.trim()) {
    enLines.push(`Tax ID: ${data.taxId.trim()}`);
    if (isBilingual) {
      const taxPrefix = ((window as any).__pos_buyer_tax_label || 'Tax ID').replace(/:$/, '');
      trLines.push(`${taxPrefix}: ${data.taxId.trim()}`);
    }
  }
  if (inc.email && data.email?.trim() && data.email.includes('@')) {
    enLines.push(data.email.trim());
    if (isBilingual) trLines.push(data.email.trim());
  }
  if (inc.phone && data.phone?.trim()) {
    enLines.push(`Ph: ${data.phone.trim()}`);
    if (isBilingual) trLines.push(`Ph: ${data.phone.trim()}`);
  }

  return {
    en: enLines.join('\n'),
    tr: isBilingual ? trLines.join('\n') : undefined,
  };
}

// ── Pure Non-Destructive Token Resolver ─────────────────────────────────────
function resolveElementTokens(
  el: any,
  tokens: {
    invoiceNumber: string;
    staffName: string;
    staffNameAr?: string;
    clientDetailsEn: string;
    clientDetailsTr?: string;
    clientNameEn?: string;
    readyBy?: string;
    notes?: string;
    notesAr?: string;
    invoiceDate?: string;
    dynamicValues?: Record<string, string>;
    dynamicValuesAr?: Record<string, string>;
    dynamicFieldLabelsAr?: Record<string, string>;
  }
) {
  if (el.type === 'signature') {
    return {
      ...el,
      signatureName: (el.signatureName || tokens.staffName).replace(/\{staff_name\}/gi, tokens.staffName),
      arabicSignatureName: (el.arabicSignatureName || tokens.staffNameAr || tokens.staffName).replace(
        /\{staff_name\}/gi,
        tokens.staffNameAr || tokens.staffName
      ),
    };
  }

  if (el.type !== 'text' || !el.textContent) return el;

  let text = el.textContent;
  let textAr = el.arabicContent;

  const activeInvNum = tokens.invoiceNumber || '{invoice_number}';
  const patientVal = tokens.dynamicValues?.patient?.trim() || '';
  const petVal = tokens.dynamicValues?.pet?.trim() || '';
  const studentVal = tokens.dynamicValues?.student?.trim() || '';

  text = text
    .replace(/\{invoice_number\}/gi, activeInvNum)
    .replace(/\bPENDING\b/gi, activeInvNum)
    .replace(/\{staff_name\}/gi, tokens.staffName)
    .replace(/\{client_details\}/g, tokens.clientDetailsEn)
    // Patient, Pet, Student use their own dynamic fields — NEVER clientName!
    .replace(/Patient:\s*\[(?:Name)?\]/gi, patientVal ? `Patient: ${patientVal}` : 'Patient: ______')
    .replace(/Pet:\s*\[(?:Name)?\]/gi, petVal ? `Pet: ${petVal}` : 'Pet: ______')
    .replace(/Student:\s*\[(?:Name)?\]/gi, studentVal ? `Student: ${studentVal}` : 'Student: ______')
    .replace(/\[Patient\]/gi, patientVal || '______')
    .replace(/\[Pet\]/gi, petVal || '______')
    .replace(/\[Student\]/gi, studentVal || '______')
    // Only Customer / Client gets clientName
    .replace(/(Customer|Client):\s*\[Name\]/gi, `$1: ${tokens.clientNameEn || 'Walk-in Customer'}`)
    .replace(/\[(Name|Client|Customer)\]/gi, tokens.clientNameEn || 'Walk-in Customer');

  if (textAr) {
    textAr = textAr
      .replace(/\{invoice_number\}/gi, activeInvNum)
      .replace(/\bPENDING\b/gi, activeInvNum)
      .replace(/\{staff_name\}/gi, tokens.staffNameAr || tokens.staffName)
      .replace(/\{client_details\}/g, tokens.clientDetailsTr || tokens.clientDetailsEn)
      .replace(/:\s*\[(Name|नाम|الاسم|nom|nombre|naam)\]/gi, `: ${tokens.clientNameEn || 'Walk-in Customer'}`)
      .replace(/:\s*\[[^\]]+\]/g, `: ${tokens.clientNameEn || 'Walk-in Customer'}`)
      .replace(/\[(Name|नाम|الاسم|nom|nombre|naam)\]/gi, tokens.clientNameEn || 'Walk-in Customer');
  }

  if (tokens.invoiceDate) {
    text = text
      .replace(/\{date\}/gi, tokens.invoiceDate)
      .replace(/(?<!Due\s)Date:\s*[\d]{1,4}[\/\-\.][\d]{1,2}[\/\-\.][\d]{2,4}/gi, `Date: ${tokens.invoiceDate}`);
    if (textAr) {
      textAr = textAr
        .replace(/\{[^}]*(?:date|दिनांक|तारीख|التاريخ|fecha|datum)[^}]*\}/gi, tokens.invoiceDate)
        .replace(/(?<!Due\s)(Date|दिनांक|तारीख|التاريخ|Fecha|Datum):\s*(?:\{[^}]+\}|[\d]{1,4}[\/\-\.][\d]{1,2}[\/\-\.][\d]{2,4})/gi, `$1: ${tokens.invoiceDate}`);
    }
  }

  // Handle Ready By and Notes cleanly
  if (tokens.readyBy && /^ready\s*by\s*:/im.test(text)) {
    text = text.replace(/^ready\s*by\s*:.*$/im, `Ready By: ${tokens.readyBy}`);
  }
  if (tokens.notes && (text.toLowerCase().startsWith('notes:') || text.toLowerCase().startsWith('terms:'))) {
    text = `Notes:\n${tokens.notes}`;
    if (tokens.notesAr) textAr = tokens.notesAr;
  }

  // Inject Template Field Values
  if (tokens.dynamicValues) {
    text = patchDynamicFields(text, tokens.dynamicValues);
  }

  if (textAr && tokens.dynamicValues) {
    textAr = patchDynamicFields(textAr, tokens.dynamicValues);
    for (const [key, value] of Object.entries(tokens.dynamicValues)) {
      if (!value) continue;
      const labelPattern = key.replace(/_/g, '[\\s\\/\\.\\(\\)#]*');
      const arLabel = tokens.dynamicFieldLabelsAr?.[key];
      const arValue = tokens.dynamicValuesAr?.[key] || value;

      if (arLabel) {
        const escLabel = arLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        textAr = textAr.replace(
          new RegExp(`(${escLabel})[ \\t]*:?[ \\t]*(?:\\[[^\\]]*\\]|_{2,}|[-—]{1,3}|[^\\n]*?)(?=[ \\t]{2,}\\S|\\n|$)`, 'g'),
          (_m: string, l: string) => `${l}: ${arValue}`
        );
      }
      textAr = textAr.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_: string, l: string) => `${l}: ${arValue}`);
      textAr = textAr.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*_{2,}`, 'gi'), (_: string, l: string) => `${l}: ${arValue}`);
      textAr = textAr.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*[-—]{1,3}`, 'gi'), (_: string, l: string) => `${l}: ${arValue}`);
    }
  }

  // Auto-expand height so adding Tax ID, Address, Phone never clips the bottom line
  const lineCount = text.split('\n').length;
  const fontSize = el.fontSize || 11;
  const neededHeight = Math.ceil(lineCount * (fontSize * 1.5) + 12);

  return {
    ...el,
    textContent: text,
    height: Math.max(el.height || 0, neededHeight),
    ...(textAr !== undefined ? { arabicContent: textAr } : {}),
  };
}

const patchLiveClientBlock = (
  tpl: any,
  inc: any,
  name: string,
  email: string,
  phone: string,
  address: string,
  taxId: string
) => {
  if (!tpl || !tpl.elements) return tpl;

  const clientBlock = buildClientBlock(
    { name, phone, email, address, taxId },
    inc,
    Boolean(tpl.elements.some((e: any) => e.arabicContent))
  );

  const resolved = tpl.elements.map((el: any) =>
    resolveElementTokens(el, {
      invoiceNumber: 'PREVIEW',
      staffName: waiterName || authUser?.name?.trim() || 'Staff',
      staffNameAr: (window as any).__pos_staff_name_ar,
      clientDetailsEn: clientBlock.en,
      clientDetailsTr: clientBlock.tr,
      clientNameEn: inc.name ? name : '',
    })
  );

  return {
    ...tpl,
    elements: tightenTable(resolved),
  };
};
const [showStatus, setShowStatus] = useState(true);
const [showAmountInWords, setShowAmountInWords] = useState(() =>
  localStorage.getItem('cloudairambo_pos_amount_words') === 'true'
);
// Loyalty/auto discount always reduces the taxable value before tax is
// calculated — standard GST/VAT invoicing math, matching QuickInvoiceView.
// Not user-configurable: whether a discount applies at all is controlled
// per-client (Accounts → Exempt from auto-discounts) and per-rule
// (Manage Discount Rules), so a second toggle here just duplicated that
// control in a different screen.
const loyaltyReducesTax = true;
  // ── Cart ─────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartItem[]>(() =>
    initialCart
      ? initialCart.items.map((item, i) => ({
          id: `ci-table-${Date.now()}-${i}`,
          productId: item.productId,
          name: item.name,
          rate: item.rate,
          qty: item.qty,
        }))
      : []
  );
  const [previewZoom, setPreviewZoom] = useState(1);
const [lastSyncedElements, setLastSyncedElements] = useState<any[]>([]);
const [lastSyncedTemplate, setLastSyncedTemplate] = useState<any>(null);

  // ── Client ───────────────────────────────────────────────────────────────
  const [clientSearch, setClientSearch] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [phoneResetKey, setPhoneResetKey] = useState(0);
  const [includeFields, setIncludeFields] = useState({
    name: true,
    phone: true,
    email: true,
    address: true,
    taxId: true,
  });
const [showPreview, setShowPreview] = useState(true);
const [translatedValues, setTranslatedValues] = useState<Record<string, string>>({});
// Kept in sync every render — lets runPOSTranslate read the CURRENT grand
// total even if it's mid-flight from an earlier call (see handlePOSTranslate's
// dedup guard), instead of a stale total captured when translation started.
const grandTotalRef = useRef(0);

  // ── Invoice settings ─────────────────────────────────────────────────────
  const [paymentMode, setPaymentMode] = useState<'cash' | 'card' | 'upi'>('cash');
  const [invoiceNumberOverride, setInvoiceNumberOverride] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState<'draft'|'paid'|'unpaid'|'overdue'|'cancelled'>('paid');
  const [notes, setNotes] = useState('');
  const [readyBy, setReadyBy] = useState('');
  const [posDefaultTemplateId, setPosDefaultTemplateId] = useState<number | null>(null);
  const [posDefaultTemplateName, setPosDefaultTemplateName] = useState<string>('');
  const [savingDefaultTemplate, setSavingDefaultTemplate] = useState(false);
  const canSetPosDefaultTemplate = ['owner', 'admin'].includes((authUser?.role || '').toLowerCase());
  const [showSettings, setShowSettings] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [activeScanSession, setActiveScanSession] = useState<string | null>(null);
  const scanPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!activeScanSession) {
      if (scanPollRef.current) clearInterval(scanPollRef.current);
      return;
    }
    scanPollRef.current = setInterval(async () => {
      try {
        const tkn = getStoredToken();
        const res = await fetch(`${BACKEND}/scan-session/${activeScanSession}`, {
          headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.value) return;
        if (data.value.includes('phone-scan') || data.value.includes('session=')) return;
        if (typeof data.value === 'string' && data.value.startsWith('OCR_ITEM:')) {
          try {
            const parsed = JSON.parse(data.value.slice('OCR_ITEM:'.length));
            const name = parsed.name?.trim();
            if (name) handleScanAdd(name, parsed.qty || 1, parsed.price || 0);
          } catch { /* malformed payload — ignore */ }
          return;
        }
        const matched = products.find(p => p.barcode === data.value || p.name.toLowerCase() === data.value.toLowerCase());
        if (matched) {
          addToCart(matched);
        } else {
          handleScanAdd(data.value, 1, 0, data.value);
        }
      } catch { /* silent */ }
    }, 800);
    return () => { if (scanPollRef.current) clearInterval(scanPollRef.current); };
  }, [activeScanSession, products]);
const [posTranslateLang, setPosTranslateLang] = useState(() => 
  localStorage.getItem('cloudairambo_pos_lang') || 'ar'
);
const [bilingualEnabled, setBilingualEnabled] = useState(() =>
  localStorage.getItem('cloudairambo_pos_bilingual_enabled') === 'true'
);


const [isTranslatingPOS, setIsTranslatingPOS] = useState(false);
const [cartPanelWidth, setCartPanelWidth] = useState(480);
  // ── Tax / Discount ────────────────────────────────────────────────────────
  const [taxLines, setTaxLines] = useState<TaxLine[]>([{ label: 'Tax', rate: 0 }]);
  const [discountLines, setDiscountLines] = useState<DiscountLine[]>([{ label: 'Discount', rate: 0 }]);
  const [shippingCost, setShippingCost] = useState(0);
const [previewPanelWidthState, setPreviewPanelWidthState] = useState(340);
// Tracks whether the cashier has manually dragged the preview panel —
// once true, the A4/Letter auto-fit effect below backs off and leaves
// their chosen size/zoom alone instead of overriding it on every template switch.
const manualPreviewResizeRef = useRef(false);
  // ── Date/Time ─────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [invoiceTime, setInvoiceTime] = useState(() => {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [lastSavedInvoice, setLastSavedInvoice] = useState<{id: string; number: string; total: number; publicToken?: string} | null>(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  // Payment-wait modal for card/UPI — QR + auto-poll, matches QuickInvoiceView.
  const [paymentWaitModal, setPaymentWaitModal] = useState<{ invoiceId: string; invoiceNumber: string; link: string; qr: string } | null>(null);
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [regeneratingQR, setRegeneratingQR] = useState(false);
  // Set when cashier closes the payment-wait modal to add a forgotten
  // item — remembers which pending bill/QR to resume once Save (now
  // "Update Bill") completes, mirroring QuickInvoiceView's flow.
  const [editingPendingInvoice, setEditingPendingInvoice] = useState<{ invoiceId: string; invoiceNumber: string; link: string; qr: string } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
const [availablePrinters, setAvailablePrinters] = useState<string[]>([]);
const [selectedPrinter, setSelectedPrinter] = useState('');
const [showPrinterPicker, setShowPrinterPicker] = useState(false);
const [useUSBPrinter, setUseUSBPrinter] = useState(() =>
  localStorage.getItem('cloudairambo_pos_usb_print') === 'true'
);
const [usbConnected, setUsbConnected] = useState(false);
  const sym = getCurrencyDisplay(activeCurrency, (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol');

  const previewPanelWidth = 308;
const templateNativeWidth = useMemo(() => {
  const t = allTemplates.find(t => t.id === selectedTemplateId);
  if (!t) return 302;
if (t.pageSize === 'THERMAL_80MM') return 302;
if (t.pageSize === 'A4') return 794;
if (t.pageSize === 'LETTER') return 816;
if (t.pageSize === 'LABEL_4X6') return 450;
if (t.pageSize === 'LABEL_3X2') return 288;
return 302;
}, [allTemplates, selectedTemplateId]);
const previewScale = useMemo(() => Math.min(1, previewPanelWidth / templateNativeWidth), [templateNativeWidth]);
const isHotelTemplate = /hotel|lodge/i.test(allTemplates.find(t => t.id === selectedTemplateId)?.name || '');

// Auto-fit the preview panel (+ zoom) to a sensible default per template
// type, so the cashier never has to manually drag/zoom it themselves:
//   - A4/Letter: wide panel (720px), CanvasWorkspace zoom fit to that width
//   - Thermal/label/anything else: compact panel (340px), 100% preview zoom
// Backs off entirely once the cashier manually drags the panel
// (manualPreviewResizeRef) — from then on their size is left alone no
// matter what template they switch to.
useEffect(() => {
  if (isMobile || manualPreviewResizeRef.current) return;
  const t = allTemplates.find(t => t.id === selectedTemplateId);
  if (!t) return;
  const isA4OrLetter = t.pageSize === 'A4' || t.pageSize === 'LETTER';
  if (isA4OrLetter) {
    const nativeW = t.pageSize === 'A4' ? 794 : 816;
    const targetPanelWidth = 720; // wide enough to read comfortably without eating the whole screen
    setPreviewPanelWidthState(targetPanelWidth);
    setCanvasZoom(Math.min(1, (targetPanelWidth - 40) / nativeW));
  } else {
    // Thermal (and label sizes) — compact panel, 100% zoom, matching the
    // original default look before A4 auto-fit started leaking its width.
    setPreviewPanelWidthState(340);
    setPreviewZoom(1);
  }
}, [selectedTemplateId, allTemplates, isMobile]);
const perms = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses ?? null);

if (!perms.canAccessPOS) {
  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '80px', 
      color: '#9ca3af', 
      fontSize: 14, 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      You don't have permission to access the POS.
    </div>
  );
}

useEffect(() => {
  tryAutoReconnectUSB().then(ok => setUsbConnected(ok || isUSBPrinterConnected()));
}, []);

useEffect(() => {
  if (showSettings) {
    fetch(`${BACKEND}/print/printers`, {
      headers: { Authorization: `Bearer ${getStoredToken()}` }
    })
      .then(r => r.json())
      .then(data => setAvailablePrinters(data.printers || []))
      .catch(() => {});
  }
}, [showSettings]);
  // Default to thermal/receipt template if available
useEffect(() => {
  // Non-owner/admin roles are locked to the org's default POS template, once known — no picking their own.
  if (!canSetPosDefaultTemplate && posDefaultTemplateId != null) {
    const locked = allTemplates.find(t => String(t.id) === String(posDefaultTemplateId));
    if (locked) { setSelectedTemplateId(locked.id); return; }
  }

  const saved = localStorage.getItem('cloudairambo_pos_template');
  if (saved && allTemplates.find(t => t.id === saved)) {
    setSelectedTemplateId(saved);
    return;
  }
  const thermal = allTemplates.find(t =>
    t.pageSize === 'THERMAL_80MM' ||
    /receipt|pos|thermal/i.test(t.name)
  );
  setSelectedTemplateId(thermal?.id || allTemplates[0]?.id || '');
}, [customTemplates.length, canSetPosDefaultTemplate, posDefaultTemplateId, allTemplates]);
// Cache outlet WhatsApp number + display token — prioritizes the currently active
// outlet (outletId prop), falling back to the user's assigned outlet only if needed.
const [outletDisplayToken, setOutletDisplayToken] = useState<string>('');
const [outletKitchenToken, setOutletKitchenToken] = useState<string>('');
const [outletPhoneCountry, setOutletPhoneCountry] = useState<string>('');
const [companyName, setCompanyName] = useState<string>('');

useEffect(() => {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  if (!jwt) return;
  fetch(`${BACKEND}/auth/company-profile`, {
    headers: { Authorization: `Bearer ${jwt}` },
  })
    .then(r => r.json())
    .then(data => {
      if (data?.name) setCompanyName(data.name);
      else if (authUser?.name) setCompanyName(authUser.name);
    })
    .catch(() => {
      if (authUser?.name) setCompanyName(authUser.name);
    });
}, [authUser]);
// Seller country/region — read from the real outlet record fetched below,
// not the dead localStorage keys used previously (nothing ever wrote
// cloudairambo_outlet_country_*/region_*, so sellerCountry was always ''
// and getDefaultSchemeForCountryAndRegion silently defaulted to India).
const [sellerCountry, setSellerCountry] = useState('');
const [sellerRegion, setSellerRegion] = useState('');
  useEffect(() => {
    if (!authUser?.org_id) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets?module=pos`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then((outlets: any[]) => {
        if (!Array.isArray(outlets)) return;
        const outlet = outlets.find(o => o.id === outletId) || outlets.find(o => o.id === authUser.outlet_id);
        (window as any).__outlet_wa_number = outlet?.whatsapp_number || '';
        setOutletDisplayToken(outlet?.display_token || '');
        setOutletKitchenToken(outlet?.kitchen_token || '');
        setOutletPhoneCountry(outlet?.default_phone_country || '');
        setSellerCountry(outlet?.country || '');
        setSellerRegion(outlet?.region || '');
        setCustomTaxScheme(outlet?.custom_tax_scheme || null);
        const activeScheme = outlet?.tax_scheme_id || localStorage.getItem(`cloudairambo_outlet_scheme_${outletId || outlet?.id}`) || (outlet?.custom_tax_scheme ? 'outlet_custom' : null);
        setSelectedSchemeId(activeScheme);

        const defTpl = outlet?.default_pos_template_id || outlet?.default_quick_invoice_template_id;
        if (defTpl && (outletId || outlet?.id)) {
          localStorage.setItem(`cloudairambo_pos_default_tpl_${outletId || outlet?.id}`, defTpl);
        }
      })
      .catch(() => {});
  }, [authUser?.org_id, outletId]);

  // Picks up country/region/custom tax saved from Profile or Catalog for
  // THIS outlet, live — no page refresh needed.
  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail?.outletId !== outletId) return;
      const o = e.detail?.outlet;
      setSellerCountry((o?.country ?? e.detail.country) || '');
      setSellerRegion((o?.region ?? e.detail.region) || '');
      if (o && 'custom_tax_scheme' in o) setCustomTaxScheme(o.custom_tax_scheme || null);
      if (e.detail?.tax_scheme_id || o?.tax_scheme_id) {
        setSelectedSchemeId(e.detail?.tax_scheme_id || o?.tax_scheme_id);
      }
    };
    window.addEventListener('outlet-location-updated', handler);
    return () => window.removeEventListener('outlet-location-updated', handler);
  }, [outletId]);
  // Fetch org's default POS template (locks non-owner/admin roles to it)
  useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/invoice-templates/pos-default`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        setPosDefaultTemplateId(data?.template_id ?? null);
        setPosDefaultTemplateName(data?.name || '');
      })
      .catch(() => {});
  }, [authUser?.org_id]);

  // Load products
  useEffect(() => {
    // Don't fetch until we actually know which outlet we're in —
    // fetching with no outlet_id pulls every product in the org.
    if (outletId == null) {
      setProducts([]);
      setProductsLoading(true);
      return;
    }

    let cancelled = false;
    setProductsLoading(true);
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/products/?outlet_id=${outletId}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return; // stale response from a superseded request — ignore
        setProducts(Array.isArray(data) ? data : []);
        setProductsLoading(false);
      })
      .catch(() => { if (!cancelled) setProductsLoading(false); });

    return () => { cancelled = true; };
  }, [outletId]);

  const [customTaxScheme, setCustomTaxScheme] = useState<CustomTaxSchemeConfig | null>(null);
  const [selectedSchemeId, setSelectedSchemeId] = useState<string | null>(() => {
    return localStorage.getItem(`cloudairambo_outlet_scheme_${outletId}`) || null;
  });

  const posTaxScheme = useMemo(() => {
    if ((selectedSchemeId === 'outlet_custom' || selectedSchemeId === 'custom' || !selectedSchemeId) && customTaxScheme) {
      return { ...buildCustomScheme(customTaxScheme), id: 'outlet_custom' };
    }
    if (selectedSchemeId) {
      const found = getSchemeByIdBase(selectedSchemeId);
      if (found) return found;
    }
    return getDefaultSchemeForCountryAndRegion(sellerCountry, sellerRegion);
  }, [sellerCountry, sellerRegion, selectedSchemeId, customTaxScheme]);
  const [buyerRegion, setBuyerRegion] = useState('');
  const [clientGSTIN, setClientGSTIN] = useState('');
  const [gstinAutoDetected, setGstinAutoDetected] = useState(false);
  const [showTaxDisclaimerModal, setShowTaxDisclaimerModal] = useState(false);

  // Universal Tax ID metadata inspector — 100% matched with QuickInvoiceView/App.
  // Replaces the old India-only GSTIN checksum/state lookup.
  const taxIdMeta = useMemo<TaxIdInfo>(
    () => inspectTaxId(clientGSTIN, sellerCountry),
    [clientGSTIN, sellerCountry]
  );

  // Manual escape hatch — same as QuickInvoiceView — only relevant when a
  // Tax ID is typed in but the validator flags it invalid. Never applies
  // to an empty field.
  const [forceB2BOverride, setForceB2BOverride] = useState(false);
  const isB2B = taxIdMeta.isValid || (forceB2BOverride && clientGSTIN.trim().length > 0);

  function getEffectiveTaxRate(item: CartItem): number {
    return (isB2B && item.b2bTaxRate !== undefined && item.b2bTaxRate !== null)
      ? item.b2bTaxRate
      : (item.taxRate || 0);
  }

  useEffect(() => {
    const clean = clientGSTIN.trim();

    if (clean.length === 0) {
      setBuyerRegion('');
      setGstinAutoDetected(false);
      return;
    }

    if ((taxIdMeta.isComplete || clean.length >= (taxIdMeta.expectedLength || 15)) && !taxIdMeta.isValid) {
      setBuyerRegion('');
      setGstinAutoDetected(false);
      return;
    }

    if (!REGIONAL_TAX_COUNTRIES.has(taxIdMeta.countryCode) && !taxIdMeta.detectedRegion) {
      setBuyerRegion('');
      setGstinAutoDetected(false);
      return;
    }

    if (taxIdMeta.detectedRegion) {
      setBuyerRegion(taxIdMeta.detectedRegion);
      setGstinAutoDetected(true);
    }
  }, [taxIdMeta.detectedRegion, taxIdMeta.isComplete, taxIdMeta.isValid, taxIdMeta.countryCode, taxIdMeta.expectedLength, clientGSTIN]);

  // ── Computed values ───────────────────────────────────────────────────────

  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map(p => p.category).filter(Boolean)));
    return ['All', ...cats];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.toLowerCase();
    return products.filter(p => {
      const matchSearch = !q ||
        p.name.toLowerCase().includes(q) ||
        p.barcode.includes(q) ||
        (p.brand || '').toLowerCase().includes(q);
      const matchCat = selectedCategory === 'All' || p.category === selectedCategory;
      return matchSearch && matchCat;
    });
  }, [products, productSearch, selectedCategory]);

  const filteredAccounts = useMemo(() =>
    accounts.filter(a =>
      a.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(clientSearch.toLowerCase())
    ), [accounts, clientSearch]);

  // Per-item tax breakdown — mirrors the mobile app's getItemTaxBreakdown exactly.
  // `subtotal` is always the pre-tax (net) base, whether an item's tax is inclusive or
  // exclusive. This matters because manual tax/discount lines are a % of `subtotal` —
  // if inclusive tax were left baked into subtotal, a manual line (e.g. a service charge)
  // would compound on top of tax the customer already paid, inflating the total.
  const getItemTaxBreakdown = (item: CartItem) => {
    const lineTotal = item.qty * item.rate;
    const rate = getEffectiveTaxRate(item);
    if (rate <= 0) return { base: lineTotal, tax: 0, total: lineTotal };
    if (item.taxInclusive) {
      const base = lineTotal / (1 + rate / 100);
      return { base, tax: lineTotal - base, total: lineTotal };
    }
    const tax = lineTotal * (rate / 100);
    return { base: lineTotal, tax, total: lineTotal + tax };
  };

  const itemTaxBreakdowns = cart.map(getItemTaxBreakdown);
  const subtotal = itemTaxBreakdowns.reduce((s, b) => s + b.base, 0);
  const discTotal = discountLines.reduce((s, d) => s + subtotal * (d.rate / 100), 0);

  // Resolve matched account by phone (falls back to name+email match) for auto-discount lookup
  const matchedAccount = useMemo(() => {
    const myNorm = normalizePhoneForMatching(clientPhone, outletPhoneCountry || 'IN');
    return accounts.find(a => {
      const accNorm = normalizePhoneForMatching(a.phone || '', outletPhoneCountry || 'IN');
      if (myNorm && accNorm && myNorm === accNorm) return true;
      return clientName.trim() && a.name.trim().toLowerCase() === clientName.trim().toLowerCase() &&
        clientEmail.trim() && a.email.trim().toLowerCase() === clientEmail.trim().toLowerCase();
    }) || null;
  }, [accounts, clientPhone, clientName, clientEmail, outletPhoneCountry]);

  const autoDiscountRule = useMemo(
    () => getBestDiscountRule(matchedAccount, discountRules || [], subtotal - discTotal, outletId),
    [matchedAccount, discountRules, subtotal, discTotal, outletId]
  );
  const autoDiscountAmount = autoDiscountRule
    ? computeAutoDiscount(matchedAccount, discountRules || [], subtotal - discTotal, outletId)
    : 0;

  // Discount reduces the TAXABLE VALUE before tax is calculated — standard GST/VAT
  // invoicing math. Whether the LOYALTY discount also counts toward that
  // reduction is controlled by the loyaltyReducesTax setting — manual
  // Discount Lines always reduce the taxable base regardless.
  const taxReducingDiscount = discTotal + (loyaltyReducesTax ? autoDiscountAmount : 0);
  const discountRatio = subtotal > 0 ? Math.min(1, taxReducingDiscount / subtotal) : 0;
  const productTaxTotal = itemTaxBreakdowns.reduce((s, b) => s + b.tax * (1 - discountRatio), 0);
  const taxTotal = taxLines.reduce((s, t) => s + (subtotal - taxReducingDiscount) * (t.rate / 100), 0);

  // Named GST/VAT breakdown (CGST/SGST/IGST/etc.) when the outlet's country
  // uses a component-based scheme (e.g. India). Falls back to the blended
  // "Product Tax" line elsewhere in this file when unavailable — same
  // fallback QuickInvoiceView and App.tsx's PDF export already use.
  const taxBreakdown = useMemo(() => {
    if (productTaxTotal <= 0) return undefined;
    const scheme = posTaxScheme;
    const combined: Record<string, { rate: number; amount: number }> = {};
    cart.forEach(item => {
      const rate = getEffectiveTaxRate(item);
      if (rate <= 0) return;
      const lineTotal = item.qty * item.rate;
      const base = item.taxInclusive ? lineTotal / (1 + rate / 100) : lineTotal;
      const discountedBase = base * (1 - discountRatio);

      if (scheme.kind === 'component' && scheme.components && scheme.components.length > 1) {
        const breakdown = computeTaxBreakdown(scheme, discountedBase, { sellerRegion, buyerRegion }, rate);
        breakdown.lines.forEach(l => {
          if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
          combined[l.name].amount += l.amount;
        });
      } else {
        const standardTaxName = item.taxName || customTaxScheme?.name || scheme.components?.[0]?.name || scheme.label.replace(/\s*\(.*?\)/, '') || 'Tax';
        const customLabel = isB2B
          ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || (scheme as any).b2b_name || standardTaxName)
          : standardTaxName;
        if (!combined[customLabel]) combined[customLabel] = { rate, amount: 0 };
        combined[customLabel].amount += (discountedBase * rate / 100);
      }
    });
    const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
    return entries.length > 0 ? entries : undefined;
  }, [sellerCountry, sellerRegion, buyerRegion, cart, discountRatio, productTaxTotal, posTaxScheme, isB2B, customTaxScheme]);

  const grandTotal = subtotal + productTaxTotal + taxTotal - discTotal - autoDiscountAmount + shippingCost;
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  grandTotalRef.current = grandTotal;

  // ── Cart operations ───────────────────────────────────────────────────────

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.findIndex(i => i.productId === product.id);
      if (existing >= 0) {
        return prev.map((item, idx) =>
          idx === existing ? { ...item, qty: item.qty + 1 } : item
        );
      }
      return [...prev, {
        id: `ci-${Date.now()}-${Math.random()}`,
        productId: product.id,
        barcode: product.barcode,
        name: product.name,
        rate: product.rate,
        qty: 1,
        unit: product.unit,
        taxRate: product.tax_rate || 0,
        taxName: product.tax_name,
        b2bTaxRate: product.b2b_tax_rate,
        b2bTaxName: product.b2b_tax_name,
        taxInclusive: !!product.tax_inclusive,
      }];
    });
  };

  const updateCartQty = (id: string, delta: number) => {
    setCart(prev =>
      prev.map(item => {
        if (item.id !== id) return item;
        const newQty = item.qty + delta;
        return newQty <= 0 ? item : { ...item, qty: newQty };
      })
    );
  };

  const setCartQty = (id: string, qty: number) => {
    if (qty <= 0) return;
    setCart(prev => prev.map(item => item.id === id ? { ...item, qty } : item));
  };

  const updateCartRate = (id: string, rate: number) => {
    setCart(prev => prev.map(item => item.id === id ? { ...item, rate } : item));
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
    setClientName(''); setClientEmail(''); setClientPhone('');
    setClientAddress(''); setClientSearch('');
    setPhoneResetKey(k => k + 1);
    setIncludeFields({
      name: true,
      phone: true,
      email: true,
      address: true,
      taxId: true,
    });
    setNotes(''); setInvoiceNumberOverride('');
    setTaxLines([{ label: 'Tax', rate: 0 }]);
    setDiscountLines([{ label: 'Discount', rate: 0 }]);
    setShippingCost(0);
    setPaymentMode('cash');
    setInvoiceStatus('paid');
    setLastSaved(null);
    setLastSavedInvoice(null);
    setPaymentFailed(false);
    const now = new Date();
    setInvoiceDate(now.toISOString().split('T')[0]);
    setInvoiceTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }));
  };

  const selectAccount = (acc: ClientAccount) => {
    setClientName(acc.name);
    setClientEmail(acc.email || '');
    setClientPhone(acc.phone || '');
    setClientAddress(acc.address || '');
    setClientSearch(acc.name);
    setShowClientDropdown(false);
  };
// Auto-fill client name/email when a matching phone number is typed —
  // compares NATIONAL numbers (post dial-code, via parsePhoneValue) so it
  // works correctly for any country's number length. Prefix-matches while
  // typing, requires uniqueness, and clears stale fields once the phone is
  // edited back down below 5 digits.
  const lastAutoFilledPhone = useRef<string>('');
  useEffect(() => {
    const national = parsePhoneValue(clientPhone, outletPhoneCountry || 'IN').nationalNumber;

    if (national.length < 5) {
      if (lastAutoFilledPhone.current && clientName) {
        setClientName('');
        setClientEmail('');
        setClientAddress('');
        setClientSearch('');
      }
      lastAutoFilledPhone.current = '';
      return;
    }

    // Only attempt a match once the number is a COMPLETE, valid phone
    // number — not on any partial 5+ digit prefix. A partial prefix can
    // coincidentally match exactly one account mid-typing (two unrelated
    // numbers sharing their first several digits), silently filling in
    // the WRONG customer's name/email/address — and since nothing clears
    // it once set, wrong data can stick even after the real full number
    // no longer matches anyone.
    const completeE164 = getCleanE164Phone(clientPhone, outletPhoneCountry || 'IN');
    if (!completeE164) {
      if (lastAutoFilledPhone.current) {
        setClientName('');
        setClientEmail('');
        setClientAddress('');
        setClientSearch('');
        lastAutoFilledPhone.current = '';
      }
      return;
    }
    if (lastAutoFilledPhone.current === national) return;

    const inputNormalized = normalizePhoneForMatching(clientPhone, outletPhoneCountry || 'IN');
    const matches = accounts.filter(a => {
      const accNormalized = normalizePhoneForMatching(a.phone || '', outletPhoneCountry || 'IN');
      return accNormalized && accNormalized === inputNormalized;
    });

    if (matches.length === 1) {
      const match = matches[0];
      lastAutoFilledPhone.current = national;
      setClientName(match.name);
      setClientEmail(match.email || '');
      setClientAddress(match.address || '');
      setClientSearch(match.name);
    }
  }, [clientPhone, accounts, clientName, outletPhoneCountry]);
// ── Global USB/Bluetooth HID barcode scanner wedge ────────────────────────
useEffect(() => {
  let buffer = '';
  let lastKeyTime = Date.now();

  const handleKey = (e: KeyboardEvent) => {
    // Skip if user is typing in any input/textarea
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    const now = Date.now();

    // Reset buffer if gap between keys > 50ms (human typing is slower)
    if (now - lastKeyTime > 50) buffer = '';
    lastKeyTime = now;

    if (e.key === 'Enter') {
      const code = buffer.trim();
      buffer = '';
      if (code.length >= 3) {
        handleScanAdd(code, 1, 0, code); // lookup happens inside handleScanAdd
      }
    } else if (e.key.length === 1) {
      buffer += e.key;
    }
  };

  window.addEventListener('keydown', handleKey);
  return () => window.removeEventListener('keydown', handleKey);
}, [products]); // re-register when products catalog loads


const confirmAndPrintRef = useRef<() => void>(() => {});
useEffect(() => { confirmAndPrintRef.current = confirmAndPrint; });
const lastBillRef = useRef<InvoiceRecord | null>(null);
const generatedTokenRef = useRef<string>('');

useEffect(() => {
  if (!paymentWaitModal) return;
  setPaymentFailed(false);
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  setPaymentPolling(true);
  const interval = setInterval(async () => {
    try {
      const res = await fetch(`${BACKEND}/payments/status/by-invoice`, { headers: { Authorization: `Bearer ${jwt}` } });
      const data = await res.json();
      const match = Array.isArray(data) ? data.find((p: any) => String(p.invoice_id) === String(paymentWaitModal.invoiceId)) : null;
      if (match?.status === 'success') {
        clearInterval(interval);
        setPaymentPolling(false);
        setPaymentWaitModal(null);
        // confirmAndPrintRef always points at the latest confirmAndPrint —
        // it already resolves the real invoice number from the PENDING-
        // placeholder, patches the receipt, then prints/confirms/clears.
        await confirmAndPrintRef.current();
      } else if (match?.status === 'failed') {
        clearInterval(interval);
        setPaymentPolling(false);
        setPaymentFailed(true);
        postLog('POS_PAYMENT_FAILED', `Bill ${paymentWaitModal.invoiceNumber} online payment failed.`);
      }
    } catch { /* keep polling */ }
  }, 4000);
  return () => clearInterval(interval);
}, [paymentWaitModal?.invoiceId]);

useEffect(() => {
  if (!outletDisplayToken || !lastSavedInvoice) return;
  const invoiceId = lastSavedInvoice.id;
  const streamUrl = `${BACKEND}/display/stream/${outletDisplayToken}?register_id=${registerId}`;
  const es = new EventSource(streamUrl);
  es.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      if (!data.bill_id) return;
      if (String(data.bill_id) !== String(invoiceId)) return;
      if (data.type === 'paid') confirmAndPrintRef.current();
    } catch {}
  };
  return () => es.close();
}, [outletDisplayToken, lastSavedInvoice?.id, registerId]);
  // Scanner callback (matches QuickInvoice)
  const [translatedTemplate, setTranslatedTemplate] = useState<any>(null);

  // QR images for the A4/Letter canvas preview — thermal stays on
  // POSReceiptRenderer (which generates its own), so this only runs for A4/Letter.
  // Must live after translatedTemplate's declaration above — referencing it
  // any earlier in the component body throws a TDZ ReferenceError on every render.
  useEffect(() => {
    const rawTemplate = allTemplates.find(t => t.id === selectedTemplateId);
    const isA4OrLetter = rawTemplate?.pageSize === 'A4' || rawTemplate?.pageSize === 'LETTER';
    if (!isA4OrLetter) return;
    const elementsForPreview = lastSyncedElements.length > 0 ? lastSyncedElements : (translatedTemplate || rawTemplate)?.elements || [];
    const qrEls = elementsForPreview.filter((el: any) => el.type === 'qr');
    if (qrEls.length === 0) return;
    let cancelled = false;
    (async () => {
      const QRCode = await import('qrcode');
      const entries = await Promise.all(
        qrEls.map(async (el: any) => [el.id, await QRCode.default.toDataURL(el.qrValue || '', { width: 200, margin: 1 })] as [string, string])
      );
      if (!cancelled) setCanvasQrDataUrls(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [lastSyncedElements, translatedTemplate, selectedTemplateId, allTemplates]);
useEffect(() => {
  const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setLastTokenQR(null); };
  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
}, []);
  // Actual translation work. Callers now go through the deduped wrapper below —
  // renamed from handlePOSTranslate.
  const runPOSTranslate = async () => {
  const cashierName = waiterName || authUser?.name?.trim() || 'Staff';

  setIsTranslatingPOS(true);
    try {
      const token = getStoredToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const tl = posTranslateLang;

      // Some translation/transliteration backends return HTML-escaped
      // punctuation (e.g. "&" → "&amp;") in their JSON string response —
      // this is language-agnostic (any phrase with &, <, >, ", ' can hit
      // it) and was never decoded back, so it showed up literally on the
      // translated/transliterated side of the receipt.
      const decodeHtmlEntities = (str: string): string => {
        if (!str) return str;
        const el = document.createElement('textarea');
        el.innerHTML = str;
        return el.value;
      };

      const translateText = async (text: string): Promise<string> => {
        if (!text?.trim()) return text;
        try {
          const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(text.trim())}&tl=${tl}`, { headers });
          if (!res.ok) throw new Error(`translate HTTP ${res.status}`);
          const data = await res.json();
          const echoedBack = data.translated && data.translated.trim().toLowerCase() === text.trim().toLowerCase();
          if (!data.translated || echoedBack) {
            console.warn('translateText: no real translation for', JSON.stringify(text));
            return text;
          }
          let out = decodeHtmlEntities(data.translated);
          if (tl === 'ar') {
            const AR_MAP: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
            out = out.replace(/[0-9]/g, d => AR_MAP[d] || d);
          }
          return out;
        } catch (e) {
          console.error('translateText failed for', JSON.stringify(text), e);
          return text;
        }
      };

      // Batch translation helper: Translates an entire list in 1 single API call
      const translateBatchText = async (texts: string[]): Promise<string[]> => {
        if (!texts.length) return [];
        try {
          const res = await fetch(`${BACKEND}/translate/batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            },
            body: JSON.stringify({ texts, tl }),
          });
          if (!res.ok) return texts;
          const data = await res.json();
          const list = Array.isArray(data?.translations) ? data.translations : texts;
          return list.map((item: string) => {
            let out = decodeHtmlEntities(item);
            if (tl === 'ar') {
              const AR_MAP: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
              out = out.replace(/[0-9]/g, d => AR_MAP[d] || d);
            }
            return out;
          });
        } catch (e) {
          console.error('translateBatchText failed:', e);
          return texts;
        }
      };

      const transliterateName = async (name: string) => {
        if (!name?.trim()) return name;
        try {
          const res = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(name.trim())}&tl=${tl}`, { headers });
          const data = await res.json();
          let out = decodeHtmlEntities(data.transliterated || name);
          // Convert English digits inside names to Arabic numerals if Arabic is selected
          if (tl === 'ar') {
            const AR_MAP: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
            out = out.replace(/[0-9]/g, d => AR_MAP[d] || d);
          }
          return out;
        } catch (e) { console.error('transliterateName failed:', e); return name; }
      };
      // Computed once up front — reused for every header/footer line below so we
      // don't hit the transliteration API once per line.
      const isRealClient = Boolean(clientName && clientName.trim() && clientName.toLowerCase() !== 'walk-in customer');
      const isRealAddress = Boolean(clientAddress && clientAddress.trim());
      const [cashierNameLocalized, clientNameAr, clientAddressAr] = await Promise.all([
        transliterateName(cashierName),
        isRealClient ? transliterateName(clientName.trim()) : Promise.resolve(null),
        isRealAddress ? transliterateName(clientAddress.trim()) : Promise.resolve(null),
      ]);
      (window as any).__pos_client_name_ar = clientNameAr;
      (window as any).__pos_client_address_ar = clientAddressAr;
      (window as any).__pos_staff_name_ar = cashierNameLocalized;
      setTranslatedValues(prev => ({
        ...prev,
        staffName_translit: cashierNameLocalized,
      }));

      // Translate cart item names — but real translation only works for actual
      // dictionary words. Product names that are brand-like, made-up, or proper
      // nouns (e.g. "Rihan3") aren't in Google's dictionary, so translateText
      // just echoes them back unchanged. Fall back to transliteration (phonetic
      // Arabic spelling) in that case — same approach already used for the
      // cashier/client name — so the receipt always shows *something* Arabic
      // instead of the English name twice.
      // Batch translate all cart item names in 1 SINGLE API call
      const cartTranslationPromise = (async () => {
        const itemNames = cart.map(i => i.name);
        const translatedNames = await translateBatchText(itemNames);

        return Promise.all(
          cart.map(async (item, idx) => {
            const translated = translatedNames[idx] || item.name;
            const echoedBack = translated.trim().toLowerCase() === item.name.trim().toLowerCase();
            const arabicName = echoedBack ? await transliterateName(item.name) : translated;

            const customColAr: Record<string, string> = {};
            await Promise.all(
              posColumns.map(async col => {
                const raw = getPosColValue(item, col);
                if (!raw?.trim() || /^[\d\s.,%+\-]+$/.test(raw)) return;
                const colTranslated = await translateText(raw);
                const colEchoed = colTranslated.trim().toLowerCase() === raw.trim().toLowerCase();
                let colAr = colEchoed ? await transliterateName(raw) : colTranslated;
                if (colAr.trim().toLowerCase() !== raw.trim().toLowerCase()) {
                  customColAr[col.key] = colAr;
                }
              })
            );

            return { ...item, arabicName, customColAr };
          })
        );
      })();

      // Translate template elements
const template = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
const thermalMatch = THERMAL_PRESETS.find(t => t.name === template.name);
const receiptType = (thermalMatch as any)?.receiptType || RECEIPT_TYPE_MAP[template.name] || 'REC';
const baseTemplate = translatedTemplate || template;
const elements = JSON.parse(JSON.stringify(baseTemplate.elements));
if (template) {
  const stripped = {
    ...template,
    elements: template.elements.map((el: any) => ({ ...el, arabicContent: undefined })),
  };
  const cloned = JSON.parse(JSON.stringify(stripped));
        const clonedElementsPromise = Promise.all(
  cloned.elements.map(async (el: any) => {
    // Clear any previous translation first
    if (el.arabicContent) el = { ...el, arabicContent: undefined };
    if (el.type === 'text' && el.textContent?.trim()) {

            // 1. DYNAMIC CLIENT BLOCK TRANSLATION (UNIVERSAL FOR ALL LANGUAGES)
            const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*(?::|\n|$)/i;
            const isClientBlock = targetLabelRegex.test((el.textContent || '').trim());
            if (isClientBlock) {
              const origLines = (el.textContent || '').split('\n');
              // Extract clean label prefix (e.g. "SELLER", "BILL TO")
              const enLabel = (origLines[0].split(':')[0] || 'Invoice To').trim().replace(/:+$/, '');
              const stdRe = /^(tax\s*id|vat\s*no|gstin|trn|npwp|phone|tel|mobile|mob|contact|cell|email|e-mail|mail|address|adress|addr|attention)\s*[:\-]/i;
              const isRawEmailOrPhone = (l: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l.trim()) || /^\+?[\d\s\-()]{7,}$/.test(l.trim());

              // Preserve only genuine custom template lines (STORE ID, PERIOD, WARD, etc.)
              const customKept = origLines.slice(1).filter(l =>
                /^[^:@]{1,30}:\s*\S/.test(l.trim()) && !stdRe.test(l.trim()) && !targetLabelRegex.test(l.trim()) && !isRawEmailOrPhone(l.trim())
              );
              
              const queryLabel = /^bill\s*to$/i.test(enLabel) ? 'Invoice To:' : `${enLabel}:`;
              const translatedLabel = await translateText(queryLabel);

              const isRealName = Boolean(clientName && clientName.trim() && clientName.toLowerCase() !== 'walk-in customer');
              const customerName = isRealName ? clientName.trim() : 'Walk-in Customer';

              const translatedName = isRealName
                ? ((window as any).__pos_client_name_ar || await transliterateName(customerName))
                : ((window as any).__pos_walkin_label || await translateText('Walk-in Customer'));

              const curInc = includeFieldsRef.current;

              // Build English lines and Translated lines in strict 1-to-1 sync
              const enLinesArr: string[] = [enLabel];
              const trLinesArr: string[] = [translatedLabel];

              if (curInc.name) {
                enLinesArr.push(customerName);
                trLinesArr.push(translatedName);
              }

              if (curInc.address && clientAddress.trim()) {
                enLinesArr.push(clientAddress.trim());
                trLinesArr.push(await transliterateName(clientAddress.trim()));
              }
              if (curInc.taxId && clientGSTIN.trim()) {
                const taxPrefix = (window as any).__pos_buyer_tax_label || await translateText('Tax ID:');
                enLinesArr.push(`Tax ID: ${clientGSTIN.trim()}`);
                trLinesArr.push(`${taxPrefix.replace(/:$/, '')}: ${clientGSTIN.trim()}`);
              }
              if (curInc.email && clientEmail.trim() && clientEmail.includes('@')) {
                enLinesArr.push(clientEmail.trim());
                trLinesArr.push(clientEmail.trim());
              }
              if (curInc.phone && clientPhone.trim()) {
                enLinesArr.push(clientPhone.trim());
                trLinesArr.push(clientPhone.trim());
              }

              // Translate all custom template lines in parallel (instant)
              const customTranslated = await Promise.all(
                customKept.map(async (cLine) => {
                  const match = cLine.match(/^([^:]+):\s*(.*)$/);
                  if (match) {
                    const [, cKey, cVal] = match;
                    const trKey = await translateText(cKey.trim());
                    const isNumericOrCode = /^[\d\s+\-–—().#:/\\%,A-Z0-9\-_]+$/.test(cVal.trim());
                    const trVal = isNumericOrCode ? cVal.trim() : await translateText(cVal.trim());
                    return { en: cLine, tr: `${trKey}: ${trVal}` };
                  }
                  return { en: cLine, tr: await translateText(cLine) };
                })
              );

              customTranslated.forEach(c => {
                enLinesArr.push(c.en);
                trLinesArr.push(c.tr);
              });

              const totalLines = enLinesArr.length;
              const fontSize = el.fontSize || 10;
              const neededHeight = Math.ceil(totalLines * (fontSize * 1.6) + 36);

              return {
                ...el,
                textContent: enLinesArr.join('\n'),
                arabicContent: trLinesArr.join('\n'),
                height: Math.max(el.height || 0, neededHeight),
                translatedLang: tl,
                bilingualGap: 6,
              };
            }

            // 2. GENERAL TEXT ELEMENTS (Headers, Metadata, Terms, Notes)
            const lines = el.textContent.split('\n');
            const translatedLines = await Promise.all(lines.map(async (l: string) => {
              // Context-aware replacements for accurate invoicing terminology in any language
              let patched = l;
              patched = patched.replace(/\bPO\s*#\s*:/gi, 'Purchase Order #:');
              patched = patched.replace(/\bBill\s*To\s*:/gi, 'Invoice To:');

              // Protect {invoice_number} from being translated/corrupted by Google Translate
              const hasInvNum = patched.includes('{invoice_number}');
              if (hasInvNum) {
                patched = patched.replace(/\{invoice_number\}/g, 'INVNUM');
              }
              patched = patched.replace(/\{staff_name\}/gi, cashierName);
                // "Shop No.X, Place Name | GSTIN: CODE" lines are dominated by proper
              // nouns + a masked code — Google echoes the whole line back unchanged
              // even with the code protected. Handle this shape specially: translate
              // just the "Shop No." label (short, generic phrases translate reliably)
              // and transliterate the place name, instead of translating the full line.
              const shopLineMatch = patched.match(/^(Shop\s*No\.?\s*\d*\s*,?\s*)([^|]+?)(\s*\|.*)?$/i);
              let shopLineOverride: string | null = null;
              if (shopLineMatch) {
                const [, shopLabel, placeName, rest] = shopLineMatch;
                const shopLabelTranslated = await translateText(shopLabel.replace(/,\s*$/, '').trim());
                // Transliterate each comma-separated part on its own (e.g. "Main Market" and
                // "Bengaluru" separately) instead of one call for the whole string — the
                // backend was dropping later segments when given multi-part place names.
                const placeParts = placeName.split(',').map(p => p.trim()).filter(Boolean);
                const placePartsTransliterated = await Promise.all(placeParts.map(p => transliterateName(p)));
                shopLineOverride = `${shopLabelTranslated}, ${placePartsTransliterated.join('، ')}${rest || ''}`;
              }
                // Protect long alphanumeric codes (GSTIN, PAN, license numbers, etc.) —
              // Google Translate just echoes lines dominated by these back unchanged,
              // which makes hasTranslation() think nothing translated at all.
              const codeMatches: string[] = [];
              patched = patched.replace(/\b(?=[A-Z0-9]{6,}\b)(?=[A-Z0-9]*[0-9])(?=[A-Z0-9]*[A-Z])[A-Z0-9]{6,}\b/g,
                (code: string) => {
                  codeMatches.push(code);
                  return `ZZQCODE${codeMatches.length - 1}9137`;
                });
              // Protect the name itself from translation (names get transliterated,
              // not translated — same reasoning as the client "To:" name).
              // Nonsense token, not real English words, so Google won't "translate" the placeholder itself.
              const namePlaceholder = 'ZZQNAME9137';
              const hasName = patched.includes(cashierName);
              if (hasName) patched = patched.split(cashierName).join(namePlaceholder);
              // Replace {invoice_number} with a safe placeholder before translating
              const placeholder = 'INVNUM';
              const hasToken = patched.includes('{invoice_number}');
              const sanitized = hasToken ? patched.replace(/\{invoice_number\}/g, placeholder) : patched;

              // FORCE BILINGUAL: don't translate the whole line as one blob —
              // that lets Google silently drop or garble parts of it (address
              // lines, hours, multi-field lines, anything free-form) with no
              // way to detect it, since the old echo-check only catches a
              // FULL-string echo, not a partial garble. Instead split the line
              // into independent segments and force EVERY segment through
              // translate-or-transliterate on its own. Covers any current or
              // future line shape generically — nothing is silently dropped.
              // Skipped entirely when shopLineOverride already handled this
              // line (Shop No./place-name lines have their own logic above).
              let translated: string;
              if (shopLineOverride) {
                translated = await translateText(sanitized); // overwritten by shopLineOverride further below anyway
              } else {
                const PLACEHOLDER_RE = /^(ZZQCODE\d+9137|ZZQNAME9137|INVNUM)$/;
                // Split on clear separators (pipe, comma, 2+ spaces) AND
                // insert a boundary directly before any subsequent
                // "Word:" field start — even with just one space or no
                // space before it, e.g. "Table:1 Server:Ravi" or
                // "Table:1Server:Ravi". This catches tightly-packed
                // label:value pairs the 2+-space rule alone would miss.
                // Sentences with no colons at all (no "Word:" pattern)
                // never trigger a split, so plain prose stays whole.
                const boundaryMarked = sanitized.replace(
                  /([^\s\u0000])\s*(?=[A-Za-z][A-Za-z]{0,23}:\S)/g,
                  '$1\u0000'
                );
                const segments = boundaryMarked
                  .split(/(\u0000|\s*\|\s*|\s*,\s*|\s{2,})/)
                  .filter(s => s.length > 0 && s !== '\u0000');
                const translatedSegments = await Promise.all(
                  segments.map(async (seg) => {
                    const trimmed = seg.trim();
                    if (!trimmed) return seg; // pure whitespace/separator — keep as-is
                    if (/^[,|;:\/\\.\-_#]+$/.test(trimmed)) return seg; // pure punctuation/delimiters — keep as-is so commas never translate to words!
                    if (PLACEHOLDER_RE.test(trimmed)) return seg; // protected placeholder — never touch
                    if (/^[\d\s+\-().#]+$/.test(trimmed)) return seg; // pure numbers/phone/punctuation
                    const fieldMatch = trimmed.match(/^([A-Za-z][A-Za-z\s]{1,24}?):\s*(\S.*)$/);
                    if (fieldMatch) {
                      let [, fieldLabel, fieldValue] = fieldMatch;
                      fieldLabel = fieldLabel.trim();
                      const cleanVal = fieldValue.trim();

                      // Dynamic translation for roles and labels across all 100+ languages (Zero hardcoding)
                      const labelTranslated = await translateText(fieldLabel);
                      const labelEchoed = labelTranslated.trim().toLowerCase() === fieldLabel.toLowerCase();
                      const labelFinal = labelEchoed ? await transliterateName(fieldLabel) : labelTranslated;

                      // Protect numbers & codes (e.g. Table: 5) so numbers don't turn into letters
                      const isNumericOrCode = /^[\d\s+\-().#:/\\%]+$/.test(cleanVal);
                      if (PLACEHOLDER_RE.test(cleanVal) || isNumericOrCode) {
                        const valNum = (tl === 'ar' && isNumericOrCode)
                          ? cleanVal.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d])
                          : cleanVal;
                        return `${labelFinal}: ${valNum}`;
                      }

                      const valTranslated = await translateText(cleanVal);
                      const valEchoed = valTranslated.trim().toLowerCase() === cleanVal.toLowerCase();
                      const valFinal = valEchoed ? await transliterateName(cleanVal) : valTranslated;
                      return `${labelFinal}: ${valFinal}`;
                    }
                    const segTranslated = await translateText(trimmed);
                    const segEchoed = segTranslated.trim().toLowerCase() === trimmed.toLowerCase();
                    const segFinal = segEchoed ? await transliterateName(trimmed) : segTranslated;
                    const leading = seg.match(/^\s*/)?.[0] || '';
                    const trailing = seg.match(/\s*$/)?.[0] || '';
                    return leading + segFinal + trailing;
                  })
                );
                translated = translatedSegments.join('');
              }
              // Restore invoice placeholder
              if (hasInvNum) {
                translated = translated.replace(/INVNUM/g, '{invoice_number}');
                patched = patched.replace(/INVNUM/g, '{invoice_number}');
              }
              if (hasName) translated = translated.replace(new RegExp(namePlaceholder, 'g'), cashierNameLocalized);
              codeMatches.forEach((code, i) => {
                translated = translated.replace(new RegExp(`ZZQCODE${i}9137`, 'g'), code);
              });
              if (shopLineOverride) {
                codeMatches.forEach((code, i) => {
                  shopLineOverride = shopLineOverride!.replace(new RegExp(`ZZQCODE${i}9137`, 'g'), code);
                });
                translated = shopLineOverride;
              }
              // Clean up any literal 'فاصل' word artifacts and replace with clean Arabic comma
              if (tl === 'ar') {
                translated = translated
                  .replace(/(\S)فاصل(\S)/g, '$1، $2')
                  .replace(/\s*فاصل\s*/g, '، ');
              }
              return translated;
            }));
            return { ...el, textContent: el.textContent, arabicContent: translatedLines.join('\n'), translatedLang: tl, bilingualGap: 6 };
          }
            if (el.type === 'table') {
             const translatedCols = await Promise.all(
  (el.columns || []).map(async (col: any) => {
    const orig = col.originalHeader || col.header;
    const forTranslation =
      orig === 'Amt' ? 'Amount' :
      orig === 'Qty' ? 'Quantity' :
      orig === 'Sno' || orig === 'S.No' || orig === '#' ? 'Serial Number' :
      orig;
    return {
      ...col,
      originalHeader: orig,
      arabicHeader: await translateText(forTranslation),
      header: orig,
    };
  })
);
              const extraCols = await Promise.all(
                posColumns.map(async c => ({
                  ...c,
                  originalHeader: c.header,
                  arabicHeader: await translateText(c.header),
                }))
              );
              return { ...el, columns: fitThermalCols(placePosColumns(translatedCols, extraCols), template.pageSize === 'THERMAL_80MM' ? 302 : 794) };
            }

            // Translate Stamp Elements (e.g. PAID, APPROVED, RECEIVED)
            if (el.type === 'stamp' && el.stampText?.trim()) {
              const translatedStamp = await translateText(el.stampText.trim());
              const echoed = translatedStamp.trim().toLowerCase() === el.stampText.trim().toLowerCase();
              const finalAr = echoed ? await transliterateName(el.stampText.trim()) : translatedStamp;
              return { ...el, arabicContent: finalAr, translatedLang: tl };
            }

            // Translate Signature Elements (e.g. Authorized Signatory, Doctor Signature)
            if (el.type === 'signature') {
              let arabicContent = undefined;
              if (el.textContent?.trim()) {
                const translatedCap = await translateText(el.textContent.trim());
                const capEchoed = translatedCap.trim().toLowerCase() === el.textContent.trim().toLowerCase();
                arabicContent = capEchoed ? await transliterateName(el.textContent.trim()) : translatedCap;
              }
              let arabicSignatureName = undefined;
              if (el.signatureName?.trim()) {
                if (/\{staff_name\}/i.test(el.signatureName)) {
                  arabicSignatureName = cashierNameLocalized;
                } else {
                  const parts = el.signatureName.split(',').map((p: string) => p.trim()).filter(Boolean);
                  const transliteratedParts = await Promise.all(
                    parts.map(async (p: string) => await transliterateName(p))
                  );
                  arabicSignatureName = transliteratedParts.join('، ');
                }
              }
              return { ...el, arabicContent, arabicSignatureName, translatedLang: tl };
            }

            // Fallback for any other custom user-added elements containing textContent
            if (el.textContent && typeof el.textContent === 'string' && el.textContent.trim() && !el.arabicContent) {
              const translated = await translateText(el.textContent.trim());
              const echoed = translated.trim().toLowerCase() === el.textContent.trim().toLowerCase();
              const finalAr = echoed ? await transliterateName(el.textContent.trim()) : translated;
              return { ...el, arabicContent: finalAr, translatedLang: tl };
            }

            return el;
          })
        );
        const [translatedCart, clonedElements] = await Promise.all([cartTranslationPromise, clonedElementsPromise]);
        // Only update cart state if Arabic text actually changed (stops the screen from flickering/dancing)
        setCart(prev => {
          const hasChange = prev.length !== translatedCart.length || prev.some((it, i) => it.arabicName !== translatedCart[i]?.arabicName);
          return hasChange ? translatedCart : prev;
        });
        cloned.elements = clonedElements;
        // Auto-reflow: push elements down if content grew
        
        // Batch translate dynamic field labels & values
        const fieldLabels = dynamicFields.map(f => f.label);
        const translatedFieldLabels = await translateBatchText(fieldLabels);
        const dynamicFieldMap: Record<string, string> = {};
        dynamicFields.forEach((f, idx) => {
          dynamicFieldMap[f.key] = translatedFieldLabels[idx] || f.label;
        });
        setDynamicFieldLabelsAr(prev => ({ ...prev, ...dynamicFieldMap }));

        // Batch translate dynamic field values
        const dynamicEntries = Object.entries(dynamicValues);
        const valsToTranslate = dynamicEntries.map(([, v]) => isLiteralFieldValue(v) ? '' : v);
        const translatedDynamicVals = await translateBatchText(valsToTranslate);

        const dynamicValsAr: Record<string, string> = {};
        dynamicEntries.forEach(([k, origV], idx) => {
          if (!origV?.trim()) return;
          if (isLiteralFieldValue(origV)) {
            dynamicValsAr[k] = (tl === 'ar' && /^\d+$/.test(origV.trim()))
              ? toArabicIndicDigits(origV.trim())
              : origV.trim();
          } else {
            dynamicValsAr[k] = translatedDynamicVals[idx] || origV;
          }
        });
        setTranslatedValues(prev => ({ ...prev, ...dynamicValsAr }));

        // Batch translate all 17 static receipt labels in 1 SINGLE API call
        const staticLabelsPromise = (async () => {
          const cached = staticLabelCache[tl];
          if (cached) return cached;
          const staticSources: Record<string, string> = {
            qrInvoiceLabel: 'Scan to view invoice',
            qrTokenLabel: 'Scan to track order',
            toLabel: 'To:',
            billToLabel: 'Invoice To:',
            buyerTaxLabel: 'Tax ID',
            emailLabel: 'Email',
            phoneLabel: 'Phone',
            addressLabel: 'Address',
            walkinLabel: 'Walk-in Customer',
            notesLabel: 'Notes:',
            subtotalLabel: 'Subtotal',
            totalLabel: 'TOTAL',
            itemLabel: 'Item',
            qtyLabel: 'Quantity',
            amtLabel: 'Amount',
            shippingLabel: 'Shipping',
            taxLabel: 'Total Tax',
            discountLabel: 'Discount',
          };
          const staticKeys = Object.keys(staticSources);
          const wordsToBatch = staticKeys.map(k => staticSources[k]);
          
          // All words translated together dynamically in 1 batch call
          const staticTranslated = await translateBatchText(wordsToBatch);

          const candidateLabels: Record<string, string> = {};
          let anyStaticFailed = false;
          await Promise.all(staticKeys.map(async (k, i) => {
            const echoed = staticTranslated[i].trim().toLowerCase() === staticSources[k].trim().toLowerCase();
            candidateLabels[k] = echoed ? await transliterateName(staticSources[k]) : staticTranslated[i];
            if (echoed) anyStaticFailed = true;
          }));

          if (!anyStaticFailed) staticLabelCache[tl] = candidateLabels;
          return candidateLabels;
        })();

        const taxLinesArPromise = Promise.all(
          taxLines.map(async t => {
            const translated = await translateText(t.label);
            const echoed = translated.trim().toLowerCase() === t.label.trim().toLowerCase();
            return { label: t.label, labelAr: echoed ? await transliterateName(t.label) : translated };
          })
        );

        // Named GST/VAT component labels (CGST/SGST/IGST/QST/B2B/Custom)
        const taxBreakdownArPromise = (async () => {
          const dict: Record<string, string> = {};
          if (!taxBreakdown || taxBreakdown.length === 0) return dict;

          // Dynamically translate ITC for whatever language is selected (French, Spanish, Hindi, Arabic, etc.)
          const translatedITC = await translateText('Input Tax Credit');
          const itcLabel = (translatedITC && translatedITC.toLowerCase() !== 'input tax credit') 
            ? translatedITC 
            : await translateText('ITC');

          await Promise.all(
            taxBreakdown.map(async l => {
              const translated = await translateText(l.name);
              const echoed = translated.trim().toLowerCase() === l.name.trim().toLowerCase();
              const nameTranslated = echoed ? await transliterateName(l.name) : translated;
              const rateStr = `${l.rate}%`;

              // Universal language mapping (works for any target language)
              dict[l.name] = nameTranslated;
              dict[`${l.name} (ITC)`] = `${nameTranslated} (${itcLabel})`;
              dict[`${l.name} (${rateStr})`] = `${nameTranslated} (${rateStr})`;
              dict[`${l.name} (${rateStr}) (ITC)`] = `${nameTranslated} (${rateStr}) (${itcLabel})`;
            })
          );
          return dict;
        })();

        // Smart translator for rules like "Loyal2" -> "लॉयल 2" (same as Quick Invoice)
        const smartTranslateLabel = async (text: string, fallback: string) => {
          const raw = (text || fallback).trim();
          if (!raw) return '';
          const numMatch = raw.match(/(\d+[\s%]*)$/);
          const baseWords = raw.replace(/(\d+[\s%]*)$/, '').trim();
          const trailingNum = numMatch ? ` ${numMatch[1].trim()}` : '';
          if (!baseWords) return raw;

          let result = await transliterateName(baseWords);
          if (!result || (result.trim().toLowerCase() === baseWords.toLowerCase() && /[a-zA-Z]/.test(result))) {
            const tr = await translateText(baseWords);
            result = (tr && tr.trim().toLowerCase() !== baseWords.toLowerCase()) ? tr : baseWords;
          }
          return `${result || baseWords}${trailingNum}`.trim();
        };

        // Translate all discounts including automatic Loyalty discount label
        const allDiscountsToTranslate = [...discountLines];
        if (autoDiscountRule?.label && !allDiscountsToTranslate.some(d => d.label === autoDiscountRule.label)) {
          allDiscountsToTranslate.push({ label: autoDiscountRule.label, rate: 0 });
        } else if (!allDiscountsToTranslate.some(d => d.label === 'Loyalty Discount' || d.label === 'Loyalty')) {
          allDiscountsToTranslate.push({ label: 'Loyalty', rate: 0 });
        }

        const discountLinesArPromise = Promise.all(
          allDiscountsToTranslate.map(async d => {
            const labelAr = await smartTranslateLabel(d.label, 'Discount');
            return { label: d.label, labelAr };
          })
        );
        // Read the ref, not the closed-over grandTotal — if the cart changed
        // while this translation run was awaiting network calls, this picks
        // up the CURRENT total instead of translating a stale one.
        const amountWordsArPromise = showAmountInWords
          ? (async () => {
              const englishWords = amountToWords(grandTotalRef.current, activeCurrency);
              const wordsTranslated = await translateText(englishWords);
              const wordsEchoed = wordsTranslated.trim().toLowerCase() === englishWords.trim().toLowerCase();
              return wordsEchoed ? await transliterateName(englishWords) : wordsTranslated;
            })()
          : Promise.resolve(null);
        const notesArPromise = notes?.trim() ? translateText(notes.trim()) : Promise.resolve(null);

        const [staticLabels, taxLinesArResult, discountLinesArResult, amountWordsArResult, notesArResult, taxBreakdownArResult] =
          await Promise.all([staticLabelsPromise, taxLinesArPromise, discountLinesArPromise, amountWordsArPromise, notesArPromise, taxBreakdownArPromise]);

        (window as any).__pos_qr_invoice_label = staticLabels.qrInvoiceLabel;
        (window as any).__pos_qr_token_label = staticLabels.qrTokenLabel;
        (window as any).__pos_to_label = staticLabels.toLabel;
        (window as any).__pos_bill_to_label = staticLabels.billToLabel;
        (window as any).__pos_buyer_tax_label = staticLabels.buyerTaxLabel;
        (window as any).__pos_email_label = staticLabels.emailLabel;
        (window as any).__pos_phone_label = staticLabels.phoneLabel;
        (window as any).__pos_address_label = staticLabels.addressLabel;
        (window as any).__pos_walkin_label = staticLabels.walkinLabel;

        (window as any).__pos_row_labels_ar = {
          subtotal: staticLabels.subtotalLabel,
          total: staticLabels.totalLabel,
          item: staticLabels.itemLabel,
          qty: staticLabels.qtyLabel,
          amt: staticLabels.amtLabel,
          shipping: staticLabels.shippingLabel,
          tax: staticLabels.taxLabel,
          discount: discountLinesArResult?.[0]?.labelAr || staticLabels.discountLabel || '',
        };
        (window as any).__pos_tax_lines_ar = taxLinesArResult;
        (window as any).__pos_discount_lines_ar = discountLinesArResult;
        (window as any).__pos_tax_breakdown_ar = Array.isArray(taxBreakdownArResult)
          ? Object.fromEntries(taxBreakdownArResult)
          : taxBreakdownArResult;
        (window as any).__pos_amount_words_ar = amountWordsArResult;
        (window as any).__pos_notes_ar = notesArResult;
        (window as any).__pos_notes_label_ar = staticLabels.notesLabel;

        setTranslatedTemplate(cloned);
        return cloned;
      }
      return null;
    } catch (err) {
      alert('Translation failed.');
      return null;
    } finally {
      setIsTranslatingPOS(false);
    }
  };

  // Always track the freshest checkbox state in a ref
  const includeFieldsRef = useRef(includeFields);
  useEffect(() => { includeFieldsRef.current = includeFields; }, [includeFields]);

  const translateInFlightRef = useRef<Promise<any> | null>(null);
  const translatePendingAgainRef = useRef(false);

  const handlePOSTranslate = async () => {
    if (cart.length === 0) { return null; }
    // If translation is already in flight, reuse it — NEVER queue another loop!
    if (translateInFlightRef.current) {
      return translateInFlightRef.current;
    }
    const promise = (async () => {
      try {
        return await runPOSTranslate();
      } finally {
        translateInFlightRef.current = null;
      }
    })();
    translateInFlightRef.current = promise;
    return promise;
  };

  const handlePOSTranslateRef = useRef<() => void>(() => {});
useEffect(() => { handlePOSTranslateRef.current = handlePOSTranslate; });

// 🔄 Instantly switch templates in preview when user picks a different template
useEffect(() => {
  setTranslatedTemplate(null);
  setLastSyncedElements([]);
  setLastSyncedTemplate(null);

  // If bilingual is active, re-translate the new template
  if (bilingualEnabled && cart.length > 0) {
    clearTimeout(autoTranslateTimer.current);
    autoTranslateTimer.current = setTimeout(() => {
      handlePOSTranslate();
    }, 150);
  }
}, [selectedTemplateId]);

// Auto-translate on cart & customer changes — only active while bilingualEnabled is ON.
const autoTranslateTimer = useRef<any>(null);
const cartSignature = cart.map(i => `${i.name}:${i.qty}:${getEffectiveTaxRate(i)}:${isB2B ? 1 : 0}`).join('|');
const includeFieldsSignature = `${includeFields.name}:${includeFields.phone}:${includeFields.email}:${includeFields.address}:${includeFields.taxId}`;

useEffect(() => {
  if (!bilingualEnabled) return;
  if (cart.length === 0) return;
  clearTimeout(autoTranslateTimer.current);
  autoTranslateTimer.current = setTimeout(() => {
    handlePOSTranslate();
  }, 600); // 600ms prevents network collisions and infinite loops
  return () => clearTimeout(autoTranslateTimer.current);
}, [cartSignature, bilingualEnabled, clientName, clientEmail, clientPhone, clientGSTIN, clientAddress, notes, showAmountInWords, buyerRegion, isB2B, includeFieldsSignature, posColumns.map(c => `${c.key}@${c.after || ''}`).join('|'), hiddenColumns.join('|')]);


const [lastTokenQR, setLastTokenQR] = useState<{ token: string; qr: string } | null>(null);

// ── Read-only A4/Letter canvas preview (locked CanvasWorkspace) ───────────
const canvasPreviewCanvasRef = useRef<HTMLDivElement>(null);
const [canvasQrDataUrls, setCanvasQrDataUrls] = useState<Record<string, string>>({});
// Faint placeholder QR codes for the A4/Letter live preview's "Scan to view
// invoice" / "Scan to track order" blocks — generated once since they don't
// encode anything real yet (no saved invoice/token exists until Save),
// mirroring what showTokenQRPlaceholder already does on the thermal preview.
const [placeholderQrDataUrls, setPlaceholderQrDataUrls] = useState<{ invoice: string; token: string } | null>(null);
useEffect(() => {
  let cancelled = false;
  (async () => {
    const QRCode = await import('qrcode');
    const [invoice, token] = await Promise.all([
      QRCode.default.toDataURL('preview', { width: 200, margin: 1 }),
      QRCode.default.toDataURL('preview-token', { width: 200, margin: 1 }),
    ]);
    if (!cancelled) setPlaceholderQrDataUrls({ invoice, token });
  })();
  return () => { cancelled = true; };
}, []);
const [canvasZoom, setCanvasZoom] = useState(0.5);
const [canvasPageBehavior, setCanvasPageBehavior] = useState<'fixed' | 'auto_expand' | 'multi_page'>('auto_expand');
const noopCanvasHandler = () => {};
const [businessType, setBusinessType] = useState<'restaurant' | 'retail' | 'pharmacy' | 'corporate' | 'logistics' | 'other'>(() => {
  return (localStorage.getItem('cloudairambo_business_type') as any) || 'retail';
});
const [enableTracking, setEnableTracking] = useState(true);
const [tableNumber, setTableNumber] = useState(() => initialCart?.tableNumber || '');
const [checkInDate, setCheckInDate] = useState(() => new Date().toISOString().split('T')[0]);
const [covers, setCovers] = useState('');
const [kotNumber, setKotNumber] = useState('');
const [roomNumber, setRoomNumber] = useState('');
const [guestName, setGuestName] = useState('');
const [checkOutDate, setCheckOutDate] = useState('');
const [postSaveActions, setPostSaveActions] = useState(() => {
  try {
    const saved = localStorage.getItem('cloudairambo_pos_actions');
    return saved ? JSON.parse(saved) : { print: true, whatsapp: false, email: false, skipConfirm: false };
  } catch {
    return { print: true, whatsapp: false, email: false };
  }
});

// Auto-send once a valid phone is typed into the Token QR modal — walk-in
// customers often don't have a phone on file until this point, so Save-time
// auto actions (postSaveActions.whatsapp) had nothing to send to. This picks
// up where that left off: debounced on typing, fires once, only while the
// modal is open and Auto WhatsApp is enabled. Doesn't touch the manual button.
const autoModalSendRef = useRef<{ phone: string; email: string }>({ phone: '', email: '' });
useEffect(() => {
  if (!lastTokenQR || !lastSavedInvoice) return;
  const cleanPhone = getCleanE164Phone(clientPhone, outletPhoneCountry || 'IN');
  const digits = cleanPhone.replace(/\D/g, '');
  const timer = setTimeout(async () => {
    if (postSaveActions.whatsapp && digits && autoModalSendRef.current.phone !== digits) {
      autoModalSendRef.current.phone = digits;
      try {
        const waitUrl = `${APP_URL}/wait?token=${lastTokenQR.token}`;
        const activeBrand = companyName || authUser?.name || 'Our Company';
        const caption = `🎟 Your order token: *${lastTokenQR.token}*\n\nTrack your order here:\n${waitUrl}\n\nWe'll notify you when it's ready! 🔔\n\n— ${activeBrand}`;
        const token = getStoredToken();
        const pdfBlob = await buildPOSPdfBlob();
        if (pdfBlob) {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ phone: digits, base64, filename: `Receipt_${lastSavedInvoice.number}.pdf`, caption }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data?.success === false) {
            throw new Error(data?.error || data?.detail || `WhatsApp send failed (${res.status})`);
          }
          postLog('POS_TOKEN_WHATSAPP_SENT', `Order token ${lastTokenQR.token} + receipt PDF auto-sent via WhatsApp to ${clientName || 'Walk-in'} | ${cleanPhone}`);
          showToast(`✅ WhatsApp sent to ${cleanPhone}`, '#16a34a');
        }
      } catch (e: any) {
        console.warn('Auto WhatsApp (modal) failed:', e);
        showToast(`⚠️ WhatsApp not sent: ${e?.message || 'unknown error'}`);
        autoModalSendRef.current.phone = '';
      }
    }
    if (postSaveActions.email && clientEmail.includes('@') && autoModalSendRef.current.email !== clientEmail) {
      autoModalSendRef.current.email = clientEmail;
      try {
        const waitUrl = `${APP_URL}/wait?token=${lastTokenQR.token}`;
        const tkn = getStoredToken();
        const pdfBlob = await buildPOSPdfBlob();
        if (pdfBlob) {
          const pdfBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          const activeBrand = companyName || authUser?.name || 'Our Company';
          const res = await fetch(`${BACKEND}/email/send-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
            body: JSON.stringify({
              invoice_id: Number(lastSavedInvoice.id), to_email: clientEmail, attach_pdf: true,
              pdf_base64: pdfBase64, pdf_filename: `${lastSavedInvoice.number}.pdf`,
              subject: `Your Order Token: ${lastTokenQR.token}`,
              message: `Your order token is: ${lastTokenQR.token}\n\nTrack your order here: ${waitUrl}\n\nWe'll notify you when it's ready!\n\n— ${activeBrand}`,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data?.success === false) {
            throw new Error(data?.error || data?.detail || `Email send failed (${res.status})`);
          }
          postLog('POS_TOKEN_EMAIL_SENT', `Order token ${lastTokenQR.token} + receipt PDF auto-emailed to ${clientName || 'Walk-in'} | ${clientEmail}`);
          showToast(`✅ Email sent to ${clientEmail}`, '#16a34a');
        }
      } catch (e: any) {
        console.warn('Auto Email (modal) failed:', e);
        showToast(`⚠️ Email not sent: ${e?.message || 'unknown error'}`);
        autoModalSendRef.current.email = '';
      }
    }
  }, 1000); // debounce — wait for the cashier to finish typing the number
  return () => clearTimeout(timer);
}, [clientPhone, clientEmail, lastTokenQR, lastSavedInvoice, postSaveActions.whatsapp, postSaveActions.email]);

  const showToast = (message: string, background = '#dc2626') => {
    const toast = document.createElement('div');
    toast.innerText = message;
    toast.style.cssText = `position:fixed;bottom:28px;left:50%;transform:translateX(-50%);background:${background};color:#fff;padding:12px 24px;border-radius:12px;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,0.3);max-width:90vw;text-align:center;pointer-events:none;`;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.4s';
      setTimeout(() => document.body.removeChild(toast), 400);
    }, 3500);
  };

  // Notify the cashier the moment a sale becomes B2B — same behavior as
  // QuickInvoiceView. Only fires on the false->true transition.
  const wasB2BRef = useRef(false);
  useEffect(() => {
    if (isB2B && !wasB2BRef.current) {
      if (taxIdMeta.isValid) {
        showToast(`✅ B2B billing active — valid ${taxIdMeta.taxLabel} detected. B2B tax rates will apply where set.`, '#2563eb');
      } else if (forceB2BOverride) {
        showToast(`⚠️ B2B billing active — Tax ID could not be validated, applied manually by cashier.`, '#f59e0b');
      }
    }
    wasB2BRef.current = isB2B;
  }, [isB2B]);

  const handleScanAdd = (description: string, qty: number, rate: number, barcode?: string) => {
    // 🔒 Fast QR Badge Scanner (Switches Cashier / Doctor / Receptionist instantly)
    if (description.startsWith('CASHIER_TOKEN:') || description.startsWith('STAFF_BADGE:')) {
      const token = description.replace(/^(CASHIER_TOKEN:|STAFF_BADGE:)/, '').trim();
      
      const applyStaffName = (name: string, role?: string) => {
        setWaiterName(name);
        showToast(`👤 Active Staff: ${name}${role ? ` (${role})` : ''}`, '#16a34a');
        if (bilingualEnabled) {
          clearTimeout(autoTranslateTimer.current);
          handlePOSTranslate();
        }
      };

      if (token.startsWith('ey') || token.length > 40) {
        // JWT Auth Token scan
        fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(user => {
            if (user?.name) applyStaffName(user.name, user.role);
          })
          .catch(() => showToast('⚠️ Invalid employee QR badge'));
      } else {
        // Direct employee badge name scan (e.g. STAFF_BADGE:Dr. Rajesh Mehta)
        applyStaffName(token);
      }
      return;
    }

    // Try to match against catalog by name or barcode
    const matched = products.find(
      p => p.name.toLowerCase() === description.toLowerCase() ||
        (barcode && p.barcode === barcode)
    );
    if (matched) {
      addToCart(matched);
    } else {
      setCart(prev => {
        const existing = prev.findIndex(i =>
          i.name.toLowerCase() === description.toLowerCase()
        );
        if (existing >= 0) {
          return prev.map((item, idx) =>
            idx === existing ? { ...item, qty: item.qty + qty } : item
          );
        }
        return [...prev, {
          id: `ci-${Date.now()}`,
          barcode,
          name: description,
          rate,
          qty,
        }];
      });
    }
  };

  // ── Save Bill ─────────────────────────────────────────────────────────────

 const handleUpdate = async () => {
  if (!lastSavedInvoice) return;
  setSaving(true);
  try {
    const cleanPhone = getCleanE164Phone(clientPhone, outletPhoneCountry || 'IN');
    const phoneDigits = cleanPhone.replace(/\D/g, '');

    // 1. Resolve live bilingual translation if enabled (always refresh with current customer name)
    clearTimeout(autoTranslateTimer.current);
    let activeTranslatedTemplate = translatedTemplate;
    if (bilingualEnabled && cart.length > 0) {
      if (translateInFlightRef.current) {
        activeTranslatedTemplate = await translateInFlightRef.current;
      } else if (!activeTranslatedTemplate) {
        activeTranslatedTemplate = await handlePOSTranslate();
      }
    }

    const template = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
    const baseTemplate = activeTranslatedTemplate || template;
    const elements = JSON.parse(JSON.stringify(baseTemplate.elements));

    // 2. Patch table with updated cart items and ensure Loyalty discount is always calculated
    const patchedElements = elements.map((el: any) => {
      if (el.type !== 'table') return el;
      const manualDiscounts = discountLines.filter(d => d.rate > 0);
      const allDiscounts = [...manualDiscounts];

      // Always re-apply loyalty discount on update
      if (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0) {
        const impliedRate = subtotal > 0 ? (autoDiscountAmount / subtotal) * 100 : 0;
        allDiscounts.push({
          label: autoDiscountRule.label || 'Loyalty Discount',
          rate: parseFloat(impliedRate.toFixed(4)),
          amount: autoDiscountAmount,
          _isAuto: true,
        });
      }

      return {
        ...el,
        rows: cart.map((item, i) => ({
          id: `r-${Date.now()}-${i}`,
          values: {
            description: item.name, qty: item.qty, rate: item.rate, total: item.qty * item.rate, ...posColValues(item), __customAr: (item as any).customColAr,
            arabicName: (item as any).arabicName || '',
            taxRate: getEffectiveTaxRate(item),
            taxName: isB2B ? (item.b2bTaxName || item.taxName || 'Tax') : (item.taxName || 'Tax'),
            b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
            b2bTaxName: isB2B ? (item.b2bTaxName || item.taxName || 'Tax') : undefined,
            taxInclusive: !!item.taxInclusive,
          },
        })),
        taxes: taxLines.filter(t => t.rate > 0),
        discounts: allDiscounts,
        columns: buildPosColumns(el.columns),
        shippingCost: shippingCost > 0 ? shippingCost : undefined,
        buyer_tax_id: clientGSTIN.trim() || undefined,
        buyerTaxId: clientGSTIN.trim() || undefined,
        taxBreakdownAr: activeTranslatedTemplate ? (window as any).__pos_tax_breakdown_ar : undefined,
      };
    });

    const cashierName = waiterName || authUser?.name?.trim() || 'Staff';
    const finalInvoiceNumber = lastSavedInvoice.number;

    // 3. Build customer text block cleanly
    const clientBlock = buildClientBlock(
      { name: clientName, phone: cleanPhone || clientPhone, email: clientEmail, address: clientAddress, taxId: clientGSTIN },
      includeFields,
      Boolean(activeTranslatedTemplate)
    );

    // 4. Resolve placeholders non-destructively (Zero regex, zero jumping tables)
    const patchedTextElements = patchedElements.map((el: any) =>
      resolveElementTokens(el, {
        invoiceNumber: finalInvoiceNumber,
        invoiceDate,
        staffName: cashierName,
        staffNameAr: (window as any).__pos_staff_name_ar,
        clientDetailsEn: clientBlock.en,
        clientDetailsTr: clientBlock.tr,
        clientNameEn: includeFields.name ? clientName : '',
        readyBy,
        notes,
        notesAr: activeTranslatedTemplate
          ? `${(window as any).__pos_notes_label_ar || 'Notes:'}\n${(window as any).__pos_notes_ar || notes}`
          : undefined,
        dynamicValues,
        dynamicValuesAr: translatedValues,
        dynamicFieldLabelsAr,
      })
    );

    // 4b. Resolve public invoice URL & tracking token
    const tkn = getStoredToken();
    const publicToken = lastSavedInvoice.publicToken;
    const invoiceQRValue = publicToken ? `${APP_URL}/invoice/view/${publicToken}` : '';
    let currentToken = generatedTokenRef.current;
    if (!currentToken) {
      const tokenEl = lastSyncedElements.find((el: any) => el.id?.startsWith('token-label-'));
      const match = tokenEl?.textContent?.match(/Token:\s*(\w+)/);
      if (match) currentToken = match[1];
    }
    if (enableTracking && !currentToken) {
      try {
        const tokenRes = await fetch(`${BACKEND}/tokens/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
          body: JSON.stringify({
            customer_name: clientName || 'Walk-in',
            order_note: cart.map(i => `${i.qty}x ${i.name}`).join(', '),
            outlet_id: outletId || null,
          }),
        });
        const tokenData = await tokenRes.json();
        currentToken = tokenData.token;
        generatedTokenRef.current = currentToken;
      } catch (e) { console.warn('Token regen failed:', e); }
    }

    const QRCode = (await import('qrcode')).default;
    const isA4OrLetter = template.pageSize === 'A4' || template.pageSize === 'LETTER';
    const pageWidth = template.pageSize === 'A4' ? 794
      : template.pageSize === 'LETTER' ? 816
      : template.pageSize === 'LABEL_4X6' ? 450
      : template.pageSize === 'LABEL_3X2' ? 288
      : 302;

    // Update template built-in QR elements to point to the real invoice public link
    const updatedWithTemplateQRs = patchedTextElements.map((el: any) => {
      if (el.type === 'qr' && invoiceQRValue) {
        return { ...el, qrValue: invoiceQRValue, textContent: invoiceQRValue };
      }
      return el;
    });

    const finalElements = tightenTable(updatedWithTemplateQRs);

    // Match Quick Invoice: Bake includeFields into elements[0] for public scan-to-invoice
    if (finalElements.length > 0) {
      finalElements[0] = {
        ...finalElements[0],
        includeFields: { ...includeFields },
        include_fields: { ...includeFields },
      };
    }

    // Append correct invoice and tracking QR codes
    if (invoiceQRValue) {
      const templateQREl = finalElements.find((el: any) => el.type === 'qr' && !el.id?.startsWith('invoice-qr-') && !el.id?.startsWith('token-qr-'));
      const autoQRSize = isA4OrLetter ? 100 : (templateQREl?.width || 80);
      const autoQRX = Math.round((pageWidth - autoQRSize) / 2);
      const invQrDataUrl = await QRCode.toDataURL(invoiceQRValue, { width: autoQRSize * 2, margin: 1 });
      const waitQRValue = (enableTracking && currentToken) ? `${APP_URL}/wait?token=${currentToken}` : null;

      if (enableTracking && waitQRValue) {
        const qrDataUrl = await QRCode.toDataURL(waitQRValue, { width: autoQRSize * 2, margin: 1 });
        if (isA4OrLetter) {
          const tableForAlign = finalElements.find((el: any) => el.type === 'table');
          const boxLeft = tableForAlign ? tableForAlign.x : 10;
          const boxRight = tableForAlign ? tableForAlign.x + tableForAlign.width : pageWidth - 10;
          const boxMid = (boxLeft + boxRight) / 2;
          const invoiceQrX = boxLeft;
          const tokenQrX = boxRight - autoQRSize;
          const rowY = finalElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 20;

          finalElements.push(
            { id: `invoice-label-${Date.now()}`, type: 'text', x: boxLeft, y: rowY, width: boxMid - boxLeft, height: 18, zIndex: 99, opacity: 100, rotation: 0, textContent: `Scan to view invoice`, arabicContent: (window as any).__pos_qr_invoice_label || 'Scan to view invoice', fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#374151', textAlign: 'left', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
            { id: `invoice-qr-${Date.now()}`, type: 'image', x: invoiceQrX, y: rowY + 22, width: autoQRSize, height: autoQRSize, zIndex: 99, opacity: 100, rotation: 0, textContent: invQrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
            { id: `token-label-${Date.now()}`, type: 'text', x: boxMid, y: rowY, width: boxRight - boxMid, height: 18, zIndex: 100, opacity: 100, rotation: 0, textContent: `Token: ${currentToken} — Scan to track order`, arabicContent: `${currentToken} — ${(window as any).__pos_qr_token_label || 'Scan to track order'}`, fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#6366f1', textAlign: 'right', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
            { id: `token-qr-${Date.now()}`, type: 'image', x: tokenQrX, y: rowY + 22, width: autoQRSize, height: autoQRSize, zIndex: 100, opacity: 100, rotation: 0, textContent: qrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
          );
        } else {
          const invQrY = finalElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 20;
          finalElements.push(
            { id: `invoice-label-${Date.now()}`, type: 'text', x: 10, y: invQrY, width: pageWidth - 20, height: 18, zIndex: 99, opacity: 100, rotation: 0, textContent: `Scan to view invoice`, arabicContent: (window as any).__pos_qr_invoice_label || 'Scan to view invoice', fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#374151', textAlign: 'center', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
            { id: `invoice-qr-${Date.now()}`, type: 'image', x: autoQRX, y: invQrY + 22, width: autoQRSize, height: autoQRSize, zIndex: 99, opacity: 100, rotation: 0, textContent: invQrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
          );
          const tokenY = finalElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 16;
          finalElements.push(
            { id: `token-label-${Date.now()}`, type: 'text', x: 10, y: tokenY, width: pageWidth - 20, height: 18, zIndex: 100, opacity: 100, rotation: 0, textContent: `Token: ${currentToken} — Scan to track order`, arabicContent: `${currentToken} — ${(window as any).__pos_qr_token_label || 'Scan to track order'}`, fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#6366f1', textAlign: 'center', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
            { id: `token-qr-${Date.now()}`, type: 'image', x: autoQRX, y: tokenY + 22, width: autoQRSize, height: autoQRSize, zIndex: 100, opacity: 100, rotation: 0, textContent: qrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
          );
        }
      } else {
        const invQrY = finalElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 20;
        finalElements.push(
          { id: `invoice-label-${Date.now()}`, type: 'text', x: 10, y: invQrY, width: pageWidth - 20, height: 18, zIndex: 99, opacity: 100, rotation: 0, textContent: `Scan to view invoice`, arabicContent: (window as any).__pos_qr_invoice_label || 'Scan to view invoice', fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#374151', textAlign: 'center', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
          { id: `invoice-qr-${Date.now()}`, type: 'image', x: autoQRX, y: invQrY + 22, width: autoQRSize, height: autoQRSize, zIndex: 99, opacity: 100, rotation: 0, textContent: invQrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
        );
      }
    }

    const updatedTotal = getFrozenTotals(finalElements)?.grandTotal ?? grandTotal;

    // Ensure translation metadata and elements are clean before saving to database
    if (!activeTranslatedTemplate) {
      // If bilingual is OFF, strip all translated content so public view renders clean monolingual
      for (let i = 0; i < finalElements.length; i++) {
        if (finalElements[i].arabicContent !== undefined) {
          const { arabicContent, translatedLang, ...rest } = finalElements[i];
          finalElements[i] = rest;
        }
      }
    } else {
      // Stamp translated language into elements so public view renderer detects bilingual mode
      const firstTextIdx = finalElements.findIndex((el: any) => el.type === 'text' && el.arabicContent);
      if (firstTextIdx >= 0) {
        finalElements[firstTextIdx] = {
          ...finalElements[firstTextIdx],
          translatedLang: posTranslateLang,
        };
      }
    }

    const dynamicValuesArToUpdate = activeTranslatedTemplate ? {
      clientName: (window as any).__pos_client_name_ar || clientName,
      client_address: (window as any).__pos_client_address_ar || clientAddress,
      clientAddress: (window as any).__pos_client_address_ar || clientAddress,
      address: (window as any).__pos_client_address_ar || clientAddress,
      label_subtotal: (window as any).__pos_row_labels_ar?.subtotal,
      label_total: (window as any).__pos_row_labels_ar?.total,
      label_tax: (window as any).__pos_row_labels_ar?.tax,
      label_shipping: (window as any).__pos_row_labels_ar?.shipping,
      label_discount: (window as any).__pos_row_labels_ar?.discount,
      ...((window as any).__pos_tax_breakdown_ar || {}),
    } : null;

    const dynamicFieldLabelsArToUpdate = activeTranslatedTemplate ? {
      tax_id: (window as any).__pos_buyer_tax_label || 'Tax ID',
      email: (window as any).__pos_email_label || 'Email',
      phone: (window as any).__pos_phone_label || 'Phone',
      address: (window as any).__pos_address_label || 'Address',
    } : null;

    const clientNameArVal = (window as any).__pos_client_name_ar || clientName;
    const clientAddressArVal = (window as any).__pos_client_address_ar || clientAddress;

    // 5. Update invoice on backend (syncs all bilingual & tracking flags with database)
    await fetch(`${BACKEND}/invoices/${lastSavedInvoice.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
      body: JSON.stringify({
        amount: updatedTotal,
        payment_mode: paymentMode,
        client_name: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
        clientName: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
        client_email: (includeFields.email ? clientEmail : '').trim(),
        clientEmail: (includeFields.email ? clientEmail : '').trim(),
        client_phone: (includeFields.phone ? (cleanPhone || clientPhone) : '').trim(),
        clientPhone: (includeFields.phone ? (cleanPhone || clientPhone) : '').trim(),
        client_address: (includeFields.address ? clientAddress : '').trim(),
        clientAddress: (includeFields.address ? clientAddress : '').trim(),
        client_name_ar: activeTranslatedTemplate ? clientNameArVal : undefined,
        clientNameAr: activeTranslatedTemplate ? clientNameArVal : undefined,
        client_address_ar: activeTranslatedTemplate ? clientAddressArVal : undefined,
        clientAddressAr: activeTranslatedTemplate ? clientAddressArVal : undefined,
        include_fields: { ...includeFields },
        includeFields: { ...includeFields },
        // 👤 Persist staff and modifier info separately in database
        staff_name: cashierName,
        cashier_name: cashierName,
        updated_by_name: authUser?.name || 'Staff',
        elements: finalElements,
        cached_html: null,
        outlet_id: outletId || null,
        register_id: registerId,
        buyer_tax_id: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
        buyerTaxId: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
        clientGSTIN: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : '',
        buyer_country: taxIdMeta.countryCode || sellerCountry,
        buyer_region: buyerRegion,
        seller_country: sellerCountry,
        seller_region: sellerRegion,
        currency: activeCurrency,
        is_bilingual: !!activeTranslatedTemplate,
        translated_lang: activeTranslatedTemplate ? posTranslateLang : null,
        row_labels_ar: activeTranslatedTemplate ? (window as any).__pos_row_labels_ar : null,
        tax_lines_ar: activeTranslatedTemplate ? (window as any).__pos_tax_lines_ar : null,
        discount_lines_ar: activeTranslatedTemplate ? (window as any).__pos_discount_lines_ar : null,
        tax_breakdown_ar: activeTranslatedTemplate ? (window as any).__pos_tax_breakdown_ar : null,
        dynamic_values_ar: dynamicValuesArToUpdate,
        dynamic_field_labels_ar: dynamicFieldLabelsArToUpdate,
        show_amount_in_words: showAmountInWords,
        amount_in_words_translated: (showAmountInWords && activeTranslatedTemplate)
          ? (window as any).__pos_amount_words_ar
          : null,
        line_items: cart.map(item => {
          const lineAmount = item.qty * item.rate;
          const rate = getEffectiveTaxRate(item);
          const taxAmt = rate ? (item.taxInclusive ? lineAmount - lineAmount / (1 + rate / 100) : lineAmount * (rate / 100)) : 0;
          return {
            description: item.name,
            arabic_name: activeTranslatedTemplate ? ((item as any).arabicName || '') : '',
            qty: item.qty,
            unit_price: item.rate,
            amount: lineAmount,
            tax_rate: rate,
            tax_inclusive: !!item.taxInclusive,
            tax_amount: parseFloat(taxAmt.toFixed(2)),
          };
        }),
      }),
    });

    setLastSavedInvoice(prev => prev ? { ...prev, total: updatedTotal } : null);
    setBills(prev => prev.map(b => b.id === lastSavedInvoice.id ? { ...b, amount: updatedTotal } : b));
    setLastSyncedElements([...finalElements]);
    setLastSyncedTemplate(template);
    // non-bilingual A4 PDF screenshots the live preview DOM, so let it re-render with the updated elements first
    await new Promise(resolve => setTimeout(resolve, 900));

    if (outletDisplayToken) {
      fetch(`${BACKEND}/display/push/${outletDisplayToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
        body: JSON.stringify({ bill_id: lastSavedInvoice.id, register_id: registerId }),
      }).catch(() => {});
    }

    // 6. Show Token QR Modal on Update if Tracking Enabled
    if (enableTracking && currentToken) {
      const waitUrl = `${APP_URL}/wait?token=${currentToken}`;
      const qrDataUrl = await QRCode.toDataURL(waitUrl, { width: 250, margin: 2 });
      autoModalSendRef.current = { phone: '', email: '' }; // Allow auto-send on every update
      setLastTokenQR({ token: currentToken, qr: qrDataUrl }); // Opens the Pop-up Modal!
    }

    // 7. Auto-Actions on Update: WhatsApp & Email (fires every time)
    if (paymentMode === 'cash') {
      if (postSaveActions.whatsapp && phoneDigits) {
        try {
          const waitUrl = currentToken ? `${APP_URL}/wait?token=${currentToken}` : '';
          const activeBrand = companyName || authUser?.name || 'Our Company';
          const caption = currentToken
            ? `🎟 Updated order token: *${currentToken}*\n\nTrack your order here:\n${waitUrl}\n\nWe'll notify you when it's ready! 🔔\n\n— ${activeBrand}`
            : `Receipt ${lastSavedInvoice.number} has been updated.\n\n— ${activeBrand}`;
          const pdfBlob = await buildPOSPdfBlob(activeTranslatedTemplate, finalElements);
          if (pdfBlob) {
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(pdfBlob);
            });
            const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
              body: JSON.stringify({ phone: phoneDigits, base64, filename: `Receipt_${lastSavedInvoice.number}.pdf`, caption }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data?.success !== false) {
              postLog('POS_WHATSAPP_SENT', `Updated receipt ${lastSavedInvoice.number} sent via WhatsApp to ${clientName || 'Walk-in'} | ${cleanPhone}`);
              showToast(`✅ Updated WhatsApp sent to ${cleanPhone}`, '#16a34a');
            }
          }
        } catch (e: any) {
          console.warn('Update auto WhatsApp failed:', e);
        }
      }

      if (postSaveActions.email && clientEmail.includes('@')) {
        try {
          let pdfBase64: string | undefined;
          let pdfFilename: string | undefined;
          const pdfBlob = await buildPOSPdfBlob(activeTranslatedTemplate, finalElements);
          if (pdfBlob) {
            pdfBase64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(pdfBlob);
            });
            pdfFilename = `${lastSavedInvoice.number}.pdf`;
          }
          const waitUrl = currentToken ? `${APP_URL}/wait?token=${currentToken}` : '';
          const activeBrand = companyName || authUser?.name || 'Our Company';
          const trackMsg = (enableTracking && currentToken)
            ? `Your order has been updated. Token: ${currentToken}\n\nTrack your order here: ${waitUrl}\n\nWe'll notify you when it's ready!\n\n— ${activeBrand}`
            : `Your invoice ${lastSavedInvoice.number} has been updated.\n\n— ${activeBrand}`;
          const res = await fetch(`${BACKEND}/email/send-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
            body: JSON.stringify({
              invoice_id: Number(lastSavedInvoice.id),
              to_email: clientEmail,
              attach_pdf: true,
              message: trackMsg,
              subject: currentToken ? `Updated Order Token: ${currentToken}` : `Updated Receipt: ${lastSavedInvoice.number}`,
              pdf_base64: pdfBase64,
              pdf_filename: pdfFilename,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.success !== false) {
            postLog('POS_EMAIL_SENT', `Updated receipt ${lastSavedInvoice.number} emailed to ${clientName || 'Walk-in'} | ${clientEmail}`);
            showToast(`✅ Updated Email sent to ${clientEmail}`, '#16a34a');
          }
        } catch (e: any) {
          console.warn('Update auto Email failed:', e);
        }
      }
    }

    // 8. Auto-Print on Update (if enabled in settings)
    if (postSaveActions.print) {
      if (useUSBPrinter) {
        await printReceiptUSB(buildPOSPrintJob(finalElements, lastSavedInvoice.number));
      }
    }

    // Toast feedback
    const toast = document.createElement('div');
    toast.innerText = `✏️ Bill ${lastSavedInvoice.number} updated — ${sym}${updatedTotal.toFixed(2)}`;
    toast.style.cssText = `position:fixed;bottom:28px;left:50%;transform:translateX(-50%);background:#7c3aed;color:#fff;padding:12px 24px;border-radius:12px;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 8px 24px rgba(124,58,237,0.4);`;
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.opacity='0'; toast.style.transition='opacity 0.4s'; setTimeout(() => document.body.removeChild(toast), 400); }, 2500);

  } catch (err: any) {
    alert(`Update failed: ${err.message}`);
  } finally {
    setSaving(false);
  }
};

 const handleSave = async () => {
  if (!authUser) return alert('Please sign in to save bills.');
  if (cart.length === 0) return alert('Add at least one item to the cart.');

  // MANDATORY FOR B2B: Enforce Tax ID entry
  if (isB2B && !clientGSTIN.trim()) {
    alert(`⚠️ Buyer ${taxIdMeta.taxLabel || 'Tax ID'} is mandatory for B2B transactions. Please enter it in the Customer section.`);
    return;
  }
  
  // If bill already saved, update it instead of creating new
  if (lastSavedInvoice) {
    return handleUpdate();
  }

  setSaving(true);
  try {
    const cleanPhone = getCleanE164Phone(clientPhone, outletPhoneCountry || 'IN');
    const phoneDigits = cleanPhone.replace(/\D/g, '');

   const template = allTemplates.find(t => t.id === selectedTemplateId)
  || THERMAL_PRESETS.find(t => t.id === selectedTemplateId || t.name === selectedTemplateId)
  || allTemplates[0];

// bilingualEnabled fires translation on a debounced side effect (800ms after
// cart changes, then network calls) — it does NOT block Save. If the cashier
// saves before that finishes, `translatedTemplate` state is still null here,
// and the bill (and any auto-send) would go out non-bilingual. Wait for it.
// bilingualEnabled fires translation on a debounced side effect — it does NOT
// block Save. If the cashier saves before that finishes, `translatedTemplate`
// state is still null here. Wait for it instead of racing it.
// Cancel any pending bilingual auto-translate unconditionally — a
// leftover 800ms debounce from an edit just before Save can still fire
// mid-save (or right after) even when translatedTemplate is already set,
// rebuilding it from the RAW template and overwriting the version this
// save is about to patch the real invoice number into.
clearTimeout(autoTranslateTimer.current);
let activeTranslatedTemplate = translatedTemplate;
if (bilingualEnabled && cart.length > 0) {
  if (translateInFlightRef.current) {
    activeTranslatedTemplate = await translateInFlightRef.current;
  } else if (!activeTranslatedTemplate) {
    activeTranslatedTemplate = await handlePOSTranslate();
  }
}

const baseTemplate = activeTranslatedTemplate || template;
const elements = JSON.parse(JSON.stringify(baseTemplate.elements));

    // Patch table with cart items
    const patchedElements = elements.map((el: any) => {
      if (el.type !== 'table') return el;
      const newRows = cart.map((item, i) => ({
  id: `r-${Date.now()}-${i}`,
  values: {
    description: item.name,
    qty: item.qty,
    rate: item.rate,
    total: item.qty * item.rate,
    ...posColValues(item),
    __customAr: (item as any).customColAr,
    arabicName: (item as any).arabicName || '',
    taxRate: getEffectiveTaxRate(item),
    taxName: isB2B ? (item.b2bTaxName || item.taxName || 'Tax') : (item.taxName || 'Tax'),
    b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
    b2bTaxName: isB2B ? (item.b2bTaxName || item.taxName || 'Tax') : undefined,
    taxInclusive: !!item.taxInclusive,
  },
}));
      const allDiscounts = discountLines.filter(d => d.rate > 0);
      if (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0) {
        const impliedRate = subtotal > 0 ? (autoDiscountAmount / subtotal) * 100 : 0;
        const loyaltyLabelAr = (window as any).__pos_discount_lines_ar?.find((x: any) => x.label === autoDiscountRule?.label)?.labelAr;
        allDiscounts.push({ 
          label: autoDiscountRule.label || 'Loyalty Discount', 
          rate: parseFloat(impliedRate.toFixed(4)), 
          amount: autoDiscountAmount,
          translatedLabel: loyaltyLabelAr,
          labelAr: loyaltyLabelAr,
          _isAuto: true 
        });
      }
      return {
        ...el,
        rows: newRows,
        taxBreakdownAr: activeTranslatedTemplate ? (window as any).__pos_tax_breakdown_ar : undefined,
        taxes: taxLines.filter(t => t.rate > 0),
        discounts: allDiscounts,
        columns: buildPosColumns(el.columns),
        shippingCost: shippingCost > 0 ? shippingCost : undefined,
        buyer_tax_id: clientGSTIN.trim() || undefined,
        buyerTaxId: clientGSTIN.trim() || undefined,
      };
    });

    const cashierName = waiterName || authUser?.name?.trim() || 'Staff';

    // Build customer block cleanly
    const clientBlock = buildClientBlock(
      { name: clientName, phone: cleanPhone || clientPhone, email: clientEmail, address: clientAddress, taxId: clientGSTIN },
      includeFields,
      Boolean(activeTranslatedTemplate)
    );

    // Resolve placeholders across all elements non-destructively
    const finalElements = patchedElements.map((el: any) =>
      resolveElementTokens(el, {
        invoiceNumber: invoiceNumberOverride.trim() || '{invoice_number}',
        invoiceDate,
        staffName: cashierName,
        staffNameAr: (window as any).__pos_staff_name_ar,
        clientDetailsEn: clientBlock.en,
        clientDetailsTr: clientBlock.tr,
        clientNameEn: includeFields.name ? clientName : '',
        readyBy,
        notes,
        notesAr: activeTranslatedTemplate
          ? `${(window as any).__pos_notes_label_ar || 'Notes:'}\n${(window as any).__pos_notes_ar || notes}`
          : undefined,
        dynamicValues,
        dynamicValuesAr: translatedValues,
        dynamicFieldLabelsAr,
      })
    );

    const receiptType = RECEIPT_TYPE_MAP[template.name] || 'REC';
    const { invoicesApi } = await import('../utils/api');

    // Cash is settled the instant it's tendered — record it as paid in
    // full immediately. Card/UPI aren't confirmed at save time (a
    // PENDING- placeholder is used below), so they stay unpaid until
    // confirmAndPrint() hears back from the gateway and patches this.
    const amountPaidOnSave = paymentMode === 'cash' ? grandTotal : 0;
    const dynamicValuesArToSave = activeTranslatedTemplate ? {
      clientName: (window as any).__pos_client_name_ar || clientName,
      client_address: (window as any).__pos_client_address_ar || clientAddress,
      clientAddress: (window as any).__pos_client_address_ar || clientAddress,
      address: (window as any).__pos_client_address_ar || clientAddress,
      label_subtotal: (window as any).__pos_row_labels_ar?.subtotal,
      label_total: (window as any).__pos_row_labels_ar?.total,
      label_tax: (window as any).__pos_row_labels_ar?.tax,
      label_shipping: (window as any).__pos_row_labels_ar?.shipping,
      label_discount: (window as any).__pos_row_labels_ar?.discount,
      ...((window as any).__pos_tax_breakdown_ar || {}),
    } : null;

    const dynamicFieldLabelsArToSave = activeTranslatedTemplate ? {
      tax_id: (window as any).__pos_buyer_tax_label || 'Tax ID',
      email: (window as any).__pos_email_label || 'Email',
      phone: (window as any).__pos_phone_label || 'Phone',
      address: (window as any).__pos_address_label || 'Address',
    } : null;

    const clientNameArVal = (window as any).__pos_client_name_ar || clientName;
    const clientAddressArVal = (window as any).__pos_client_address_ar || clientAddress;

    const savedInv = await invoicesApi.create({
      client_name: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
      clientName: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
      client_email: (includeFields.email ? clientEmail : '').trim(),
      clientEmail: (includeFields.email ? clientEmail : '').trim(),
      client_phone: (includeFields.phone ? cleanPhone : '').trim(),
      clientPhone: (includeFields.phone ? cleanPhone : '').trim(),
      client_address: (includeFields.address ? clientAddress : '').trim(),
      clientAddress: (includeFields.address ? clientAddress : '').trim(),
      client_name_ar: activeTranslatedTemplate ? clientNameArVal : undefined,
      clientNameAr: activeTranslatedTemplate ? clientNameArVal : undefined,
      client_address_ar: activeTranslatedTemplate ? clientAddressArVal : undefined,
      clientAddressAr: activeTranslatedTemplate ? clientAddressArVal : undefined,
      include_fields: { ...includeFields },
      includeFields: { ...includeFields },
      // 👤 Persist staff and creator info separately in database
      staff_name: cashierName,
      cashier_name: cashierName,
      created_by_name: authUser?.name || 'Staff',
      amount: grandTotal,
      amount_paid: amountPaidOnSave,
      balance_due: Math.max(0, grandTotal - amountPaidOnSave),
      currency: activeCurrency,
      seller_country: sellerCountry,
      seller_region: sellerRegion,
      buyer_region: buyerRegion,
      buyer_tax_id: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
      buyerTaxId: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
      clientGSTIN: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : '',
      buyer_country: taxIdMeta.countryCode || sellerCountry,
      elements: finalElements,
      row_labels_ar: activeTranslatedTemplate ? (window as any).__pos_row_labels_ar : null,
      tax_lines_ar: activeTranslatedTemplate ? (window as any).__pos_tax_lines_ar : null,
      discount_lines_ar: activeTranslatedTemplate ? (window as any).__pos_discount_lines_ar : null,
      tax_breakdown_ar: activeTranslatedTemplate ? (window as any).__pos_tax_breakdown_ar : null,
      dynamic_values_ar: dynamicValuesArToSave,
      dynamic_field_labels_ar: dynamicFieldLabelsArToSave,
      // Never previously sent — Bills View's downloaded PDF reads
      // show_amount_in_words / amount_in_words_translated back from the
      // saved invoice record to decide whether to render the "Amount in
      // Words" block. Without these, that block silently vanished on
      // every downloaded PDF even though the POS email (which reads its
      // own live component state, not the saved record) showed it fine.
      show_amount_in_words: showAmountInWords,
      amount_in_words_translated: (showAmountInWords && activeTranslatedTemplate)
        ? (window as any).__pos_amount_words_ar
        : null,
      page_size: template.pageSize || 'THERMAL_80MM',
      line_items: cart.map(item => {
        const lineAmount = item.qty * item.rate;
        const rate = getEffectiveTaxRate(item);
        const taxAmt = rate ? (item.taxInclusive ? lineAmount - lineAmount / (1 + rate / 100) : lineAmount * (rate / 100)) : 0;
        return {
          description: item.name,
          arabic_name: (item as any).arabicName || '',
          qty: item.qty,
          unit_price: item.rate,
          amount: lineAmount,
          barcode: item.barcode,
          product_id: item.productId,
          tax_rate: rate,
          tax_inclusive: !!item.taxInclusive,
          tax_amount: parseFloat(taxAmt.toFixed(2)),
        };
      }),
      document_type: 'receipt',
      receipt_type: receiptType,
      payment_mode: paymentMode,
      status: showStatus ? invoiceStatus : 'paid',
      is_bilingual: !!activeTranslatedTemplate,
      outlet_id: outletId || null,
      register_id: registerId,
      // Cash is confirmed the moment it's tendered — get a real sequential
      // number right away. Card/UPI aren't confirmed yet, so send a
      // placeholder that never touches the counter; the real number is
      // only assigned once confirmAndPrint() actually confirms payment.
      ...(invoiceNumberOverride.trim()
        ? { invoice_number: invoiceNumberOverride.trim() }
        : paymentMode !== 'cash'
        ? { invoice_number: `PENDING-${Date.now()}` }
        : {}),
    });
// Refresh stock badges after save
if (outletId != null) {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  fetch(`${BACKEND}/products/?outlet_id=${outletId}`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
    .then(r => r.json())
    .then(data => setProducts(Array.isArray(data) ? data : []))
    .catch(() => {});
}
    const finalInvoiceNumber = invoiceNumberOverride.trim() || savedInv.invoice_number;
    setLastSavedInvoice({ id: String(savedInv.id), number: finalInvoiceNumber, total: grandTotal, publicToken: savedInv.public_token });

    // Card/UPI aren't confirmed yet — show the waiting-for-payment QR
    // instead of firing print/WhatsApp/email off an unconfirmed sale.
    if (paymentMode !== 'cash') {
      try {
        const gwRes = await fetch(`${BACKEND}/payments/bill/${savedInv.id}`);
        const gwData = gwRes.ok ? await gwRes.json() : null;
        const payLink = gwData?.payment_link || `${APP_URL}/pay/bill/${savedInv.id}`;
        const QRCode = await import('qrcode');
        const qrDataUrl = await QRCode.default.toDataURL(payLink, { width: 260, margin: 2 });
        setPaymentWaitModal({ invoiceId: String(savedInv.id), invoiceNumber: finalInvoiceNumber, link: payLink, qr: qrDataUrl });
      } catch (e) {
        console.warn('Failed to fetch payment link:', e);
      }
    }
// Auto-save new customer to Accounts directory (skip walk-ins and existing matches),
    // or bump visit/spend stats on the existing matched account.
    const hasRealName = Boolean(clientName.trim()) && clientName.trim().toLowerCase() !== 'walk-in customer';
    const hasEmail = clientEmail.includes('@');
    if (hasRealName || hasEmail) {
      const myNorm = normalizePhoneForMatching(cleanPhone, outletPhoneCountry || 'IN');
      const existingMatch = accounts.find(a => {
        const aNorm = normalizePhoneForMatching(a.phone || '', outletPhoneCountry || 'IN');
        return (myNorm && aNorm && myNorm === aNorm) ||
               (a.name.trim().toLowerCase() === clientName.trim().toLowerCase() && a.email.trim().toLowerCase() === clientEmail.trim().toLowerCase());
      });
      if (!existingMatch && onAddAccount && cleanPhone) {
        onAddAccount({
          name: hasRealName ? clientName.trim() : clientEmail.trim(),
          email: clientEmail.trim(),
          phone: cleanPhone,
          company: '',
          address: clientAddress.trim(),
          visitCount: 1,
          totalSpend: grandTotal,
        });
      } else if (existingMatch && onUpdateAccount) {
        onUpdateAccount({
          ...existingMatch,
          visitCount: (existingMatch.visitCount || 0) + 1,
          totalSpend: (existingMatch.totalSpend || 0) + grandTotal,
        });
      }
    }
    // Generate token FIRST before building syncedElements — but only for
    // cash. Card/UPI isn't confirmed yet, so generating a token now would
    // burn a token number on a sale that might fail or get discarded, and
    // races the token-QR modal against the payment-wait modal. For
    // card/UPI, token generation happens later, in confirmAndPrint, once
    // payment is actually confirmed.
    let generatedToken = '';
    if (enableTracking && paymentMode === 'cash') {
      try {
        const tokenRes = await fetch(`${BACKEND}/tokens/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
          },
          body: JSON.stringify({
            customer_name: clientName || 'Walk-in',
            order_note: cart.map(i => `${i.qty}x ${i.name}`).join(', '),
            outlet_id: outletId || null,
          }),
        });
        const tokenData = await tokenRes.json();
        generatedToken = tokenData.token;
        autoModalSendRef.current = {
  phone: '',
  email: clientEmail.includes('@') ? clientEmail : '',
}; // fresh bill — allow the modal auto-send effect to fire again
        const QRCode = await import('qrcode');
        const waitUrl = `${APP_URL}/wait?token=${generatedToken}`;
        const qrDataUrl = await QRCode.default.toDataURL(waitUrl, { width: 250, margin: 2 });
        setLastTokenQR({ token: generatedToken, qr: qrDataUrl });


      } catch (e) {
        console.warn('Token generation failed:', e);
      }
    }

    const qrEls = finalElements.filter((el: any) => el.type === 'qr');
    const invoiceQRValue = `${APP_URL}/invoice/view/${savedInv.public_token}`;
    const waitQRValue = generatedToken ? `${APP_URL}/wait?token=${generatedToken}` : null;
    // The appended "Scan to view invoice" / Token QR blocks below were hardcoded
    // to thermal receipt width (280/91/120) — fine for the 302px thermal roll,
    // but on A4/Letter (794/816px) that leaves them stuck near the left edge
    // instead of centered, and can push them past the page height onto page 2.
    const pageWidth = template.pageSize === 'A4' ? 794
      : template.pageSize === 'LETTER' ? 816
      : template.pageSize === 'LABEL_4X6' ? 450
      : template.pageSize === 'LABEL_3X2' ? 288
      : 302;
console.log('Invoice QR will encode:', invoiceQRValue);
    // Leave business-authored QR elements untouched — invoice/token QRs are appended separately below
    const syncedElements = tightenTable(finalElements).map((el: any) => {
      if (el.type === 'qr') {
        return el;
      }
      if (el.type === 'text' && el.textContent) {
        let t = el.textContent as string;
        // Swap {invoice_number}, PENDING, and template codes into the real assigned number
        t = t
          .replace(/\{invoice_number\}/gi, finalInvoiceNumber)
          .replace(/\bPENDING\b/gi, finalInvoiceNumber)
          .replace(/\b(CLO|INV|REC|POS|BILL|EST)-[A-Za-z0-9\-]+\b/gi, finalInvoiceNumber);

        let arFixed = el.arabicContent ? (el.arabicContent as string) : undefined;
        if (arFixed) {
          arFixed = arFixed
            .replace(/\{invoice_number\}/gi, finalInvoiceNumber)
            .replace(/\bPENDING\b/gi, finalInvoiceNumber)
            .replace(/\b(CLO|INV|REC|POS|BILL|EST)-[A-Za-z0-9\-]+\b/gi, finalInvoiceNumber);
        }

        return { ...el, textContent: t, ...(arFixed ? { arabicContent: arFixed } : {}) };
      }
      return el;
    });
// Always append invoice QR (business QRs are untouched, separate elements)
if (!enableTracking) {
  const QRCode = await import('qrcode');
  const isA4OrLetterSave = template.pageSize === 'A4' || template.pageSize === 'LETTER';
  const templateQREl = syncedElements.find((el: any) => el.type === 'qr' && !el.id?.startsWith('invoice-qr-') && !el.id?.startsWith('token-qr-'));
  const autoQRSize = isA4OrLetterSave ? 100 : (templateQREl?.width || 80);
  const autoQRX = Math.round((pageWidth - autoQRSize) / 2);
  const invQrDataUrl = await QRCode.default.toDataURL(invoiceQRValue, { width: autoQRSize * 2, margin: 1 });
  const invQrY = syncedElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 20;
 syncedElements.push(
  {
    id: `invoice-label-${Date.now()}`,
    type: 'text', x: 10, y: invQrY, width: pageWidth - 20, height: 18,
    zIndex: 99, opacity: 100, rotation: 0,
    textContent: `Scan to view invoice`,
    fontSize: 8, fontFamily: 'Inter', fontWeight: '600',
    color: '#374151', textAlign: 'center', lineHeight: 1.3,
    backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0,
  },
  {
    id: `invoice-qr-${Date.now()}`,
    type: 'image', x: autoQRX, y: invQrY + 22, width: autoQRSize, height: autoQRSize,
    zIndex: 99, opacity: 100, rotation: 0,
    textContent: invQrDataUrl,
    backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0,
  }
);
}
 // Always append invoice QR + token QR when tracking is enabled
if (enableTracking && waitQRValue) {
  const QRCode = await import('qrcode');
  const isA4OrLetterSave = template.pageSize === 'A4' || template.pageSize === 'LETTER';
  const templateQREl = syncedElements.find((el: any) => el.type === 'qr' && !el.id?.startsWith('invoice-qr-') && !el.id?.startsWith('token-qr-'));
  const autoQRSize = isA4OrLetterSave ? 100 : (templateQREl?.width || 80);
  const autoQRX = Math.round((pageWidth - autoQRSize) / 2);

  const invQrDataUrl = await QRCode.default.toDataURL(invoiceQRValue, { width: autoQRSize * 2, margin: 1 });
  const qrDataUrl = await QRCode.default.toDataURL(waitQRValue, { width: autoQRSize * 2, margin: 1 });

  if (isA4OrLetterSave) {
    // Align the QR row to the ACTUAL left/right edges of the NOTES box
    // (its x and x+width) instead of the raw page margin — the notes
    // box is inset from the page edge, and the QR row looked misaligned
    // against it when using the page margin directly.
    const tableForAlign = syncedElements.find((el: any) => el.type === 'table');
    const fallbackMargin = 10;
    const boxLeft = tableForAlign ? tableForAlign.x : fallbackMargin;
    const boxRight = tableForAlign ? tableForAlign.x + tableForAlign.width : pageWidth - fallbackMargin;
    const boxMid = (boxLeft + boxRight) / 2;

    // Invoice QR stays flush with the notes box's left edge. Track QR goes
    // flush to the actual page right margin (not the notes box's right
    // edge) so it sits at the true right corner instead of just nudged.
    const invoiceQrX = boxLeft;
    const tokenQrX = boxRight - autoQRSize;
    const rowY = syncedElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 20;

    syncedElements.push(
      { id: `invoice-label-${Date.now()}`, type: 'text', x: boxLeft, y: rowY, width: boxMid - boxLeft, height: 18, zIndex: 99, opacity: 100, rotation: 0, textContent: `Scan to view invoice`, arabicContent: (window as any).__pos_qr_invoice_label || 'Scan to view invoice', fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#374151', textAlign: 'left', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
      { id: `invoice-qr-${Date.now()}`, type: 'image', x: invoiceQrX, y: rowY + 22, width: autoQRSize, height: autoQRSize, zIndex: 99, opacity: 100, rotation: 0, textContent: invQrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
      { id: `token-label-${Date.now()}`, type: 'text', x: boxMid, y: rowY, width: boxRight - boxMid, height: 18, zIndex: 100, opacity: 100, rotation: 0, textContent: `Token: ${generatedToken} — Scan to track order`, arabicContent: `${generatedToken} — ${(window as any).__pos_qr_token_label || 'Scan to track order'}`, fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#6366f1', textAlign: 'right', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
      { id: `token-qr-${Date.now()}`, type: 'image', x: tokenQrX, y: rowY + 22, width: autoQRSize, height: autoQRSize, zIndex: 100, opacity: 100, rotation: 0, textContent: qrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
    );
  } else {
    // Thermal / label sizes stay stacked — too narrow for a side-by-side row.
    const invQrY = syncedElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 20;
    syncedElements.push(
      { id: `invoice-label-${Date.now()}`, type: 'text', x: 10, y: invQrY, width: pageWidth - 20, height: 18, zIndex: 99, opacity: 100, rotation: 0, textContent: `Scan to view invoice`, arabicContent: (window as any).__pos_qr_invoice_label || 'Scan to view invoice', fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#374151', textAlign: 'center', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
      { id: `invoice-qr-${Date.now()}`, type: 'image', x: autoQRX, y: invQrY + 22, width: autoQRSize, height: autoQRSize, zIndex: 99, opacity: 100, rotation: 0, textContent: invQrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
    );
    const tokenY = syncedElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 16;
    syncedElements.push(
      { id: `token-label-${Date.now()}`, type: 'text', x: 10, y: tokenY, width: pageWidth - 20, height: 18, zIndex: 100, opacity: 100, rotation: 0, textContent: `Token: ${generatedToken} — Scan to track order`, arabicContent: `${generatedToken} — ${(window as any).__pos_qr_token_label || 'Scan to track order'}`, fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#6366f1', textAlign: 'center', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
      { id: `token-qr-${Date.now()}`, type: 'image', x: autoQRX, y: tokenY + 22, width: autoQRSize, height: autoQRSize, zIndex: 100, opacity: 100, rotation: 0, textContent: qrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 }
    );
  }
}
// Strip token QR if tracking disabled
if (!enableTracking) {
  for (let i = syncedElements.length - 1; i >= 0; i--) {
    const el = syncedElements[i];
    if (el.id?.startsWith('token-qr-') || el.id?.startsWith('token-label-')) {
      syncedElements.splice(i, 1);
    }
  }
}
// Non-bilingual save: the appended QR labels carry an English-fallback arabicContent,
// which makes the public viewer think the invoice is bilingual. Strip it.
if (!activeTranslatedTemplate) {
  for (let i = 0; i < syncedElements.length; i++) {
    if (syncedElements[i].arabicContent !== undefined) {
      const { arabicContent, ...rest } = syncedElements[i];
      syncedElements[i] = rest;
    }
  }
}
// Bake includeFields directly into elements[0] so the public scan-to-invoice page honors unticked fields
if (syncedElements.length > 0) {
  syncedElements[0] = {
    ...syncedElements[0],
    includeFields: { ...includeFields },
    include_fields: { ...includeFields },
  };
}

// Normalize all business QR elements to match appended QR size and centering
const normalizedElements = syncedElements.map((el: any) => {
  if (el.type === 'qr') {
    // A4/Letter: keep the position the template author set. Only thermal/label gets centered.
    if (template.pageSize === 'A4' || template.pageSize === 'LETTER') return el;
    const qrSize = el.width || 120;
    return { ...el, x: Math.round((pageWidth - qrSize) / 2), width: qrSize, height: qrSize };
  }
  return el;
});
setLastSyncedElements([...normalizedElements]);
setLastSyncedTemplate(template);
await new Promise(resolve => setTimeout(resolve, 1000));

// Stamp translatedLang into first text element so PDF renderer detects bilingual mode
if (activeTranslatedTemplate) {
  const firstTextIdx = normalizedElements.findIndex(
    (el: any) => el.type === 'text' && el.arabicContent
  );
  if (firstTextIdx >= 0 && !normalizedElements[firstTextIdx].translatedLang) {
    normalizedElements[firstTextIdx] = {
      ...normalizedElements[firstTextIdx],
      translatedLang: posTranslateLang,
    };
  }
}
try {
  await fetch(`${BACKEND}/invoices/${savedInv.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
    },
    body: JSON.stringify({
      client_name: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
      client_email: (includeFields.email ? clientEmail : '').trim(),
      client_phone: (includeFields.phone ? cleanPhone : '').trim(),
      client_address: (includeFields.address ? clientAddress : '').trim(),
      client_name_ar: activeTranslatedTemplate ? ((window as any).__pos_client_name_ar || clientName) : undefined,
      client_address_ar: activeTranslatedTemplate ? ((window as any).__pos_client_address_ar || clientAddress) : undefined,
      buyer_tax_id: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
      include_fields: { ...includeFields },
      elements: syncedElements,
      cached_html: null,
      is_bilingual: !!activeTranslatedTemplate,
      translated_lang: activeTranslatedTemplate ? posTranslateLang : null,
      row_labels_ar: activeTranslatedTemplate ? (window as any).__pos_row_labels_ar : null,
      tax_lines_ar: activeTranslatedTemplate ? (window as any).__pos_tax_lines_ar : null,
      discount_lines_ar: activeTranslatedTemplate ? (window as any).__pos_discount_lines_ar : null,
      tax_breakdown_ar: activeTranslatedTemplate ? (window as any).__pos_tax_breakdown_ar : null,
      dynamic_values_ar: dynamicValuesArToSave,
      dynamic_field_labels_ar: dynamicFieldLabelsArToSave,
      show_amount_in_words: showAmountInWords,
      amount_in_words_translated: (showAmountInWords && activeTranslatedTemplate)
        ? (window as any).__pos_amount_words_ar
        : null,
    }),
  });
} catch (e) {
  console.warn('Backend element patch failed:', e);
}


      await new Promise(resolve => setTimeout(resolve, 2000));
    const newBill: InvoiceRecord = {
      id: String(savedInv.id),
      invoiceNumber: finalInvoiceNumber,
      includeFields: { ...includeFields },
      include_fields: { ...includeFields },
      clientName: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
      client_name: (includeFields.name ? (clientName || 'Walk-in Customer') : '').trim(),
      clientNameAr: activeTranslatedTemplate ? ((window as any).__pos_client_name_ar || clientName) : undefined,
      client_name_ar: activeTranslatedTemplate ? ((window as any).__pos_client_name_ar || clientName) : undefined,
      clientPhone: (includeFields.phone ? cleanPhone : '').trim(),
      client_phone: (includeFields.phone ? cleanPhone : '').trim(),
      clientEmail: (includeFields.email ? clientEmail : '').trim(),
      client_email: (includeFields.email ? clientEmail : '').trim(),
      clientAddress: (includeFields.address ? clientAddress : '').trim(),
      client_address: (includeFields.address ? clientAddress : '').trim(),
      clientAddressAr: activeTranslatedTemplate ? ((window as any).__pos_client_address_ar || clientAddress) : undefined,
      client_address_ar: activeTranslatedTemplate ? ((window as any).__pos_client_address_ar || clientAddress) : undefined,
      // 👤 Retain staff name in frontend bill record
      staff_name: cashierName,
      cashierName: cashierName,
      created_by_name: authUser?.name || 'Staff',
      buyer_tax_id: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
      buyerTaxId: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : null,
      clientGSTIN: (includeFields.taxId && clientGSTIN.trim()) ? clientGSTIN.trim() : '',
      seller_country: sellerCountry,
      seller_region: sellerRegion,
      buyer_region: buyerRegion,
      amount: grandTotal,
      amountPaid: amountPaidOnSave,
      status: showStatus ? invoiceStatus : 'paid',
      createdAt: savedInv.created_at || new Date().toISOString(),
      elements: syncedElements,
      pageSize: template.pageSize || 'THERMAL_80MM',
      payment_mode: paymentMode,
    } as InvoiceRecord;
    lastBillRef.current = newBill;
    generatedTokenRef.current = generatedToken;

    // ── Auto actions ─────────────────────────────────────────────
    // Store invoice for print-after-payment
    // lastSavedInvoice already set earlier (right after invoicesApi.create) — no need to set again here.

    // Card/UPI aren't confirmed yet — sending a receipt for a sale that
    // might still fail or get discarded is exactly the bug we just fixed
    // for the invoice number itself. Only cash sends immediately; card/UPI
    // deliverables fire from confirmAndPrint once payment is confirmed.
    if (paymentMode === 'cash') {
      if (postSaveActions.whatsapp && phoneDigits) {
        try {
          await onSendInvoiceWhatsApp?.(newBill, cleanPhone);
          postLog('POS_WHATSAPP_SENT', `POS receipt ${finalInvoiceNumber} sent via WhatsApp to ${clientName || 'Walk-in'} | ${cleanPhone}`);
          showToast(`✅ WhatsApp sent to ${cleanPhone}`, '#16a34a');
          if (enableTracking && generatedToken) {
            const waitUrl = `${APP_URL}/wait?token=${generatedToken}`;
            const activeBrand = companyName || authUser?.name || 'Our Company';
            const trackMsg = `🎟 Your order token: *${generatedToken}*\n\nTrack your order here:\n${waitUrl}\n\nWe'll notify you when it's ready! 🔔\n\n— ${activeBrand}`;
            const tkn = getStoredToken();
            await fetch(`${BACKEND}/whatsapp/send`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
              body: JSON.stringify({ phone: phoneDigits, message: trackMsg, outlet_id: outletId || null }),
            });
            postLog('POS_TOKEN_WHATSAPP_SENT', `Order token ${generatedToken} tracking link sent via WhatsApp to ${clientName || 'Walk-in'} | ${cleanPhone}`);
          }
        } catch (e: any) {
        console.warn('Auto WhatsApp failed:', e);
        showToast(`⚠️ WhatsApp not sent: ${e?.message || 'unknown error'}`);
      }
      }

      if (postSaveActions.email && clientEmail) {
        try {
          const tkn = getStoredToken();
          let pdfBase64: string | undefined;
          let pdfFilename: string | undefined;
          try {
            const pdfBlob = await buildPOSPdfBlob(activeTranslatedTemplate, normalizedElements); // resolved bilingual template + freshly-built elements from earlier in this save — don't rely on lastSyncedElements state, which hasn't flushed yet inside this closure
            if (pdfBlob) {
              pdfBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(pdfBlob);
              });
              pdfFilename = `${finalInvoiceNumber}.pdf`;
            }
          } catch (e) { console.warn('PDF prefetch failed:', e); }
          const activeBrand = companyName || authUser?.name || 'Our Company';
          const trackMsg = (enableTracking && generatedToken)
            ? `Your order token is: ${generatedToken}\n\nTrack your order here: ${APP_URL}/wait?token=${generatedToken}\n\nWe'll notify you when it's ready!\n\n— ${activeBrand}`
            : '';
          await fetch(`${BACKEND}/email/send-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
            body: JSON.stringify({
              invoice_id: Number(savedInv.id),
              to_email: clientEmail,
              attach_pdf: true,
              message: trackMsg,
              ...(trackMsg ? { subject: `Your Order Token: ${generatedToken}` } : {}),
              pdf_base64: pdfBase64,
              pdf_filename: pdfFilename,
            }),
          });
          postLog('POS_EMAIL_SENT', `POS receipt ${finalInvoiceNumber} emailed to ${clientName || 'Walk-in'} | ${clientEmail}${trackMsg ? ' (with tracking link)' : ''}`);
          showToast(`✅ Email sent to ${clientEmail}`, '#16a34a');
        } catch (e: any) {
          console.warn('Auto Email failed:', e);
          showToast(`⚠️ Email not sent: ${e?.message || 'unknown error'}`);
        }
      }
    }

  
    onAddLog('POS_BILL_SAVED', `POS bill ${finalInvoiceNumber} saved for ${clientName || 'Walk-in'} — ${sym}${grandTotal.toFixed(2)}`);
    await postLog('POS_BILL_SAVED', `POS receipt ${finalInvoiceNumber} · ${sym}${grandTotal.toFixed(2)}`);
    setLastSaved(finalInvoiceNumber);
    onPostSave?.(newBill);

    // Cash payments are already "confirmed" — no external payment proof needed.
    // Skip the manual confirm step, auto-print, and clear the cart immediately.
    // (Skip auto-clear if order tracking is on — let the token QR modal flow finish first.)
    if (paymentMode === 'cash' && !enableTracking) {
      setTimeout(() => confirmAndPrint(), 100);
    }
   

    // Toast
    const toast = document.createElement('div');
    toast.innerText = `✅ Receipt ${finalInvoiceNumber} saved — ${sym}${grandTotal.toFixed(2)}`;
    toast.style.cssText = `position:fixed;bottom:28px;left:50%;transform:translateX(-50%);background:#16a34a;color:#fff;padding:12px 24px;border-radius:12px;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 8px 24px rgba(22,163,74,0.4);pointer-events:none;`;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.4s';
      setTimeout(() => document.body.removeChild(toast), 400);
    }, 3000);


         

   
    searchRef.current?.focus();

  } catch (err: any) {
    alert(`Failed to save: ${err.message}`);
  } finally {
    setSaving(false);
  }
};

  const buildPOSPdfBlob = async (overrideTranslatedTemplate?: any, overrideElements?: any[]): Promise<Blob | null> => {
    try {
      const template = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
      const isThermal = (template?.pageSize || '') === 'THERMAL_80MM';
      const isA4OrLetter = template?.pageSize === 'A4' || template?.pageSize === 'LETTER';

      const receiptWidth = template?.pageSize === 'A4' ? 794
        : template?.pageSize === 'LETTER' ? 816
        : isThermal ? 302
        : 794;

      // A4 / LETTER (100% Client-Side html2canvas-pro + jsPDF Engine):
      if (isA4OrLetter) {
        const effectiveTranslatedA4 =
          overrideTranslatedTemplate !== undefined
            ? overrideTranslatedTemplate
            : translatedTemplate;

        const pageWidth = template.pageSize === 'A4' ? 794 : 816;
        const pageHeight = template.pageSize === 'A4' ? 1123 : 1056;

        const html2canvas = (await import('html2canvas-pro')).default;
        const { default: jsPDF } = await import('jspdf');

        if (effectiveTranslatedA4) {
          const container = document.createElement('div');
          container.style.cssText = `position:fixed;left:0;top:0;width:${pageWidth}px;z-index:-99999;opacity:1;pointer-events:none;background:#ffffff;`;
          document.body.appendChild(container);

          const { createRoot } = await import('react-dom/client');
          const root = createRoot(container);

          const effectiveElementsA4 = overrideElements ?? lastSyncedElements;
          const frozen = getFrozenTotals(effectiveElementsA4);

          await new Promise<void>(resolve => {
            root.render(
              React.createElement(BilingualInvoiceRenderer, {
                template: withPosColumns(effectiveTranslatedA4),
                clientName: includeFields.name ? (clientName || 'Walk-in Customer') : '',
                clientEmail: includeFields.email ? clientEmail : '',
                clientPhone: includeFields.phone ? clientPhone : '',
                clientAddress: includeFields.address ? clientAddress : '',
                buyerTaxId: includeFields.taxId ? (isB2B ? clientGSTIN : undefined) : undefined,
                isB2B,
                taxBreakdown,
                taxBreakdownAr: (window as any).__pos_tax_breakdown_ar,
                invoiceDate,
                invoiceTime,
                notes,
                lineItems: cart.map(item => ({
                  id: item.id,
                  description: item.name,
                  qty: item.qty,
                  rate: item.rate,
                  arabicName: (item as any).arabicName,
                  taxRate: getEffectiveTaxRate(item),
                  taxName: item.taxName,
                  b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
                  b2bTaxName: isB2B ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || item.taxName || customTaxScheme?.name) : undefined,
                  taxInclusive: item.taxInclusive, ...posColValues(item),
                  __customAr: (item as any).customColAr,
                })),
                taxLines,
                discountLines,
                autoDiscountAmount: (autoDiscountRule && autoDiscountRule.visibleOnBill) ? autoDiscountAmount : (frozen?.autoDiscountAmount ?? 0),
                autoDiscountLabel: autoDiscountRule?.label || frozen?.autoDiscountLabel || 'Loyalty',
                discountLinesAr: (window as any).__pos_discount_lines_ar,
                taxLinesAr: (window as any).__pos_tax_lines_ar,
                sym,
                shippingCost,
                invoiceStatus: showStatus ? invoiceStatus : undefined,
                translateTargetLang: posTranslateLang,
                isBilingual: true,
                bilingualLayout: 'side',
                isThermal: false,
                showAmountInWords,
                amountInWordsTranslated: showAmountInWords ? (window as any).__pos_amount_words_ar : '',
                taxScheme: posTaxScheme,
                sellerRegion,
                buyerRegion,
                savedElements: effectiveElementsA4.length > 0 ? effectiveElementsA4 : null,
                showTrackingQR: enableTracking,
                invoiceNumber: lastSavedInvoice?.number || invoiceNumberOverride,
                zoom: 1,
              })
            );
            setTimeout(resolve, 800);
          });

          try {
            const targetElement = (container.firstElementChild as HTMLElement) || container;
            const fullHeight = Math.max(
              targetElement.scrollHeight,
              targetElement.offsetHeight,
              pageHeight
            );

            const canvas = await html2canvas(targetElement, {
              scale: 2,
              useCORS: true,
              logging: false,
              width: pageWidth,
              height: fullHeight,
              windowWidth: pageWidth,
              windowHeight: fullHeight,
              backgroundColor: '#ffffff',
            });

            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4',
            });

            const totalCanvasHeight = canvas.height;
            const pageCanvasHeight = Math.round(pageHeight * 2); // 2246px @ 2x

            if (totalCanvasHeight <= pageCanvasHeight + 60) {
              const imgData = canvas.toDataURL('image/jpeg', 0.95);
              pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
            } else {
              const page1MaxCanvasH = Math.round(((297 - 18) / 297) * pageCanvasHeight);
              const contMaxCanvasH = Math.round(((297 - 20 - 16) / 297) * pageCanvasHeight);

              // 1. Scan bounding boxes of all elements to prevent splitting QR codes, images, or table rows
              const tRect = targetElement.getBoundingClientRect();
              const boxes = Array.from(
                targetElement.querySelectorAll('tr, p, img, canvas, table, .cw-element, h1, h2, h3, h4, div')
              )
                .map(el => {
                  const r = (el as HTMLElement).getBoundingClientRect();
                  return {
                    top: Math.round((r.top - tRect.top) * 2),
                    bottom: Math.round((r.bottom - tRect.top) * 2),
                    height: Math.round(r.height * 2),
                  };
                })
                .filter(b => b.height > 6 && b.height < 1500);

              const slices: { startY: number; height: number }[] = [];
              let tempY = 0;

              while (tempY < totalCanvasHeight) {
                const maxH = slices.length === 0 ? page1MaxCanvasH : contMaxCanvasH;

                if (tempY + maxH >= totalCanvasHeight - 30) {
                  slices.push({ startY: tempY, height: totalCanvasHeight - tempY });
                  break;
                }

                let chosenCut = tempY + maxH;

                // 2. Intelligent Collision Avoidance:
                // If chosenCut slices through ANY image, QR code, or block, pull the cut UP above it
                for (let iter = 0; iter < 12; iter++) {
                  const hit = boxes.find(b => b.top < chosenCut - 4 && b.bottom > chosenCut + 4);
                  if (!hit || hit.top <= tempY + 150) break;
                  chosenCut = hit.top - 6; // Move cut cleanly above the element
                }

                const chosenH = Math.max(150, chosenCut - tempY);
                slices.push({ startY: tempY, height: chosenH });
                tempY += chosenH;
              }

              const totalPages = slices.length;
              slices.forEach((slice, pageIdx) => {
                if (pageIdx > 0) pdf.addPage('a4', 'portrait');
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = slice.height;
                const pCtx = pageCanvas.getContext('2d')!;
                pCtx.fillStyle = '#ffffff';
                pCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
                pCtx.drawImage(canvas, 0, slice.startY, canvas.width, slice.height, 0, 0, canvas.width, slice.height);

                const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
                const sliceHeightMm = (slice.height / pageCanvasHeight) * 297;

                if (pageIdx === 0) {
                  pdf.addImage(imgData, 'JPEG', 0, 0, 210, sliceHeightMm, undefined, 'FAST');
                } else {
                  pdf.setFont('helvetica', 'bold');
                  pdf.setFontSize(8.5);
                  pdf.setTextColor(51, 65, 85);
                  const invNum = lastSavedInvoice?.number || invoiceNumberOverride || 'Invoice';
                  pdf.text(`Invoice #: ${invNum} (Continued)`, 15, 11);
                  pdf.setFont('helvetica', 'normal');
                  pdf.setFontSize(8);
                  pdf.setTextColor(100, 116, 139);
                  pdf.text(`Page ${pageIdx + 1} of ${totalPages}`, 195, 11, { align: 'right' });
                  pdf.setDrawColor(226, 232, 240);
                  pdf.setLineWidth(0.25);
                  pdf.line(15, 14, 195, 14);
                  pdf.addImage(imgData, 'JPEG', 0, 18, 210, sliceHeightMm, undefined, 'FAST');
                }

                if (totalPages > 1) {
                  pdf.setFont('helvetica', 'normal');
                  pdf.setFontSize(8);
                  pdf.setTextColor(148, 163, 184);
                  pdf.text(`Page ${pageIdx + 1} of ${totalPages}`, 105, 292, { align: 'center' });
                }
              });
            }

            return pdf.output('blob');
          } finally {
            root.unmount();
            if (document.body.contains(container)) {
              document.body.removeChild(container);
            }
          }
        }

        // NON-BILINGUAL A4: Capture from CanvasWorkspace
        const previewArea = document.getElementById('pos-receipt-preview-area');
        const canvasPage = (
          previewArea?.querySelector('.bg-white.shadow-xl.relative.border.border-slate-200') ||
          previewArea?.querySelector('[data-bilingual-invoice-page="true"]')
        ) as HTMLElement | null;

        if (!canvasPage) {
          console.error('POS preview page not found');
          return null;
        }

        // Preview is scaled by canvasZoom — force 100% so the capture is a true 794x1123 page
        const prevZoom = canvasZoom;
        setCanvasZoom(1);
        await new Promise<void>(resolve => setTimeout(resolve, 250));

        const zoomedPage = (
          previewArea?.querySelector('.bg-white.shadow-xl.relative.border.border-slate-200') ||
          previewArea?.querySelector('[data-bilingual-invoice-page="true"]')
        ) as HTMLElement | null;

        let boxes: { top: number; bottom: number }[] = [];
        let canvas: HTMLCanvasElement;
        try {
          const target = (zoomedPage || canvasPage) as HTMLElement;
          const fullHeight = Math.max(pageHeight, target.scrollHeight, target.offsetHeight);
          canvas = await html2canvas(target, {
            scale: 2,
            useCORS: true,
            logging: false,
            width: pageWidth,
            height: fullHeight,
            windowWidth: pageWidth,
            windowHeight: fullHeight,
            backgroundColor: '#ffffff',
          });
          // Measure at zoom 1 (before the finally restores it): small elements = things we must not cut through
          const tRect = ((zoomedPage || canvasPage) as HTMLElement).getBoundingClientRect();
          boxes = Array.from(((zoomedPage || canvasPage) as HTMLElement).querySelectorAll('*'))
            .map(n => { const r = (n as HTMLElement).getBoundingClientRect(); return { top: (r.top - tRect.top) * 2, bottom: (r.bottom - tRect.top) * 2, h: r.height }; })
            .filter(b => b.h > 2 && b.h < 300);
        } finally {
          setCanvasZoom(prevZoom);
        }

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });

        // Slice into A4 pages: never cut through an element, leave breathing space on page 2+
        const fullPage = pageHeight * 2; // canvas is scale 2
        const topPad = 80;    // 40px blank at top of continuation pages
        const bottomPad = 40; // 20px blank at bottom of every page
        const slices: { y: number; h: number; pad: number }[] = [];
        let y = 0;
        while (y < canvas.height) {
          const pad = slices.length ? topPad : 0;
          const maxH = fullPage - pad - bottomPad;
          if (y + maxH >= canvas.height - 10) { slices.push({ y, h: canvas.height - y, pad }); break; }
          let cut = y + maxH;
          for (let i = 0; i < 8; i++) {
            const hit = boxes.find(b => b.top < cut - 1 && b.bottom > cut + 1);
            if (!hit || hit.top <= y + 100) break;
            cut = hit.top; // move cut up to the start of the element it would slice
          }
          slices.push({ y, h: cut - y, pad });
          y = cut;
        }
        slices.forEach((s, p) => {
          if (p > 0) pdf.addPage('a4', 'portrait');
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = s.pad + s.h;
          const ctx = pageCanvas.getContext('2d')!;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, s.y, canvas.width, s.h, 0, s.pad, canvas.width, s.h);
          pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, 210, ((s.pad + s.h) / fullPage) * 297, undefined, 'FAST');
        });
        return pdf.output('blob');
      }

      // THERMAL:
      // Keep the existing POSReceiptRenderer flow.
      const effectiveTranslated =
        overrideTranslatedTemplate !== undefined
          ? overrideTranslatedTemplate
          : translatedTemplate;

      // Prefer the caller's freshly-computed elements over the
      // lastSyncedElements state — same staleness issue already fixed on
      // the A4/Letter bilingual branch above: during the FIRST save,
      // handleSave's setLastSyncedElements(...) call schedules a state
      // update for the next render, but THIS closure (and this function,
      // called from within it) still sees the STALE pre-save value of
      // lastSyncedElements (empty array on a first save). Without this
      // override, the auto-email/print PDF fired at save time fell back to
      // the raw un-patched template — literal "{invoice_number}" and no
      // invoice/token QR footer, exactly the bug on the thermal Hotel &
      // Lodge receipt.
      const elements = overrideElements
        ?? (lastSyncedElements.length > 0
          ? lastSyncedElements
          : (effectiveTranslated || template)?.elements || []);

      const container = document.createElement('div');
      container.style.cssText =
        `position:absolute;left:-9999px;top:-9999px;width:${receiptWidth}px`;

      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');
      const root = createRoot(container);

      const frozen = getFrozenTotals(elements);

      // Match Quick Invoice: Merge Loyal2 into discountLines with rate % and translation for the PDF
      const activeLoyaltyAmount = frozen ? frozen.autoDiscountAmount : (autoDiscountRule?.visibleOnBill ? autoDiscountAmount : 0);
      const activeLoyaltyLabel = frozen ? frozen.autoDiscountLabel : (autoDiscountRule?.label || 'Loyalty Discount');
      const loyaltyLabelAr = (window as any).__pos_discount_lines_ar?.find((x: any) => x.label === activeLoyaltyLabel)?.labelAr;
      const pdfBaseSubtotal = frozen ? frozen.subtotal : subtotal;

      const pdfDiscountLines = (activeLoyaltyAmount > 0)
        ? [
            ...discountLines.filter(d => !d._isAuto && !/loyalty/i.test(d.label || '')),
            {
              label: activeLoyaltyLabel,
              rate: parseFloat(((activeLoyaltyAmount / Math.max(pdfBaseSubtotal, 1)) * 100).toFixed(2)),
              amount: activeLoyaltyAmount,
              translatedLabel: loyaltyLabelAr,
              labelAr: loyaltyLabelAr,
              _isAuto: true,
            },
          ]
        : discountLines;

      await new Promise<void>(resolve => {
        root.render(
          React.createElement(POSReceiptRenderer, {
            template: { ...(lastSyncedTemplate || template), elements },

            clientName: clientName || 'Walk-in Customer',
            clientEmail,
            clientPhone,
            clientAddress,
            buyerTaxId: isB2B ? clientGSTIN : undefined,
            isB2B,

            invoiceDate,
            invoiceTime,
            notes,

            lineItems: cart.map(item => ({
              id: item.id,
              description: item.name,
              qty: item.qty,
              rate: item.rate,
              arabicName: (item as any).arabicName,
              taxRate: getEffectiveTaxRate(item),
              taxName: item.taxName,
              b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
              b2bTaxName: isB2B ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || item.taxName || customTaxScheme?.name) : undefined,
              taxInclusive: item.taxInclusive, ...posColValues(item),
              customColAr: (item as any).customColAr,
            })),

            taxLines,
            discountLines: pdfDiscountLines,
            sym,
            shippingCost,

            invoiceStatus: showStatus ? invoiceStatus : undefined,

            translateTargetLang: posTranslateLang,
            isBilingual: !!effectiveTranslated,
            isThermal,
            dynamicValues,
            dynamicValuesAr: effectiveTranslated ? translatedValues : undefined,
            dynamicFieldLabelsAr: effectiveTranslated ? dynamicFieldLabelsAr : undefined,

            showTokenQRPlaceholder: false,
            extraColumns: posColumns,
            showWalkIn: true,

            showAmountInWords,

            amountInWords: showAmountInWords
              ? amountToWords(
                  frozen?.grandTotal ?? grandTotal,
                  activeCurrency
                )
              : undefined,

            amountInWordsTranslated:
              showAmountInWords && effectiveTranslated
                ? (window as any).__pos_amount_words_ar
                : undefined,

            productTaxTotal:
              frozen
                ? frozen.productTaxTotal
                : productTaxTotal,

            // Named CGST/SGST/IGST breakdown — the live thermal preview and
            // hidden print area already pass this, but this build path (used
            // for auto-email/WhatsApp/manual print PDFs) never did, so those
            // exports silently fell back to a blended "Total Tax" line.
            taxBreakdown,
            taxBreakdownAr: effectiveTranslated ? (window as any).__pos_tax_breakdown_ar : undefined,

            autoDiscountAmount: 0,
            autoDiscountLabel: undefined,

            netSubtotal:
              frozen
                ? frozen.subtotal
                : subtotal,

            notesAr:
              effectiveTranslated
                ? (window as any).__pos_notes_ar
                : undefined,

            rowLabelsAr:
              effectiveTranslated
                ? (window as any).__pos_row_labels_ar
                : undefined,

            taxLinesAr:
              effectiveTranslated
                ? (window as any).__pos_tax_lines_ar
                : undefined,

            discountLinesAr:
              effectiveTranslated
                ? (window as any).__pos_discount_lines_ar
                : undefined,
          })
        );

        setTimeout(resolve, 800);
      });

      const html =
        `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }

              body {
                background: #fff;
                font-family: system-ui, sans-serif;
              }
            </style>
          </head>

          <body>
            ${container.innerHTML}
          </body>
        </html>`;

      // 📏 Measure the real height of the receipt before unmounting
      const targetElement = (container.firstElementChild as HTMLElement) || container;
      const measuredContentHeight = Math.ceil(
        Math.max(
          targetElement.scrollHeight,
          targetElement.offsetHeight,
          targetElement.getBoundingClientRect().height
        )
      );

      // Add a clean 16px bottom margin instead of the hardcoded 1200px block
      const exactReceiptHeight = Math.max(150, measuredContentHeight + 16);

      root.unmount();
      document.body.removeChild(container);

      const { pdfApi } = await import('../utils/api');

      return await pdfApi.generate(
        html,
        receiptWidth,
        exactReceiptHeight
      );

    } catch (err) {
      console.error('POS PDF build failed:', err);
      return null;
    }
  };





  const buildPOSPrintJob = (overrideElements?: any[], overrideInvoiceNumber?: string): PrintJob => {
    const elements = overrideElements ?? (lastSyncedElements.length > 0 ? lastSyncedElements : []);
    const tableEl = elements.find((el: any) => el.type === 'table');
    const tableY = tableEl?.y ?? Infinity;
    const skipPrefixes = ['bill to:', 'customer:', 'client:', 'patient:'];

    const textLines = (isHeader: boolean) =>
      elements
        .filter((el: any) =>
          el.type === 'text' &&
          !el.id?.startsWith('invoice-label-') &&
          !el.id?.startsWith('token-label-') &&
          (isHeader ? (el.y || 0) < tableY : (el.y || 0) >= tableY)
        )
        .sort((a: any, b: any) => (a.y || 0) - (b.y || 0))
        .flatMap((el: any) => {
          const enLines = (el.textContent || '').split('\n');
          const arLines = el.arabicContent ? el.arabicContent.split('\n') : [];
          const combined: string[] = [];
          enLines.forEach((line: string, idx: number) => {
            combined.push(line);
            const arLine = arLines[idx];
            if (!arLine) return;
            const sameAsEnglish = arLine.trim().toLowerCase() === line.trim().toLowerCase();
            if (!sameAsEnglish) {
              combined.push(arLine);
            }
          });
          return combined;
        })
        .map((l: string) => l.trim())
        .filter((l: string) => l && !skipPrefixes.some(p => l.toLowerCase().startsWith(p)));

    const taxIdLine = clientGSTIN.trim() ? `Tax ID: ${clientGSTIN.trim()}` : undefined;

    // Build dynamic fields for ESC/POS printer
    const printDynamicFields = dynamicFields
      .filter(f => dynamicValues[f.key]?.trim())
      .map(f => ({
        label: f.label,
        value: dynamicValues[f.key].trim(),
        labelAr: dynamicFieldLabelsAr[f.key],
        valueAr: translatedValues[f.key],
      }));

    // Custom/catalog columns (posColumns) — same inline-vs-overflow split
    // used for the PDF/preview path (POSReceiptRenderer via extraColumns).
    // Without this, printReceiptUSB/BT falls back to buildReceiptBytes'
    // hardcoded 4-column Item/Qty/Rate/Amt layout and any column added
    // via "+ Add Col" (e.g. Tax) silently drops off the physical receipt.
    const _posTplTable: any = (translatedTemplate || allTemplates.find(t => t.id === selectedTemplateId))?.elements.find((e: any) => e.type === 'table');
    // buildPosColumns (already defined above) dedupes against columns the
    // table already has before merging in posColumns — calling
    // placePosColumns directly here (as the first version of this fix did)
    // skips that check, so a column already baked into the template (from
    // a prior save/translate pass) gets appended a SECOND time instead of
    // reused, which is what printed "Tax: 5% Tax: 5%" twice on the receipt.
    const _posAllCols: any[] = buildPosColumns(_posTplTable?.columns || []);
    // Bumped from 4 to 5 inline columns — on a 58mm/80mm thermal roll,
    // 5 narrow columns (Item/Qty/Rate/Amt/Tax) still fit legibly side by
    // side, matching what the on-screen preview already shows in one row
    // instead of dropping the 5th column to a separate overflow line.
    const _posInline = _posAllCols.slice(0, 5);
    const _posOverflow = _posAllCols.slice(5);
    const _posHdr = (c: any) => c.originalHeader || c.header || '';
    const _posCell = (item: CartItem, c: any, idx: number) => {
      if (c.key === 'description') return item.name;
      if (c.key === 'qty') return String(item.qty);
      if (c.key === 'rate') return `${sym}${item.rate.toFixed(2)}`;
      if (c.key === 'total') return `${sym}${(item.qty * item.rate).toFixed(2)}`;
      if (['sno', 'serial', 'index'].includes(c.key)) return String(idx + 1);
      return getPosColValue(item, c);
    };
    const _posPrintCols = _posInline.length
      ? _posInline.map((c: any) => ({
          header: _posHdr(c),
          headerAr: translatedTemplate ? c.arabicHeader : undefined,
          align: c.align,
          width: c.width,
          primary: c.key === 'description',
        }))
      : undefined;
    const _posCells = cart.map((item, i) => _posInline.map((c: any) => _posCell(item, c, i)));
    const _posExtraLines = cart.map((item, i) =>
      _posOverflow
        .map((c: any) => { const v = _posCell(item, c, i); return v ? `${_posHdr(c)}: ${v}` : ''; })
        .filter(Boolean)
        .join('  ')
    );
    // Arabic mirror of _posExtraLines — only emitted when the column
    // actually has a translated header (i.e. bilingual mode ran). A
    // column with no arabicHeader contributes nothing to this line,
    // same as itemExtraLines skips a column with no value.
    const _posExtraLinesAr = translatedTemplate
      ? cart.map((item, i) =>
          _posOverflow
            .map((c: any) => {
              const v = _posCell(item, c, i);
              const ar = (item as any).customColAr?.[c.key];
              return v && c.arabicHeader && ar ? `${c.arabicHeader}: ${ar}` : '';
            })
            .filter(Boolean)
            .join('  ')
        )
      : undefined;

    return {
      invoiceNumber: overrideInvoiceNumber ?? lastSavedInvoice?.number,
      date: `${invoiceDate} ${invoiceTime}`,
      clientName: includeFields.name ? (clientName || 'Walk-in Customer') : '',
      clientPhone: includeFields.phone ? (clientPhone || undefined) : undefined,
      clientEmail: includeFields.email ? (clientEmail || undefined) : undefined,
      buyerTaxId: includeFields.taxId ? (clientGSTIN.trim() || undefined) : undefined,
      buyerTaxLabel: taxIdMeta.taxLabel,
      items: cart.map(item => ({ description: item.name, qty: item.qty, rate: item.rate, arabicName: (item as any).arabicName || undefined })),
      tableColumns: _posPrintCols,
      itemCells: _posCells,
      itemExtraLines: _posExtraLines,
      itemExtraLinesAr: _posExtraLinesAr,
      taxLines: taxLines.filter(t => t.rate > 0).map(t => ({
        ...t,
        labelAr: translatedTemplate
          ? (window as any).__pos_tax_lines_ar?.find((x: any) => x.label === t.label)?.labelAr
          : undefined,
      })),
      discountLines: discountLines.filter(d => d.rate > 0).map(d => ({
        ...d,
        labelAr: translatedTemplate
          ? (window as any).__pos_discount_lines_ar?.find((x: any) => x.label === d.label)?.labelAr
          : undefined,
      })),
      shippingCost,
      sym,
      notes,
      headerLines: textLines(true),
      footerLines: textLines(false),
      invoiceQR: lastSavedInvoice?.publicToken ? `${APP_URL}/invoice/view/${lastSavedInvoice.publicToken}` : undefined,
      trackingQR: lastTokenQR ? `${APP_URL}/wait?token=${lastTokenQR.token}` : undefined,
      invoiceQRLabel: translatedTemplate ? (window as any).__pos_qr_invoice_label : undefined,
      trackingQRLabel: translatedTemplate ? (window as any).__pos_qr_token_label : undefined,
      arabicNumerals: posTranslateLang === 'ar',
      rowLabelsAr: translatedTemplate ? (window as any).__pos_row_labels_ar : undefined,
      amountInWords: showAmountInWords ? amountToWords(grandTotal, activeCurrency) : undefined,
      amountInWordsAr: showAmountInWords && translatedTemplate ? (window as any).__pos_amount_words_ar : undefined,
      clientLineAr: translatedTemplate
        ? `${(window as any).__pos_to_label || 'To:'} ${
            !clientName || clientName === 'Walk-in Customer'
              ? ((window as any).__pos_walkin_label || 'Walk-in Customer')
              : ((window as any).__pos_client_name_ar || clientName)
          }`
        : undefined,
      netSubtotal: (() => { const f = getFrozenTotals(elements); return f ? f.subtotal : subtotal; })(),
      productTaxTotal: (() => { const f = getFrozenTotals(elements); return f ? f.productTaxTotal : productTaxTotal; })(),
      // Named CGST/SGST/IGST breakdown — POSReceiptRenderer already receives
      // this correctly in the hidden print area and buildPOSPdfBlob (thermal
      // branch), but buildPOSPrintJob (used for actual USB thermal printing
      // via printReceiptUSB) never carried it through, so USB receipts
      // silently fell back to the blended "Total Tax" line. nameAr is merged
      // in per-entry (not a separate field) since buildReceiptBytes prints
      // each component's Arabic name right under its English line.
      taxBreakdown: taxBreakdown?.map(t => ({
        ...t,
        nameAr: translatedTemplate ? (window as any).__pos_tax_breakdown_ar?.[t.name] : undefined,
      })),
      autoDiscountAmount: (() => { const f = getFrozenTotals(elements); return f ? f.autoDiscountAmount : (autoDiscountRule && autoDiscountRule.visibleOnBill ? autoDiscountAmount : 0); })(),
      autoDiscountLabel: (() => { const f = getFrozenTotals(elements); return f ? f.autoDiscountLabel : autoDiscountRule?.label; })(),
      notesAr: translatedTemplate ? (window as any).__pos_notes_ar : undefined,
    };
  };

  // Executes the actual print job for the Token QR modal — extracted so the
  // printer-picker popover can call it after the cashier picks WiFi/USB, or
  // explicitly asks for the OS dialog via forceDialog.
  const executeTokenModalPrint = async (forceDialog = false) => {
    // A4/Letter — print the real generated PDF (same handleExportPDF flow-
    // layout renderer used by WhatsApp/Email via buildPOSPdfBlob) instead of
    // the thermal print area, which is fixed to 80mm/302px and was never
    // suited for a full page document.
    const printTemplate = allTemplates.find(t => t.id === selectedTemplateId);
    if (printTemplate?.pageSize === 'A4' || printTemplate?.pageSize === 'LETTER') {
      const blob = await buildPOSPdfBlob();
      if (!blob) return alert('Could not prepare the PDF for printing.');
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);
      let printFired = false;
      const firePrint = () => {
        if (printFired) return;
        printFired = true;
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };
      iframe.onload = () => setTimeout(firePrint, 300);
      setTimeout(firePrint, 1200);
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 30000);
      return;
    }
    if (!forceDialog && useUSBPrinter) {
      await printReceiptUSB(buildPOSPrintJob());
      return;
    }
    if (!forceDialog && selectedPrinter) {
      try {
        const printArea = document.getElementById('pos-receipt-print-area');
        const tkn = getStoredToken();
        await fetch(`${BACKEND}/print/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
          body: JSON.stringify({ printer: selectedPrinter, html: printArea?.innerHTML || '' }),
        });
        return;
      } catch (e) {
        console.warn('Named printer send failed, falling back to browser print:', e);
      }
    }
    const printWindow = window.open('', '_blank', 'width=420,height=800');
    if (!printWindow) return alert('Allow pop-ups to print.');
    const printArea = document.getElementById('pos-receipt-print-area');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Receipt</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#fff;}@media print{@page{size:80mm auto;margin:0;}}</style></head><body>${printArea?.innerHTML || ''}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.focus(); printWindow.print(); }, 500);
  };

  // ── Confirm payment + print (manual click or auto via webhook SSE) ───────
  const confirmInProgressRef = useRef(false);
  const confirmAndPrint = async () => {
    if (!lastSavedInvoice || confirmInProgressRef.current) return;
    confirmInProgressRef.current = true;
    setConfirmingPayment(true);
    let printElementsOverride: any[] | undefined;
    let printNumberOverride: string | undefined;
    try {
      const tkn = getStoredToken();

      // Card/UPI sales got a PENDING- placeholder at save time — nothing
      // was burned if the sale never completed. Payment is confirmed now,
      // so get the real number and swap it into the receipt before printing.
      if (lastSavedInvoice.number.startsWith('PENDING-')) {
        try {
          const confirmRes = await fetch(`${BACKEND}/invoices/${lastSavedInvoice.id}/confirm-payment`, {
            method: 'POST',
            headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
          });
          const confirmed = await confirmRes.json();
          const realNumber = confirmed?.invoice_number;
          if (realNumber) {
            const swapNumber = (s: string) => s.split(lastSavedInvoice.number).join(realNumber);
            const patchedElements = lastSyncedElements.map((el: any) =>
              el.type === 'text' && el.textContent
                ? { ...el, textContent: swapNumber(el.textContent), ...(el.arabicContent ? { arabicContent: swapNumber(el.arabicContent) } : {}) }
                : el
            );
            printElementsOverride = patchedElements;
            printNumberOverride = realNumber;
            setLastSyncedElements(patchedElements);
            setLastSavedInvoice(prev => prev ? { ...prev, number: realNumber } : prev);
            await fetch(`${BACKEND}/invoices/${lastSavedInvoice.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
              // Payment gateway/manual confirm just settled this card/UPI
              // sale — mark it fully paid now, same as a cash sale at save time.
              body: JSON.stringify({ elements: patchedElements, amount_paid: lastSavedInvoice.total, balance_due: 0, status: 'paid' }),
            });
            // Combined into one patch — status/invoiceNumber/amountPaid all
            // flip together, whether confirmed via webhook or manually.
            setBills(prev => prev.map(b => b.id === lastSavedInvoice.id ? { ...b, invoiceNumber: realNumber, amountPaid: lastSavedInvoice.total, status: 'Paid' as any } : b));
            // Give the hidden #pos-receipt-print-area time to re-render with
            // the real number before the browser-print path grabs its innerHTML.
            await new Promise(resolve => setTimeout(resolve, 350));
          }
        } catch (e) {
          console.warn('confirm-payment (number assignment) failed:', e);
        }
      }

      if (outletDisplayToken) {
  await fetch(`${BACKEND}/display/confirm-paid/${outletDisplayToken}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: 'Bearer ' + tkn } : {}) },
    body: JSON.stringify({ bill_id: lastSavedInvoice.id, invoice_number: printNumberOverride || lastSavedInvoice.number, register_id: registerId }),
  }).catch(() => {});
}
      // Confirmed card/UPI — deliverables were held back at save time
      // (see handleSave), so fire them now that payment is real.
      if (paymentMode !== 'cash' && lastBillRef.current) {
        const confirmedBill = { ...lastBillRef.current, invoiceNumber: printNumberOverride || lastBillRef.current.invoiceNumber };

        // Token wasn't generated at save time for card/UPI (see handleSave) —
        // generate it now that payment is actually confirmed, so no token
        // number gets burned on a sale that might have failed or been discarded.
        let token = generatedTokenRef.current;
        if (enableTracking && !token) {
          try {
            const tokenRes = await fetch(`${BACKEND}/tokens/create`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
              },
              body: JSON.stringify({
                customer_name: clientName || 'Walk-in',
                order_note: cart.map(i => `${i.qty}x ${i.name}`).join(', '),
                outlet_id: outletId || null,
              }),
            });
            const tokenData = await tokenRes.json();
            token = tokenData.token;
            generatedTokenRef.current = token;
            const QRCode = await import('qrcode');
            const waitUrl = `${APP_URL}/wait?token=${token}`;
            const qrDataUrl = await QRCode.default.toDataURL(waitUrl, { width: 250, margin: 2 });
            setLastTokenQR({ token, qr: qrDataUrl });
          } catch (e) {
            console.warn('Token generation (post-confirm) failed:', e);
          }
          // Append the token QR block to the receipt elements now — the
          // printed/emailed receipt renders from lastSyncedElements, not
          // from lastTokenQR state directly, so without this the token
          // never actually shows up on the physical/PDF receipt.
          if (token) {
            try {
              const QRCode = await import('qrcode');
              const waitQRValue = `${APP_URL}/wait?token=${token}`;
              const currentElements = printElementsOverride || lastSyncedElements;
              const templateQREl = currentElements.find((el: any) => el.type === 'qr' && !el.id?.startsWith('invoice-qr-') && !el.id?.startsWith('token-qr-'));
              const autoQRSize = templateQREl?.width || 80;
              const autoQRX = Math.round((280 - autoQRSize) / 2);
              const qrDataUrl = await QRCode.default.toDataURL(waitQRValue, { width: autoQRSize * 2, margin: 2 });
              const tokenY = currentElements.reduce((max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 16;
              const withToken = [
                ...currentElements,
                { id: `token-label-${Date.now()}`, type: 'text', x: 10, y: tokenY, width: 280, height: 18, zIndex: 100, opacity: 100, rotation: 0, textContent: `Token: ${token} — Scan to track order`, fontSize: 8, fontFamily: 'Inter', fontWeight: '600', color: '#6366f1', textAlign: 'center', lineHeight: 1.3, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
                { id: `token-qr-${Date.now()}`, type: 'image', x: autoQRX, y: tokenY + 22, width: autoQRSize, height: autoQRSize, zIndex: 100, opacity: 100, rotation: 0, textContent: qrDataUrl, backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0 },
              ];
              printElementsOverride = withToken;
              setLastSyncedElements(withToken);
              // Give the hidden #pos-receipt-print-area time to re-render
              // with the new token QR before the browser-print path grabs
              // its innerHTML — same pattern used above for the number swap.
              await new Promise(resolve => setTimeout(resolve, 350));
            } catch (e) {
              console.warn('Appending token QR to receipt failed:', e);
            }
          }
        }
        const cleanPhone = getCleanE164Phone(clientPhone, outletPhoneCountry || 'IN');
        const waDigits = cleanPhone.replace(/\D/g, '');
        if (postSaveActions.whatsapp && waDigits) {
          try {
            await onSendInvoiceWhatsApp?.(confirmedBill, cleanPhone);
            postLog('POS_WHATSAPP_SENT', `POS receipt ${confirmedBill.invoiceNumber} sent via WhatsApp to ${clientName || 'Walk-in'} | ${cleanPhone}`);
            showToast(`✅ WhatsApp sent to ${cleanPhone}`, '#16a34a');
            if (enableTracking && token) {
              const waitUrl = `${APP_URL}/wait?token=${token}`;
              const activeBrand = companyName || authUser?.name || 'Our Company';
              const trackMsg = `🎟 Your order token: *${token}*\n\nTrack your order here:\n${waitUrl}\n\nWe'll notify you when it's ready! 🔔\n\n— ${activeBrand}`;
              await fetch(`${BACKEND}/whatsapp/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
                body: JSON.stringify({ phone: waDigits, message: trackMsg, outlet_id: outletId || null }),
              });
              postLog('POS_TOKEN_WHATSAPP_SENT', `Order token ${token} tracking link sent via WhatsApp to ${clientName || 'Walk-in'} | ${cleanPhone}`);
            }
          } catch (e: any) {
            console.warn('Confirmed WhatsApp send failed:', e);
            showToast(`⚠️ WhatsApp not sent: ${e?.message || 'unknown error'}`);
          }
        }
        if (postSaveActions.email && clientEmail) {
          try {
            let pdfBase64: string | undefined;
            let pdfFilename: string | undefined;
            const pdfBlob = await buildPOSPdfBlob();
            if (pdfBlob) {
              pdfBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(pdfBlob);
              });
              pdfFilename = `${confirmedBill.invoiceNumber}.pdf`;
            }
            const activeBrand = companyName || authUser?.name || 'Our Company';
            const trackMsg = (enableTracking && token)
              ? `Your order token is: ${token}\n\nTrack your order here: ${APP_URL}/wait?token=${token}\n\nWe'll notify you when it's ready!\n\n— ${activeBrand}`
              : '';
            await fetch(`${BACKEND}/email/send-invoice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
              body: JSON.stringify({
                invoice_id: Number(lastSavedInvoice.id),
                to_email: clientEmail,
                attach_pdf: true,
                message: trackMsg,
                ...(trackMsg ? { subject: `Your Order Token: ${token}` } : {}),
                pdf_base64: pdfBase64,
                pdf_filename: pdfFilename,
              }),
            });
            postLog('POS_EMAIL_SENT', `POS receipt ${confirmedBill.invoiceNumber} emailed to ${clientName || 'Walk-in'} | ${clientEmail}${trackMsg ? ' (with tracking link)' : ''}`);
            showToast(`✅ Email sent to ${clientEmail}`, '#16a34a');
          } catch (e: any) {
            console.warn('Confirmed Email send failed:', e);
            showToast(`⚠️ Email not sent: ${e?.message || 'unknown error'}`);
          }
        }
      }

      if (postSaveActions.print) {
        console.log('[USB PRINT] print block reached, useUSBPrinter =', useUSBPrinter, 'usbConnected =', usbConnected);
        const template = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
        const isA4OrLetter = template?.pageSize === 'A4' || template?.pageSize === 'LETTER';
        if (useUSBPrinter) {
          console.log('[USB PRINT] calling printReceiptUSB...');
          await printReceiptUSB(buildPOSPrintJob(printElementsOverride, printNumberOverride));
        } else if (isA4OrLetter) {
          // Real PDF generator instead of the thermal print area — see
          // executeTokenModalPrint for the same pattern/reasoning.
          const blob = await buildPOSPdfBlob();
          if (blob) {
            const url = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
            iframe.src = url;
            document.body.appendChild(iframe);
            let printFired = false;
            const firePrint = () => {
              if (printFired) return;
              printFired = true;
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            };
            iframe.onload = () => setTimeout(firePrint, 300);
            setTimeout(firePrint, 1200);
            setTimeout(() => {
              if (document.body.contains(iframe)) document.body.removeChild(iframe);
              URL.revokeObjectURL(url);
            }, 30000);
          }
        } else {
          const isThermal = (template?.pageSize || '') === 'THERMAL_80MM';
          const printArea = document.getElementById('pos-receipt-print-area');
          if (printArea) {
            const printWindow = window.open('', '_blank', 'width=420,height=800');
            if (printWindow) {
              printWindow.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt</title><style>*{margin:0;padding:0;box-sizing:border-box;}html,body{background:#fff;font-family:system-ui,sans-serif;width:'+(isThermal?'302px':'794px')+'}@page{size:'+(isThermal?'80mm':'A4')+' auto;margin:0;}</style></head><body>'+printArea.innerHTML+'</body></html>');
              printWindow.document.close();
              setTimeout(() => { printWindow.focus(); printWindow.print(); setTimeout(() => printWindow.close(), 1000); }, 800);
            }
          }
        }
      }
    
      setLastSavedInvoice(null);
      setPaymentFailed(false);
      clearCart();
    } finally {
      setConfirmingPayment(false);
      confirmInProgressRef.current = false;
    }
  };


  // Fetches a fresh payment link/QR for the SAME pending bill — no new
  // invoice is created, nothing is re-charged. Lets the cashier recover
  // from a glitched/expired QR without switching to cash or discarding.
  const regeneratePaymentQR = async () => {
    if (!paymentWaitModal) return;
    setRegeneratingQR(true);
    try {
      const gwRes = await fetch(`${BACKEND}/payments/bill/${paymentWaitModal.invoiceId}`);
      const gwData = gwRes.ok ? await gwRes.json() : null;
      const payLink = gwData?.payment_link || `${APP_URL}/pay/bill/${paymentWaitModal.invoiceId}`;
      const QRCode = await import('qrcode');
      const qrDataUrl = await QRCode.default.toDataURL(payLink, { width: 260, margin: 2 });
      setPaymentWaitModal(prev => prev ? { ...prev, link: payLink, qr: qrDataUrl } : prev);
      setPaymentFailed(false);
      setPaymentPolling(true);
      if (outletDisplayToken) {
        const jwt = localStorage.getItem('cloudairambo_jwt') || '';
        fetch(`${BACKEND}/display/push/${outletDisplayToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ bill_id: paymentWaitModal.invoiceId, register_id: registerId }),
        }).catch(() => {});
      }
      postLog('POS_QR_REGENERATED', `Payment QR regenerated for bill ${paymentWaitModal.invoiceNumber}`);
    } catch (e) {
      console.warn('QR regeneration failed:', e);
      alert('Could not regenerate the QR — please try again.');
    } finally {
      setRegeneratingQR(false);
    }
  };

  // Bails a stuck UPI/card payment straight to cash — flips payment_mode,
  // then lets confirmAndPrint do the same number-assign + print flow every
  // other cash sale uses.
  const switchToCashAndConfirm = async () => {
    if (!paymentWaitModal) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    setPaymentPolling(false);
    try {
      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ payment_mode: 'cash' }),
      });
    } catch (e) {
      console.warn('switchToCashAndConfirm failed:', e);
      alert('Could not switch to cash — please try again.');
      setPaymentPolling(true);
      return;
    }
    setPaymentMode('cash');
    setPaymentWaitModal(null);
    postLog('POS_PAID_CASH_FALLBACK', `Bill ${paymentWaitModal.invoiceNumber} switched to cash after ${paymentMode} was stuck`);
    await confirmAndPrintRef.current();
  };

  // Cancels the bill on the backend (same /cancel the failed-payment retry
  // uses) so an abandoned card/UPI sale never sits around as a dead
  // PENDING-xxxx row — nothing was ever charged, nothing was ever printed.
  const discardPendingInvoice = async () => {
    if (!paymentWaitModal) return;
    // Same pattern as the Token QR modal's Discard button — require an
    // actual typed reason instead of a hardcoded string, so the audit
    // log/cancellation_reason reflects why the cashier bailed.
    const reason = window.prompt('Reason for discarding this bill? (required)');
    if (reason === null) return; // cancelled the prompt
    if (!reason.trim()) { alert('A reason is required to discard a bill.'); return; }
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    try {
      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      setBills(prev => prev.map(b => b.id === paymentWaitModal.invoiceId ? { ...b, status: 'cancelled' as any } : b));
      postLog('POS_BILL_CANCELLED', `Bill ${paymentWaitModal.invoiceNumber} discarded before payment confirmed. Reason: ${reason.trim()}`);
    } catch (e) {
      console.warn('Discard failed:', e);
    }
    setPaymentWaitModal(null);
    setPaymentFailed(false);
    setLastSavedInvoice(null);
    clearCart();
  };

  const handleSetPosDefaultTemplate = async () => {
    const template = allTemplates.find(t => t.id === selectedTemplateId);
    if (!template) return;
    setSavingDefaultTemplate(true);
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      localStorage.setItem('cloudairambo_pos_template', selectedTemplateId);
      if (outletId) {
        localStorage.setItem(`cloudairambo_pos_default_tpl_${outletId}`, selectedTemplateId);
      }
      
      if (outletId) {
        await fetch(`${BACKEND}/outlets/${outletId}/default-template`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ template_id: selectedTemplateId, pos_template_id: selectedTemplateId }),
        }).catch(() => {});
      } else {
        await fetch(`${BACKEND}/invoice-templates/pos-default`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ template_id: selectedTemplateId }),
        }).catch(() => {});
      }

      setPosDefaultTemplateId(selectedTemplateId as any);
      setPosDefaultTemplateName(template.name);
      showToast(`✓ "${template.name}" set as default template for team`, '#16a34a');
    } catch (e: any) {
      alert(`Failed to set default template: ${e.message}`);
    } finally {
      setSavingDefaultTemplate(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', background: '#f0f4f8', 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>

      {isMobile && (
        <div style={{
          display: 'flex', flexShrink: 0, background: '#fff',
          borderBottom: '1px solid #e5e7eb', padding: '6px 8px', gap: 6,
        }}>
          {([
            { key: 'products', label: 'Products', icon: <Package size={13} /> },
            { key: 'cart', label: `Cart${cartCount > 0 ? ` (${cartCount})` : ''}`, icon: <ShoppingCart size={13} /> },
            { key: 'preview', label: 'Preview', icon: <Receipt size={13} /> },
          ] as const).map(t => (
            <button key={t.key} onClick={() => setMobileTab(t.key)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '8px 6px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700,
              background: mobileTab === t.key ? '#3b82f6' : '#f1f5f9',
              color: mobileTab === t.key ? '#fff' : '#6b7280',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      )}

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'row',
        overflow: 'hidden', minHeight: 0,
      }}>

      {/* ── LEFT: Product Browser ──────────────────────────────────────────── */}
      <div className="pos-scroll" style={{
        flex: 1, display: isMobile && mobileTab !== 'products' ? 'none' : 'flex',
        flexDirection: 'column',
        overflowY: 'auto', padding: 16, gap: 12, minWidth: 0,
      }}>

        {/* Search bar */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              ref={searchRef}
              style={{ ...inp, paddingLeft: 32, fontSize: 13 }}
              placeholder="Search products by name, barcode, brand..."
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
        
            />
            {productSearch && (
              <button onClick={() => setProductSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>×</button>
            )}
          </div>
          <button
            onClick={() => setShowScanner(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'linear-gradient(135deg,#059669,#10b981)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(16,185,129,0.3)' }}
          >
            <Scan size={14} /> Scan Barcode
          </button>
        </div>

        {/* Category filter */}
        {categories.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {categories.slice(0, 12).map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)} style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                border: '1px solid', cursor: 'pointer', transition: 'all 0.15s',
                background: selectedCategory === cat ? '#3b82f6' : '#fff',
                color: selectedCategory === cat ? '#fff' : '#6b7280',
                borderColor: selectedCategory === cat ? '#3b82f6' : '#e5e7eb',
              }}>{cat}</button>
            ))}
          </div>
        )}

        {/* Products grid */}
        {productsLoading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }} />
              Loading catalog...
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: '#9ca3af' }}>
              <Package size={36} style={{ marginBottom: 8, opacity: 0.4 }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>No products found</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Try a different search or scan a barcode</div>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 10,
          }}>
            {filteredProducts.map(product => {
              const inCart = cart.find(i => i.productId === product.id);
              const lowStock = (product.stock_quantity ?? 99) < 5;
              return (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  style={{
                    background: inCart ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#fff',
                    border: `2px solid ${inCart ? '#3b82f6' : '#e5e7eb'}`,
                    borderRadius: 10,
                    padding: '12px 10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                    position: 'relative',
                    boxShadow: inCart ? '0 0 0 3px rgba(59,130,246,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                  onMouseEnter={e => {
                    if (!inCart) (e.currentTarget as HTMLElement).style.borderColor = '#93c5fd';
                  }}
                  onMouseLeave={e => {
                    if (!inCart) (e.currentTarget as HTMLElement).style.borderColor = '#e5e7eb';
                  }}
                >
                  {/* Category tag */}
                  {product.category && (
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      {product.category}
                    </div>
                  )}
                  {/* Name */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: 6 }}>
                    {product.name}
                  </div>
                  {/* Brand */}
                  {product.brand && (
                    <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>{product.brand}</div>
                  )}
                  {/* Price & unit */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: '#3b82f6' }}>
                      {sym}{product.rate.toFixed(2)}
                    </span>
                    {product.unit && (
                      <span style={{ fontSize: 9, color: '#9ca3af', background: '#f3f4f6', borderRadius: 4, padding: '1px 5px' }}>
                        /{product.unit}
                      </span>
                    )}
                  </div>
                  {/* Stock warning */}
                  {lowStock && (
                    <div style={{ fontSize: 9, color: '#ef4444', fontWeight: 600, marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <AlertCircle size={9} /> Low stock ({product.stock_quantity})
                    </div>
                  )}
                  {/* In cart badge */}
                  {inCart && (
                    <div style={{
                      position: 'absolute', top: 6, right: 6,
                      background: '#3b82f6', color: '#fff',
                      borderRadius: 10, fontSize: 10, fontWeight: 700,
                      padding: '1px 6px', minWidth: 20, textAlign: 'center',
                    }}>
                      {inCart.qty}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── RIGHT: Cart + Billing ──────────────────────────────────────────── */}
      <div style={{
  width: isMobile ? '100%' : cartPanelWidth, minWidth: isMobile ? 0 : 380, maxWidth: isMobile ? '100%' : 750,
  display: isMobile && mobileTab !== 'cart' ? 'none' : 'flex',
  flexDirection: 'column',
  background: '#fff', borderLeft: isMobile ? 'none' : '1px solid #e5e7eb',
  flexShrink: 0, overflow: 'hidden',
  position: 'relative',
}}>
  {/* Drag handle */}
  <div
    onMouseDown={(e) => {
      if (isMobile) return;
      e.preventDefault();
      const startX = e.clientX;
      const startW = cartPanelWidth;
      const onMove = (mv: MouseEvent) => {
        const newW = Math.min(750, Math.max(380, startW - (mv.clientX - startX)));
        setCartPanelWidth(newW);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }}
    style={{
      position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
      cursor: 'col-resize', zIndex: 10, background: 'transparent',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(99,102,241,0.3)')}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
  />

       {/* Cart header */}
<div style={{
  padding: '14px 18px',
  background: 'linear-gradient(135deg,#3b82f6,#9333ea)',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}}>
  {/* TOP ROW: Title on Left, System Tools on Right */}
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
    {/* Left: Cart Title + Count */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <ShoppingCart size={20} color="#fff" />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontWeight: 900, fontSize: 17, color: '#fff', letterSpacing: 0.3 }}>POS Cart</span>
        {cartCount > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 700, color: '#fff',
            background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)',
            padding: '2px 8px', borderRadius: 99,
          }}>
            {cartCount} item{cartCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>

    {/* Right: System Tools (Display, Kitchen, Print, Settings) */}
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
      {/* Counter Display */}
      {perms.canAccessPaymentDisplay && (
        <button
          onClick={() => window.open(outletDisplayToken ? `/display?token=${outletDisplayToken}&register_id=${registerId}` : '#', '_blank')}
          disabled={!outletDisplayToken}
          title={outletDisplayToken ? 'Counter Display' : 'No display token found'}
          style={{ ...posToolbarBtn(false, !outletDisplayToken), width: 36, height: 36, minWidth: 36, flexShrink: 0, padding: 0 }}
        >
          <Monitor size={17} color="#fff" />
        </button>
      )}

      {/* Kitchen Dashboard */}
      {perms.canAccessKitchenDashboard && (
        <button
          onClick={() => outletKitchenToken && window.open(`/kitchen?outlet_token=${outletKitchenToken}`, '_blank')}
          disabled={!outletKitchenToken}
          title={outletKitchenToken ? 'Kitchen Dashboard' : 'Loading outlet info...'}
          style={{ ...posToolbarBtn(false, !outletKitchenToken), width: 36, height: 36, minWidth: 36, flexShrink: 0, padding: 0 }}
        >
          📋
        </button>
      )}

      {/* Divider */}
      <div style={{ ...posToolbarDivider, height: 22, flexShrink: 0 }} />

      {/* Print */}
      <button
        onClick={() => executeTokenModalPrint(false)}
        title="Print Receipt"
        style={{ ...posToolbarBtn(), width: 36, height: 36, minWidth: 36, flexShrink: 0, padding: 0 }}
      >
        <Printer size={17} color="#fff" />
      </button>

      {/* Settings */}
      <button
        onClick={() => setShowSettings(s => !s)}
        title="Settings"
        style={{ ...posToolbarBtn(showSettings), width: 36, height: 36, minWidth: 36, flexShrink: 0, padding: 0 }}
      >
        <Settings size={17} color="#fff" />
      </button>
    </div>
  </div>

  {/* BOTTOM ROW: Translation & Reset Toolbar */}
  <div style={{
    display: 'flex', gap: 6, alignItems: 'center',
    overflowX: 'auto', scrollbarWidth: 'none',
  }}>

    {/* Reset / Clear cart */}
    <button
      onClick={clearCart}
      title="Reset / Clear cart"
      style={{ ...posToolbarBtn(), width: 36, height: 36, minWidth: 36, flexShrink: 0, padding: 0 }}
    >
      <RotateCcw size={16} />
    </button>

    {/* Divider */}
    <div style={{ ...posToolbarDivider, height: 22, flexShrink: 0 }} />

    {/* Bilingual toggle */}
    {perms.canPOSToggleBilingual && (
      <button
        onClick={() => {
          const next = !bilingualEnabled;
          setBilingualEnabled(next);
          localStorage.setItem('cloudairambo_pos_bilingual_enabled', String(next));
          clearTimeout(autoTranslateTimer.current);
          if (next) {
            if (cart.length > 0) handlePOSTranslate();
          } else {
            setTranslatedTemplate(null);
            // Reset saved elements to clean template so non-bilingual box expansion recalculates cleanly
            setLastSyncedElements([]);
            setCart(prev => prev.map(i => ({ ...i, arabicName: undefined })));
          }
        }}
        title={bilingualEnabled ? 'Bilingual receipts: ON' : 'Bilingual receipts: OFF'}
        style={{
          ...posToolbarBtn(bilingualEnabled),
          minWidth: 72,
          flexShrink: 0,
          padding: '0 10px',
          justifyContent: 'center',
        }}
      >
        🌐 {bilingualEnabled ? 'ON' : 'OFF'}
      </button>
    )}

    {/* Language select */}
    {perms.canPOSToggleBilingual && (
      <select
        value={posTranslateLang}
        onChange={e => { setPosTranslateLang(e.target.value); localStorage.setItem('cloudairambo_pos_lang', e.target.value); }}
        style={{
          fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(255,255,255,0.14)', color: '#fff', height: 36,
          padding: '0 8px', borderRadius: 10, cursor: 'pointer', outline: 'none',
          maxWidth: 130, flexShrink: 0,
        }}
      >
        {[
          ['af','AF — Afrikaans'],['sq','SQ — Albanian'],['am','AM — Amharic'],['ar','AR — Arabic'],['hy','HY — Armenian'],
          ['az','AZ — Azerbaijani'],['eu','EU — Basque'],['be','BE — Belarusian'],['bn','BN — Bengali'],['bs','BS — Bosnian'],
          ['bg','BG — Bulgarian'],['ca','CA — Catalan'],['ceb','CEB — Cebuano'],['ny','NY — Chichewa'],['zh','ZH — Chinese (Simplified)'],
          ['zh-TW','ZH-TW — Chinese (Traditional)'],['co','CO — Corsican'],['hr','HR — Croatian'],['cs','CS — Czech'],['da','DA — Danish'],
          ['nl','NL — Dutch'],['en','EN — English'],['eo','EO — Esperanto'],['et','ET — Estonian'],['tl','TL — Filipino'],
          ['fi','FI — Finnish'],['fr','FR — French'],['fy','FY — Frisian'],['gl','GL — Galician'],['ka','KA — Georgian'],
          ['de','DE — German'],['el','EL — Greek'],['gu','GU — Gujarati'],['ht','HT — Haitian Creole'],['ha','HA — Hausa'],
          ['haw','HAW — Hawaiian'],['iw','IW — Hebrew'],['hi','HI — Hindi'],['hmn','HMN — Hmong'],['hu','HU — Hungarian'],
          ['is','IS — Icelandic'],['ig','IG — Igbo'],['id','ID — Indonesian'],['ga','GA — Irish'],['it','IT — Italian'],
          ['ja','JA — Japanese'],['jw','JW — Javanese'],['kn','KN — Kannada'],['kk','KK — Kazakh'],['km','KM — Khmer'],
          ['rw','RW — Kinyarwanda'],['ko','KO — Korean'],['ku','KU — Kurdish (Kurmanji)'],['ky','KY — Kyrgyz'],['lo','LO — Lao'],
          ['la','LA — Latin'],['lv','LV — Latvian'],['lt','LT — Lithuanian'],['lb','LB — Luxembourgish'],['mk','MK — Macedonian'],
          ['mg','MG — Malagasy'],['ms','MS — Malay'],['ml','ML — Malayalam'],['mt','MT — Maltese'],['mi','MI — Maori'],
          ['mr','MR — Marathi'],['mn','MN — Mongolian'],['my','MY — Myanmar (Burmese)'],['ne','NE — Nepali'],['no','NO — Norwegian'],
          ['or','OR — Odia (Oriya)'],['ps','PS — Pashto'],['fa','FA — Persian'],['pl','PL — Polish'],['pt','PT — Portuguese'],
          ['pa','PA — Punjabi'],['ro','RO — Romanian'],['ru','RU — Russian'],['sm','SM — Samoan'],['gd','GD — Scots Gaelic'],
          ['sr','SR — Serbian'],['st','ST — Sesotho'],['sn','SN — Shona'],['sd','SD — Sindhi'],['si','SI — Sinhala'],
          ['sk','SK — Slovak'],['sl','SL — Slovenian'],['so','SO — Somali'],['es','ES — Spanish'],['su','SU — Sundanese'],
          ['sw','SW — Swahili'],['sv','SV — Swedish'],['tg','TG — Tajik'],['ta','TA — Tamil'],['tt','TT — Tatar'],
          ['te','TE — Telugu'],['th','TH — Thai'],['tr','TR — Turkish'],['tk','TK — Turkmen'],['ug','UG — Uyghur'],
          ['uk','UK — Ukrainian'],['ur','UR — Urdu'],['uz','UZ — Uzbek'],['vi','VI — Vietnamese'],['cy','CY — Welsh'],
          ['xh','XH — Xhosa'],['yi','YI — Yiddish'],['yo','YO — Yoruba'],['zu','ZU — Zulu'],['ak','AK — Akan'],
          ['bho','BHO — Bhojpuri'],['doi','DOI — Dogri'],['dv','DV — Dhivehi'],['ee','EE — Ewe'],['gn','GN — Guarani'],
          ['ilo','ILO — Ilocano'],['kri','KRI — Krio'],['ckb','CKB — Kurdish (Sorani)'],['lg','LG — Luganda'],['mai','MAI — Maithili'],
          ['lus','LUS — Mizo'],['om','OM — Oromo'],['qu','QU — Quechua'],['sa','SA — Sanskrit'],['nso','NSO — Sepedi'],
          ['ti','TI — Tigrinya'],['ts','TS — Tsonga'],
        ].map(([code, name]) => (
          <option key={code} value={code} style={{ color: '#111' }}>{name}</option>
        ))}
      </select>
    )}

    {/* Striker (⚡ Lightning strike button) - ALWAYS VISIBLE */}
    {perms.canPOSToggleBilingual && (
      <button
        onClick={() => {
          if (!bilingualEnabled) {
            setBilingualEnabled(true);
            localStorage.setItem('cloudairambo_pos_bilingual_enabled', 'true');
          }
          clearTimeout(autoTranslateTimer.current);
          handlePOSTranslate();
        }}
        disabled={isTranslatingPOS || cart.length === 0}
        title="Translate receipt (Lightning Strike)"
        style={{
          ...posToolbarBtn(bilingualEnabled),
          width: 36,
          height: 36,
          minWidth: 36,
          flexShrink: 0,
          padding: 0,
          opacity: isTranslatingPOS ? 0.5 : 1,
          color: '#facc15',
          fontSize: 16,
        }}
      >
        {isTranslatingPOS ? '...' : '⚡'}
      </button>
    )}

    {/* Revert translation button */}
    {translatedTemplate && (
      <button
        onClick={() => { 
          setTranslatedTemplate(null); 
          setLastSyncedElements([]);
          setCart(prev => prev.map(i => ({ ...i, arabicName: undefined }))); 
        }}
        title="Revert translation"
        style={{
          ...posToolbarBtn(),
          background: 'rgba(248,113,113,0.28)',
          borderColor: 'rgba(248,113,113,0.4)',
          width: 36,
          height: 36,
          minWidth: 36,
          flexShrink: 0,
          padding: 0,
        }}
      >
        ↩
      </button>
    )}

  </div>
</div>
    

        {/* Scrollable body */}
        <div className="pos-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {editingPendingInvoice && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>
                ✏️ Editing pending bill {editingPendingInvoice.invoiceNumber} — click "Update Bill" when done
              </span>
              <button onClick={() => { setPaymentWaitModal(editingPendingInvoice); setEditingPendingInvoice(null); }}
                style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                Cancel, resume QR
              </button>
            </div>
          )}

          {/* ── Settings panel (collapsible) ── */}
          {showSettings && (
            <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 2 }}>Bill Settings</div>
              
              {/* Business Type */}
{perms.canPOSChangeBusinessType && (
<div>
  <label style={lbl}>Business Type</label>
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {(['restaurant', 'retail', 'pharmacy', 'corporate', 'logistics', 'other'] as const).map(bt => (
      <button key={bt} onClick={() => {
        setBusinessType(bt);
        localStorage.setItem('cloudairambo_business_type', bt);
      }} style={{
        flex: '1 1 30%', minWidth: 90, padding: '5px 4px', borderRadius: 7, border: '1.5px solid',
        fontSize: 10, fontWeight: 700, cursor: 'pointer',
        background: businessType === bt ? '#6366f1' : '#fff',
        color: businessType === bt ? '#fff' : '#6b7280',
        borderColor: businessType === bt ? '#6366f1' : '#e5e7eb',
      }}>
        {bt === 'restaurant' ? '🍽 Food' 
      : bt === 'retail' ? '🛍 Retail'
      : bt === 'pharmacy' ? '💊 Pharmacy'
      : bt === 'corporate' ? '🏢 Corporate'
      : bt === 'logistics' ? '🚚 Logistics'
      : '⚙ Other'}
      </button>
    ))}
  </div>
  <div style={{ fontSize: 9, color: '#6366f1', marginTop: 4, fontWeight: 600 }}>
  {businessType === 'restaurant' ? '✓ Token queue + kitchen dashboard active'
  : businessType === 'retail' ? '✓ Order tracking QR enabled for all sales'
  : businessType === 'pharmacy' ? '✓ Prescription tracking QR enabled'
  : businessType === 'corporate' ? '✓ Invoice tracking QR enabled'
  : businessType === 'logistics' ? '✓ Shipment tracking QR enabled'
  : '✓ Bill tracking QR enabled for all sales'}
</div>
{/* Staff / Cashier Name — Locked & Read-only */}
<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
      <label style={{ ...lbl, marginBottom: 0 }}>Staff / Cashier</label>
      <span style={{ fontSize: 9, fontWeight: 700, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '1px 6px', borderRadius: 4 }}>
        🔒 Logged In
      </span>
    </div>
    <div style={{
      ...inp,
      fontSize: 12,
      fontWeight: 700,
      color: '#1e293b',
      background: '#f8fafc',
      border: '1.5px solid #cbd5e1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'not-allowed',
      userSelect: 'none'
    }}>
      <span>👤 {waiterName || authUser?.name?.trim() || 'Staff'}</span>
      <span style={{ fontSize: 10, color: '#64748b' }}>🔒 Locked</span>
    </div>
  </div>

  {/* Dining / Restaurant Specific Sub-fields */}
  {businessType === 'restaurant' && (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
      <div>
        <label style={lbl}>Table No</label>
        <input style={{ ...inp, fontSize: 11 }} placeholder="07" value={tableNumber} onChange={e => setTableNumber(e.target.value)} />
      </div>
      <div>
        <label style={lbl}>Covers</label>
        <input style={{ ...inp, fontSize: 11 }} placeholder="2" value={covers} onChange={e => setCovers(e.target.value)} />
      </div>
      <div>
        <label style={lbl}>KOT</label>
        <input style={{ ...inp, fontSize: 11 }} placeholder="441" value={kotNumber} onChange={e => setKotNumber(e.target.value)} />
      </div>
    </div>
  )}
</div>
</div>
)}
{isHotelTemplate && (
  <>
    <div>
      <label style={lbl}>Check-in Date</label>
      <input type="date" style={{ ...inp, fontSize: 11 }} value={checkInDate} onChange={e => setCheckInDate(e.target.value)} />
    </div>
    <div>
      <label style={lbl}>Room Number</label>
      <input type="text" style={{ ...inp, fontSize: 11 }} value={roomNumber} onChange={e => setRoomNumber(e.target.value)} placeholder="204" />
    </div>
    <div>
      <label style={lbl}>Guest Name</label>
      <input type="text" style={{ ...inp, fontSize: 11 }} value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Guest name" />
    </div>
    <div>
      <label style={lbl}>Check-out Date</label>
      <input type="date" style={{ ...inp, fontSize: 11 }} value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)} />
    </div>
  </>
)}
{/* Template */}
<div>
  <label style={lbl}>Template</label>
  {!canSetPosDefaultTemplate ? (
    <div style={{ ...inp, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f3f4f6', color: '#374151', cursor: 'not-allowed' }}>
      <span>🔒 {allTemplates.find(t => t.id === selectedTemplateId)?.name || posDefaultTemplateName || 'No default set'}</span>
    </div>
  ) : (
    <>
      <select
        value={selectedTemplateId}
        onChange={e => {
      setSelectedTemplateId(e.target.value);
      localStorage.setItem('cloudairambo_pos_template', e.target.value);
    }}
        style={{ ...inp, fontSize: 11 }}
      >
        <optgroup label="── Thermal / POS Receipts ──">
          {allTemplates.filter(t => t.pageSize === 'THERMAL_80MM' || /receipt|pos|thermal/i.test(t.name)).map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </optgroup>
        <optgroup label="── A4 / Letter ──">
          {allTemplates.filter(t => t.pageSize !== 'THERMAL_80MM' && !/receipt|pos|thermal/i.test(t.name)).map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </optgroup>
        {customTemplates.length > 0 && (
          <optgroup label="── My Templates ──">
            {customTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </optgroup>
        )}
      </select>
      <button
        onClick={handleSetPosDefaultTemplate}
        disabled={savingDefaultTemplate || String(posDefaultTemplateId) === String(selectedTemplateId)}
        style={{
          marginTop: 5, width: '100%', fontSize: 10, fontWeight: 700, padding: '5px 8px', borderRadius: 6,
          border: '1px solid #c7d2fe', cursor: savingDefaultTemplate ? 'not-allowed' : 'pointer',
          background: String(posDefaultTemplateId) === String(selectedTemplateId) ? '#f0fdf4' : '#eef2ff',
          color: String(posDefaultTemplateId) === String(selectedTemplateId) ? '#16a34a' : '#4f46e5',
        }}
      >
        {String(posDefaultTemplateId) === String(selectedTemplateId)
          ? '✓ This is the org default'
          : savingDefaultTemplate ? 'Setting...' : 'Set as Default for team'}
      </button>
    </>
  )}
</div>
             
{/* Tracking Toggle */}
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
  <div>
    <label style={lbl}>Order Tracking QR</label>
    <div style={{ fontSize: 9, color: enableTracking ? '#6366f1' : '#9ca3af', fontWeight: 600 }}>
      {enableTracking ? '✓ Token + QR generated on save' : '✗ Plain receipt only'}
    </div>
  </div>
  <button onClick={() => setEnableTracking(s => !s)} style={{
    width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
    background: enableTracking ? '#6366f1' : '#d1d5db', position: 'relative',
    transition: 'background 0.2s', flexShrink: 0,
  }}>
    <div style={{
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: enableTracking ? 21 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    }} />
  </button>
</div>

{/* Post-Save Auto Actions */}
{perms.canPOSManageAutoActions && (
<div>
  <label style={lbl}>Auto Actions on Save</label>
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {[
     { key: 'print',       icon: '🖨',  label: 'Auto Print',            sub: 'Prints receipt on save' },
{ key: 'whatsapp',    icon: '💬',  label: 'Auto WhatsApp',         sub: 'Sends PDF to customer phone' },
{ key: 'email',       icon: '✉️',  label: 'Auto Email',            sub: 'Sends PDF to customer email' },
{ key: 'skipConfirm', icon: '🧾',  label: 'Skip Payment Confirm',  sub: 'Invoice only — no confirm step' },
    ].map(({ key, icon, label, sub }) => (
      <div key={key} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 10px',
        background: postSaveActions[key] ? '#f5f3ff' : '#f8fafc',
        borderRadius: 8,
        border: `1px solid ${postSaveActions[key] ? '#ddd6fe' : '#e5e7eb'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{label}</div>
            <div style={{ fontSize: 9, color: '#9ca3af' }}>{sub}</div>
          </div>
        </div>
        
        <button onClick={() => {
          const updated = { ...postSaveActions, [key]: !postSaveActions[key] };
          setPostSaveActions(updated);
          localStorage.setItem('cloudairambo_pos_actions', JSON.stringify(updated));
        }} style={{
          width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
          background: postSaveActions[key] ? '#6366f1' : '#d1d5db',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}>
          <div style={{
            width: 18, height: 18, borderRadius: '50%', background: '#fff',
            position: 'absolute', top: 3, left: postSaveActions[key] ? 21 : 3,
            transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>
    ))}
  </div>
</div>
)}
{/* Invoice number override */}
{perms.canPOSOverrideInvNo && (
  <div>
    <label style={lbl}>Receipt / Invoice No</label>
    <input style={{ ...inp, fontFamily: 'monospace', fontWeight: 700, color: '#3b82f6', letterSpacing: 1, fontSize: 11 }}
      placeholder="Auto-generated"
      value={invoiceNumberOverride}
      onChange={e => setInvoiceNumberOverride(e.target.value)} />
    <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>Leave blank to auto-assign</div>
  </div>
)}

             {/* Status Toggle + Status */}
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
  <label style={lbl}>Show Status on Receipt</label>
  <button onClick={() => setShowStatus(s => !s)} style={{
    width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
    background: showStatus ? '#6366f1' : '#d1d5db', position: 'relative',
    transition: 'background 0.2s', flexShrink: 0,
  }}>
    <div style={{
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: showStatus ? 21 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    }} />
  </button>
</div>

{/* Amount in Words Toggle */}
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
  <label style={lbl}>Show Amount in Words</label>
  <button onClick={() => {
    const next = !showAmountInWords;
    setShowAmountInWords(next);
    localStorage.setItem('cloudairambo_pos_amount_words', String(next));
  }} style={{
    width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
    background: showAmountInWords ? '#6366f1' : '#d1d5db', position: 'relative',
    transition: 'background 0.2s', flexShrink: 0,
  }}>
    <div style={{
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: showAmountInWords ? 21 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    }} />
  </button>
</div>


{showStatus && perms.canPOSChangeStatus && (
  <div>
    <label style={lbl}>Status</label>
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {(['paid', 'draft', 'unpaid', 'overdue', 'cancelled'] as const).map(s => (
        <button key={s} onClick={() => setInvoiceStatus(s)} style={{
          padding: '3px 9px', borderRadius: 5, border: '1px solid',
          fontSize: 10, fontWeight: 600, cursor: 'pointer',
          background: invoiceStatus === s ? (
            s === 'paid' ? '#10b981' : s === 'overdue' ? '#ef4444' : s === 'cancelled' ? '#6b7280' : s === 'unpaid' ? '#f59e0b' : '#3b82f6'
          ) : '#fff',
          color: invoiceStatus === s ? '#fff' : '#6b7280',
          borderColor: invoiceStatus === s ? 'transparent' : '#e5e7eb',
          textTransform: 'uppercase',
        }}>{s}</button>
      ))}
    </div>
  </div>
)}
{/* Printer */}

<div>
  <label style={lbl}>Printer</label>
  <select value={selectedPrinter} onChange={e => setSelectedPrinter(e.target.value)} style={{ ...inp, fontSize: 11 }}>
    <option value="">Default Printer</option>
    {availablePrinters.map(p => <option key={p} value={p}>{p}</option>)}
  </select>
  <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>
    {availablePrinters.length === 0 ? 'No printers detected — will use browser print' : `${availablePrinters.length} printer(s) found`}
  </div>
</div>

<div>
  <label style={lbl}>Printer Connection</label>
  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
    <button onClick={() => {
      setUseUSBPrinter(false);
      localStorage.setItem('cloudairambo_pos_usb_print', 'false');
    }} style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
      padding: '10px 6px', borderRadius: 8, border: '1.5px solid',
      cursor: 'pointer', fontSize: 11, fontWeight: 700,
      background: !useUSBPrinter ? '#eef2ff' : '#fff',
      borderColor: !useUSBPrinter ? '#6366f1' : '#e5e7eb',
      color: !useUSBPrinter ? '#4f46e5' : '#6b7280',
    }}>
      <span style={{ fontSize: 16 }}>📶</span>
      WiFi / Network
    </button>
    <button onClick={() => {
      setUseUSBPrinter(true);
      localStorage.setItem('cloudairambo_pos_usb_print', 'true');
    }} style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
      padding: '10px 6px', borderRadius: 8, border: '1.5px solid',
      cursor: 'pointer', fontSize: 11, fontWeight: 700,
      background: useUSBPrinter ? '#eef2ff' : '#fff',
      borderColor: useUSBPrinter ? '#6366f1' : '#e5e7eb',
      color: useUSBPrinter ? '#4f46e5' : '#6b7280',
    }}>
      <span style={{ fontSize: 16 }}>🔌</span>
      USB (direct)
    </button>
  </div>

  {!useUSBPrinter && (
    <div style={{ fontSize: 9, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
      Opens your device's print dialog on Save/Print — pick your WiFi printer
      there, same as printing from any other app. Make sure the printer is
      already added in your phone/computer's WiFi or printer settings first.
    </div>
  )}

  {useUSBPrinter && (
    <>
      <div style={{ fontSize: 9, color: '#6b7280', marginTop: 6 }}>
        {usbConnected ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected — skips the print dialog entirely.'}
      </div>
      {!usbConnected && (
        <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }} style={{
          marginTop: 6, width: '100%', fontSize: 11, fontWeight: 700,
          padding: '6px 8px', borderRadius: 6, border: '1px solid #c7d2fe',
          background: '#eef2ff', color: '#4f46e5', cursor: 'pointer',
        }}>
          🔌 Connect USB Printer
        </button>
      )}
    </>
  )}
</div>

{/* Date / Time */}
{perms.canPOSEditDateTime && (
              <div>
                <label style={lbl}>Date & Time</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="date" style={{ ...inp, flex: 1, fontSize: 11 }} value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
                  <input style={{ ...inp, width: 90, fontFamily: 'monospace', flexShrink: 0, fontSize: 11 }} value={invoiceTime} onChange={e => setInvoiceTime(e.target.value)} placeholder="04:32 am" />
                </div>
              </div>
)}
              {/* Tax Lines */}
              {perms.canPOSEditTaxLines && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <label style={{ ...lbl, marginBottom: 0 }}>Tax Lines</label>
                  <button onClick={() => setTaxLines(prev => [...prev, { label: 'Tax', rate: 0 }])}
                    style={{ fontSize: 10, fontWeight: 600, color: '#10b981', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>+ Add</button>
                </div>
                {taxLines.map((t, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 20px', gap: 4, marginBottom: 4 }}>
                    <input style={{ ...inp, fontSize: 11 }} placeholder="Tax label" value={t.label}
                      onChange={e => setTaxLines(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                    <div style={{ position: 'relative' }}>
                      <input type="number" style={{ ...inp, fontSize: 11, paddingRight: 16 }} placeholder="0" value={t.rate || ''}
                        onChange={e => setTaxLines(prev => prev.map((x, j) => j === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x))} />
                      <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>%</span>
                    </div>
                    <button onClick={() => taxLines.length > 1 && setTaxLines(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
)}
              {/* Discount Lines */}
              {perms.canPOSApplyDiscount && (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
      <label style={{ ...lbl, marginBottom: 0 }}>Discount Lines</label>
      <button onClick={() => setDiscountLines(prev => [...prev, { label: 'Discount', rate: 0 }])}
        style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>+ Add</button>
    </div>
    {discountLines.map((d, i) => (
      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 20px', gap: 4, marginBottom: 4 }}>
        <input style={{ ...inp, fontSize: 11 }} placeholder="Discount label" value={d.label}
          onChange={e => setDiscountLines(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
        <div style={{ position: 'relative' }}>
          <input type="number" style={{ ...inp, fontSize: 11, paddingRight: 16 }} placeholder="0" value={d.rate || ''}
            onChange={e => setDiscountLines(prev => prev.map((x, j) => j === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x))} />
          <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>%</span>
        </div>
        <button onClick={() => discountLines.length > 1 && setDiscountLines(prev => prev.filter((_, j) => j !== i))}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
          <Trash2 size={11} />
        </button>
      </div>
    ))}
  </div>
)}

              {/* Shipping */}
              {perms.canPOSEditShipping && (
              <div>
                <label style={lbl}>Shipping Cost</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}>{sym}</span>
                  <input type="number" style={{ ...inp, paddingLeft: 22, fontSize: 11 }} placeholder="0.00" value={shippingCost || ''}
                    onChange={e => setShippingCost(parseFloat(e.target.value) || 0)} />
                </div>
              </div>
              )}
            </div>
          )}

          {/* ── Client section ── */}
          <div style={cardStyle}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
    <label style={{ ...lbl, marginBottom: 0 }}>Customer</label>
    <button
      type="button"
      onClick={() => {
        const walkInInc = { name: true, phone: false, email: false, address: false, taxId: false };
        includeFieldsRef.current = walkInInc;
        setClientName('Walk-in Customer');
        setClientSearch('Walk-in Customer');
        setClientPhone('');
        setClientEmail('');
        setClientAddress('');
        setClientGSTIN('');
        setIncludeFields(walkInInc);
      }}
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: '#2563eb',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 5,
        padding: '2px 8px',
        cursor: 'pointer',
      }}
    >
      👤 Walk-in
    </button>
  </div>

  {/* Individual Checkboxes for each field */}
  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
    {[
      { key: 'name' as const, label: 'Name' },
      { key: 'phone' as const, label: 'Phone' },
      { key: 'email' as const, label: 'Email' },
      { key: 'address' as const, label: 'Address' },
      { key: 'taxId' as const, label: 'Tax ID' },
    ].map(({ key, label }) => (
      <label
        key={key}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 10,
          fontWeight: 700,
          cursor: 'pointer',
          padding: '3px 7px',
          borderRadius: 6,
          userSelect: 'none',
          background: includeFields[key] ? '#eff6ff' : '#f8fafc',
          color: includeFields[key] ? '#2563eb' : '#94a3b8',
          border: `1px solid ${includeFields[key] ? '#bfdbfe' : '#e2e8f0'}`,
          transition: 'all 0.15s ease',
        }}
      >
        <input
          type="checkbox"
          checked={includeFields[key]}
          onChange={e => {
            const next = { ...includeFields, [key]: e.target.checked };
            includeFieldsRef.current = next;
            setIncludeFields(next);
          }}
          style={{ accentColor: '#2563eb', cursor: 'pointer', margin: 0, width: 12, height: 12 }}
        />
        {label}
      </label>
    ))}
  </div>

  <div ref={clientDropdownRef} style={{ position: 'relative', opacity: includeFields.name ? 1 : 0.45 }}>
    <User size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
    <input
      style={{ ...inp, paddingLeft: 28 }}
      placeholder="Walk-in or search customer..."
      value={clientSearch}
      onChange={e => { setClientSearch(e.target.value); setClientName(e.target.value); setShowClientDropdown(true); }}
      onFocus={() => setShowClientDropdown(true)}
    />
    {showClientDropdown && filteredAccounts.length > 0 && (
      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, zIndex: 50, maxHeight: 130, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        {filteredAccounts.slice(0, 6).map(acc => (
          <div key={acc.id} onClick={() => selectAccount(acc)}
            style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f9fafb' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
            onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
            <div style={{ fontWeight: 600, color: '#111827' }}>{acc.name}</div>
            <div style={{ color: '#9ca3af', fontSize: 10 }}>{acc.email}</div>
          </div>
        ))}
      </div>
    )}
  </div>
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6, marginTop: 6 }}>
    <div style={{ opacity: includeFields.email ? 1 : 0.45 }}>
      <input style={{ ...inp, fontSize: 11 }} placeholder="Email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} />
    </div>
    <div style={{ opacity: includeFields.phone ? 1 : 0.45 }}>
      <PhoneInput key={phoneResetKey} value={clientPhone} onChange={setClientPhone} inputStyle={{ ...inp, fontSize: 11 }} defaultCountry={outletPhoneCountry} />
    </div>
  </div>
  <div style={{ marginTop: 6, opacity: includeFields.address ? 1 : 0.45 }}>
    <input style={{ ...inp, fontSize: 11 }} placeholder="Address" value={clientAddress} onChange={e => setClientAddress(e.target.value)} />
  </div>
  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6, opacity: includeFields.taxId ? 1 : 0.45 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
        Buyer {taxIdMeta.taxLabel}
      </span>
      <button
        type="button"
        onClick={() => setShowTaxDisclaimerModal(true)}
        style={{
          background: 'none', border: 'none', color: '#3b82f6', fontSize: 10,
          fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: 0
        }}
      >
        ℹ️ Legal Disclaimer
      </button>
    </div>
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        style={{ ...inp, fontSize: 11, paddingRight: 80, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5 }}
        placeholder={`Buyer's ${taxIdMeta.taxLabel} (${REGIONAL_TAX_COUNTRIES.has(taxIdMeta.countryCode) ? 'auto-fills state/region' : 'optional'})`}
        maxLength={25}
        value={clientGSTIN}
        onChange={e => {
          const clean = e.target.value.replace(/[\s\-\.]/g, '').toUpperCase();
          setClientGSTIN(clean);
          setForceB2BOverride(false);
        }}
      />
      {taxIdMeta.currentLength > 0 && (
        <div 
          onClick={() => setShowTaxDisclaimerModal(true)}
          style={{ position: 'absolute', right: 10, cursor: 'pointer' }}
          title="Click to view verification disclaimer"
        >
          {taxIdMeta.isComplete || taxIdMeta.currentLength >= taxIdMeta.expectedLength ? (
            <span style={{ fontSize: 11, fontWeight: 800, color: taxIdMeta.isValid ? '#16a34a' : '#ef4444', display: 'flex', alignItems: 'center', gap: 2 }}>
              {taxIdMeta.isValid ? '✓ Valid' : '✗ Invalid'} <span style={{ fontSize: 9, opacity: 0.7 }}>ℹ️</span>
            </span>
          ) : (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>
              {taxIdMeta.currentLength}/{taxIdMeta.expectedLength}
            </span>
          )}
        </div>
      )}
    </div>
    <div style={{ fontSize: 9, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 4, marginTop: -1, lineHeight: 1.4 }}>
      <span>⚠️ <strong>Do not trust this for legal verification:</strong> This only detects format and character count, and filtering can be wrong. Always verify active business status on official government websites.</span>
    </div>

    {/* Manual B2B override — only offered when there's a real Tax ID typed
        in that our validator is rejecting. Never shown for an empty field,
        and never silently applied — cashier must explicitly tick it, and
        it resets on every edit (see onChange above). */}
    {clientGSTIN.trim().length > 0 &&
      (taxIdMeta.isComplete || taxIdMeta.currentLength >= taxIdMeta.expectedLength) &&
      !taxIdMeta.isValid && (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7,
        padding: '7px 10px',
      }}>
        <input
          type="checkbox"
          id="posForceB2BOverride"
          checked={forceB2BOverride}
          onChange={e => setForceB2BOverride(e.target.checked)}
          style={{ cursor: 'pointer', accentColor: '#f59e0b', flexShrink: 0 }}
        />
        <label htmlFor="posForceB2BOverride" style={{ fontSize: 10, fontWeight: 600, color: '#92400e', cursor: 'pointer', lineHeight: 1.4 }}>
          This Tax ID doesn't validate, but I know it's correct — bill this as B2B anyway (at my own risk).
        </label>
      </div>
    )}

    {taxIdMeta.currentLength >= 2 && (
      <div style={{
        display: 'flex', alignItems: 'center',
        backgroundColor: (taxIdMeta.isComplete && !taxIdMeta.isValid) ? '#fef2f2' : taxIdMeta.isValid ? '#f0fdf4' : '#eff6ff',
        border: `1px solid ${(taxIdMeta.isComplete && !taxIdMeta.isValid) ? '#fecaca' : taxIdMeta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
        borderRadius: 7, padding: '5px 9px', gap: 6, fontSize: 11,
      }}>
        <span style={{ fontSize: 13 }}>{taxIdMeta.countryFlag}</span>
        <span style={{ fontWeight: 700, color: '#1e293b', flex: 1 }}>
          {taxIdMeta.countryName}
          {buyerRegion ? ` · ${buyerRegion}` : ''}
          <span style={{ color: '#64748b', fontWeight: 500 }}> ({taxIdMeta.taxLabel})</span>
        </span>
        {buyerRegion && (!taxIdMeta.isComplete || taxIdMeta.isValid) && (
          <span style={{ fontSize: 10, fontWeight: 800, color: '#16a34a' }}>✓ State Auto-Set</span>
        )}
      </div>
    )}

    {(REGIONAL_TAX_COUNTRIES.has(taxIdMeta.countryCode) ||
      Boolean(taxIdMeta.detectedRegion) ||
      (!clientGSTIN.trim() && posTaxScheme?.region_based) ||
      (taxIdMeta.countryCode === sellerCountry && posTaxScheme?.region_based)) && (
      <StatePicker
        countryCode={taxIdMeta.countryCode || sellerCountry}
        value={buyerRegion}
        onChange={(v: string) => { setBuyerRegion(v); setGstinAutoDetected(false); }}
        placeholder="Buyer's State / Province"
      />
    )}
  </div>
  <input 
    style={{ ...inp, fontSize: 11, marginTop: 6 }} 
    placeholder="Ready By (e.g. 5:00 PM)" 
    value={readyBy} 
    onChange={e => setReadyBy(e.target.value)} 
  />
</div>

          {/* ── Dynamic Template Fields (Matching QuickInvoice) ── */}
          {dynamicFields.length > 0 && dynamicFields.some(f => !isStaffField(f.key)) && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ ...lbl, marginBottom: 0 }}>Template Fields</label>
                <span style={{ fontSize: 9, color: '#6366f1', fontWeight: 700 }}>
                  {dynamicFields.filter(f => !isStaffField(f.key)).length} field{dynamicFields.filter(f => !isStaffField(f.key)).length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {dynamicFields.map(f => {
                  if (isStaffField(f.key)) return null;
                  const val = dynamicValues[f.key] !== undefined ? dynamicValues[f.key] : '';

                  return (
                    <div key={f.key}>
                      <label style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>{f.label}</label>
                      <input
                        style={{ ...inp, fontSize: 11, padding: '7px 10px' }}
                        placeholder={f.placeholder}
                        value={val}
                        onChange={e => {
                          const newVal = e.target.value;
                          setDynamicValues(prev => ({ ...prev, [f.key]: newVal }));
                          if (bilingualEnabled) {
                            clearTimeout(autoTranslateTimer.current);
                            autoTranslateTimer.current = setTimeout(() => {
                              handlePOSTranslate();
                            }, 600);
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Table Columns (Base Column Hiding + Custom Columns + Restore Tray) ── */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Table Columns</label>
              <button
                onClick={() => {
                  if (!perms.canUseCustomColumns) { alert('Custom columns is a Pro feature.'); return; }
                  setShowAddPosCol(s => !s);
                }}
                style={{ fontSize: 10, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>
                + Add Col
              </button>
            </div>

            {/* Add Column Tray & Restore Removed Base Columns */}
            {showAddPosCol && (() => {
              const used = new Set(posColumns.map(c => c.key));
              const hiddenBase = toggleableBase.filter(b => hiddenColumns.includes(b.key));
              const options = catalogColumnOptions
                .filter(o => !used.has(o.key))
                .filter(o => !((o.key === 'cat_tax_b2c' || o.key === 'cat_tax_b2b') && (used.has('cat_tax_b2c') || used.has('cat_tax_b2b'))));

              const addColumn = (name: string) => {
                if (!name.trim()) return;
                const match = catalogColumnOptions.find(o => o.label.toLowerCase() === name.trim().toLowerCase());
                const key = match ? match.key : name.trim().toLowerCase().replace(/\s+/g, '_');
                if (posColumns.some(c => c.key === key)) return;
                setPosColumns(prev => [...prev, { id: `col-${Date.now()}`, header: match ? match.header : name.trim(), key, align: 'center' }]);
                setNewPosColName('');
                setShowAddPosCol(false);
              };

              return (
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 7, padding: '8px 10px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Restore Base Columns Tray */}
                  {hiddenBase.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: 4 }}>
                        Restore Removed Standard Columns (click to show)
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {hiddenBase.map(b => (
                          <button
                            key={b.key}
                            type="button"
                            onClick={() => setHiddenColumns(prev => prev.filter(k => k !== b.key))}
                            style={{
                              background: '#ecfdf5',
                              border: '1px solid #a7f3d0',
                              borderRadius: 5,
                              padding: '3px 8px',
                              color: '#059669',
                              fontSize: 10,
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 3,
                            }}
                          >
                            + {b.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Catalog Column Dropdown */}
                  {options.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', marginBottom: 3 }}>
                        Catalog Columns (Pick to auto-fill from products)
                      </div>
                      <select
                        style={{ ...inp, fontSize: 11 }}
                        value=""
                        onChange={e => { if (e.target.value) addColumn(e.target.value); }}
                      >
                        <option value="">— Select a column —</option>
                        {options.map(o => <option key={o.key} value={o.label}>{o.label}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Manual Column Name Input */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      style={{ ...inp, fontSize: 11, flex: 1 }}
                      placeholder={options.length > 0 ? 'Or type custom column name...' : 'Column name (e.g. Batch No)'}
                      value={newPosColName}
                      onChange={e => setNewPosColName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addColumn(newPosColName); }}
                    />
                    <button onClick={() => addColumn(newPosColName)} style={{ background: '#7c3aed', border: 'none', borderRadius: 5, padding: '5px 10px', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Add</button>
                    <button onClick={() => setShowAddPosCol(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, lineHeight: 1 }}>×</button>
                  </div>
                </div>
              );
            })()}

            {/* Active Column Chips (Base columns [x] + Custom columns with position picker & [x]) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {/* Base Columns */}
              {toggleableBase.filter(b => !hiddenColumns.includes(b.key)).map(b => (
                <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 7px', fontSize: 11, color: '#475569' }}>
                  <span>{b.label}</span>
                  <button
                    type="button"
                    title={`Hide ${b.label} column`}
                    onClick={() => setHiddenColumns(prev => [...prev, b.key])}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, lineHeight: 1, padding: 0 }}
                  >
                    ×
                  </button>
                </div>
              ))}

              {/* Custom Columns */}
              {posColumns.map(col => (
                <div key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#ede9fe', border: '1px solid #ddd6fe', borderRadius: 4, padding: '2px 7px', fontSize: 11, color: '#5b21b6' }}>
                  <span>{col.header}</span>
                  <select
                    value={col.after || ''}
                    onChange={e => setPosColumns(prev => prev.map(c => c.id === col.id ? { ...c, after: e.target.value || undefined } : c))}
                    title="Where this column goes in the table"
                    style={{ fontSize: 10, border: '1px solid #ddd6fe', borderRadius: 4, background: '#fff', color: '#5b21b6', padding: '1px 2px', cursor: 'pointer', outline: 'none' }}
                  >
                    <option value="">at end</option>
                    {templateTableCols.filter((tc: any) => tc.key === mainColKey || !hiddenColumns.includes(tc.key)).map((tc: any) => (
                      <option key={tc.key} value={tc.key}>after {tc.originalHeader || tc.header || tc.key}</option>
                    ))}
                  </select>
                  <button onClick={() => setPosColumns(prev => prev.filter(c => c.id !== col.id))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                </div>
              ))}
            </div>
          </div>

          {/* ── Cart items ── */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Cart Items</label>
              {cart.length > 0 && (
                <button onClick={() => setCart([])} style={{ fontSize: 10, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', fontWeight: 600 }}>
                  Clear All
                </button>
              )}
            </div>

            {cart.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', padding: '24px 16px', color: '#9ca3af' }}>
                <ShoppingCart size={28} style={{ marginBottom: 8, opacity: 0.3 }} />
                <div style={{ fontSize: 12, fontWeight: 600 }}>Cart is empty</div>
                <div style={{ fontSize: 11, marginTop: 3 }}>Click products or scan to add items</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {cart.map(item => (
                  <div key={item.id} style={{
                    ...cardStyle,
                    padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>{item.name}</div>
                        {(item as any).arabicName && <div style={{ fontSize: 11, color: '#64748b', fontFamily: ['ar','ur','fa','iw','ckb','ku','ps','sd','dv'].includes(posTranslateLang) ? 'Noto Sans Arabic, sans-serif' : 'inherit', direction: ['ar','ur','fa','iw','ckb','ku','ps','sd','dv'].includes(posTranslateLang) ? 'rtl' : 'ltr', textAlign: ['ar','ur','fa','iw','ckb','ku','ps','sd','dv'].includes(posTranslateLang) ? 'right' : 'left' }}>{(item as any).arabicName}</div>}
                        {item.unit && <div style={{ fontSize: 11, color: '#94a3b8' }}>per {item.unit}</div>}
                      </div>
                      <button onClick={() => removeFromCart(item.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, flexShrink: 0 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}>
                        <X size={15} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* Qty stepper */}
                      <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #cbd5e1', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                        <button onClick={() => updateCartQty(item.id, -1)}
                          style={{ padding: '6px 10px', background: '#f8fafc', border: 'none', cursor: 'pointer', color: '#334155', display: 'flex', alignItems: 'center' }}>
                          <Minus size={13} />
                        </button>
                        <input
                          type="number"
                          value={item.qty}
                          min={1}
                          onChange={e => setCartQty(item.id, parseFloat(e.target.value) || 1)}
                          style={{ width: 44, textAlign: 'center', border: 'none', fontSize: 13, fontWeight: 800, color: '#0f172a', outline: 'none', padding: '6px 2px' }}
                        />
                        <button onClick={() => updateCartQty(item.id, 1)}
                          style={{ padding: '6px 10px', background: '#f8fafc', border: 'none', cursor: 'pointer', color: '#334155', display: 'flex', alignItems: 'center' }}>
                          <Plus size={13} />
                        </button>
                      </div>
                      {/* Rate */}
                      <div style={{ position: 'relative', flex: 1 }}>
                        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8' }}>{sym}</span>
                        <input
  type="number"
  value={item.rate || ''}
  onChange={e => updateCartRate(item.id, parseFloat(e.target.value) || 0)}
  readOnly={!perms.canPOSOverridePrice}
  style={{ ...inp, paddingLeft: 24, fontSize: 13, fontWeight: 600, textAlign: 'right', background: !perms.canPOSOverridePrice ? '#f8fafc' : '#fff' }}
  placeholder="0.00"
/>
                      </div>
                      {/* Line total */}
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#2563eb', minWidth: 64, textAlign: 'right', flexShrink: 0 }}>
                        {sym}{(item.qty * item.rate).toFixed(2)}
                      </div>
                    </div>
                    {((item.taxRate ?? 0) > 0 || (isB2B && (item.b2bTaxRate ?? 0) > 0)) && (
                      <div style={{ display: 'flex', gap: 12, marginTop: 2, alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>
                            {isB2B && item.b2bTaxRate !== undefined ? 'B2B Tax' : 'Tax Rate'}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: isB2B && item.b2bTaxRate !== undefined ? '#16a34a' : '#7c3aed' }}>
                            {getEffectiveTaxRate(item)}%
                          </span>
                          {isB2B && (
                            <span style={{ fontSize: 8, fontWeight: 800, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', padding: '0 4px', borderRadius: 4 }}>
                              ITC
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Inclusive</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: item.taxInclusive ? '#16a34a' : '#6b7280' }}>
                            {item.taxInclusive ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* ── Bottom: Payment mode + Totals + Save ── */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Payment mode */}
          <div>
            <label style={{ ...lbl, marginBottom: 6 }}>Payment Mode</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {PAY_MODES.map(pm => (
                <button key={pm.key} onClick={() => {
                  setPaymentMode(pm.key);
                  // Cash is settled the moment it's tendered — default
                  // status to Paid so it matches (user can still override).
                  if (pm.key === 'cash') setInvoiceStatus('paid');
                }} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 8px', borderRadius: 10, border: '2px solid',
                  fontSize: 13, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s',
                  background: paymentMode === pm.key ? '#3b82f6' : '#f8fafc',
                  color: paymentMode === pm.key ? '#fff' : '#64748b',
                  borderColor: paymentMode === pm.key ? '#3b82f6' : '#cbd5e1',
                }}>
                  {pm.icon} {pm.label}
                </button>
              ))}
            </div>
          </div>

          {/* Totals */}
          {cart.length > 0 && (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Subtotal</span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{sym}{subtotal.toFixed(2)}</span>
              </div>
              {productTaxTotal > 0 && posTaxScheme.kind === 'component' && taxBreakdown ? (
                <>
                  {taxBreakdown.map(({ name, rate, amount }) => (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ color: '#7c3aed', fontSize: 11 }}>{name} ({rate}%)</span>
                      <span style={{ fontSize: 11, color: '#7c3aed' }}>+{sym}{amount.toFixed(2)}</span>
                    </div>
                  ))}
                  {posTaxScheme.region_based && !buyerRegion.trim() && (
                    <div style={{ fontSize: 9, color: '#f59e0b', fontStyle: 'italic', marginBottom: 3 }}>
                      ⚠ Enter buyer's state above — showing IGST (different-state) by default
                    </div>
                  )}
                </>
              ) : productTaxTotal > 0 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#7c3aed', fontSize: 11 }}>Product Tax</span>
                  <span style={{ fontSize: 11, color: '#7c3aed' }}>+{sym}{productTaxTotal.toFixed(2)}</span>
                </div>
              ) : null}
              {taxLines.filter(t => t.rate > 0).map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#6b7280', fontSize: 11 }}>{t.label} ({t.rate}%)</span>
                  <span style={{ fontSize: 11 }}>+{sym}{((subtotal - taxReducingDiscount) * t.rate / 100).toFixed(2)}</span>
                </div>
              ))}
              {perms.canPOSApplyDiscount && discountLines.filter(d => d.rate > 0).map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#6b7280', fontSize: 11 }}>{d.label} ({d.rate}%)</span>
                  <span style={{ fontSize: 11, color: '#ef4444' }}>-{sym}{(subtotal * d.rate / 100).toFixed(2)}</span>
                </div>
              ))}
              {autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#059669', fontSize: 11 }}>
                    {autoDiscountRule.label || 'Loyalty Discount'} 🏷️
                  </span>
                  <span style={{ fontSize: 11, color: '#059669' }}>-{sym}{autoDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              {autoDiscountRule && !autoDiscountRule.visibleOnBill && autoDiscountAmount > 0 && (
                <div style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic', marginBottom: 3 }}>
                  ✓ Loyalty discount applied (hidden from bill)
                </div>
              )}
              {shippingCost > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#6b7280', fontSize: 11 }}>Shipping</span>
                  <span style={{ fontSize: 11 }}>+{sym}{shippingCost.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Grand Total</span>
                <span style={{ fontWeight: 800, fontSize: 18, color: '#3b82f6' }}>{sym}{grandTotal.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Last saved + Confirm Payment */}
          {lastSaved && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '6px 10px', fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
              <CheckCircle size={12} /> Last saved: {lastSaved}
            </div>
          )}
          {lastSavedInvoice && !paymentFailed && !lastTokenQR && paymentMode === 'cash' && (
            <button
              onClick={() => confirmAndPrint()}
              disabled={confirmingPayment}
              style={{
                width: '100%', padding: '15px 18px', borderRadius: 12, border: 'none',
                background: confirmingPayment ? '#cbd5e1' : 'linear-gradient(135deg,#22c55e,#16a34a)',
                color: '#fff', fontWeight: 800, fontSize: 15, cursor: confirmingPayment ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 16px rgba(34,197,94,0.4)',
              }}
            >
              {confirmingPayment ? 'Confirming...' : '✅ Confirm Payment & Print'}
            </button>
          )}

          {lastSavedInvoice && paymentFailed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '8px 10px', fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
                <AlertCircle size={14} /> Payment failed for {lastSavedInvoice.number}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setPaymentFailed(false)}
                  style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >
                  🔄 Retry Payment
                </button>
              {perms.canPOSDeleteBill && (
<button
  onClick={async () => {
    if (!lastSavedInvoice) return;
    if (!window.confirm(`Delete bill ${lastSavedInvoice.number}? This cannot be undone.`)) return;
    try {
      const tkn = getStoredToken();
      await fetch(`${BACKEND}/invoices/${lastSavedInvoice.id}`, {
        method: 'DELETE',
        headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
      });
      setBills(prev => prev.filter(b => b.id !== lastSavedInvoice.id));
    } catch (e) {
      console.warn('Delete failed:', e);
    } finally {
      setLastSavedInvoice(null);
      setPaymentFailed(false);
    }
  }}
  style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
>
  🗑 Delete Bill
</button>
)}
              </div>
            </div>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving || (bilingualEnabled && isTranslatingPOS) || cart.length === 0 || !perms.canAccessPOS}
            style={{
              width: '100%', padding: '16px 20px', borderRadius: 12, border: 'none',
              background: (bilingualEnabled && isTranslatingPOS)
                ? '#94a3b8'
                : cart.length === 0 || !perms.canAccessPOS
                ? '#e2e8f0'
                : 'linear-gradient(to right,#3b82f6,#9333ea)',
              color: (bilingualEnabled && isTranslatingPOS) || cart.length === 0 || !perms.canAccessPOS ? '#f8fafc' : '#fff',
              fontWeight: 800, fontSize: 16,
              cursor: (saving || (bilingualEnabled && isTranslatingPOS) || cart.length === 0 || !perms.canAccessPOS) ? 'not-allowed' : 'pointer',
              opacity: saving || (bilingualEnabled && isTranslatingPOS) ? 0.8 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: cart.length > 0 && perms.canAccessPOS && !(bilingualEnabled && isTranslatingPOS) ? '0 4px 16px rgba(59,130,246,0.4)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {bilingualEnabled && isTranslatingPOS
              ? '🌐 Translating receipt...'
              : saving
              ? (editingPendingInvoice ? 'Updating pending bill...' : 'Saving...')
              : cart.length === 0
              ? 'Add items to save'
              : !perms.canAccessPOS
              ? 'No permission to save'
              : editingPendingInvoice
              ? <><Receipt size={16} /> Update Pending Bill — {sym}{grandTotal.toFixed(2)}</>
              : lastSavedInvoice
  ? <><Receipt size={16} /> Update Bill — {sym}{grandTotal.toFixed(2)}</>
  : <><Receipt size={16} /> Save Bill — {sym}{grandTotal.toFixed(2)}</>}
          </button>
        </div>
      </div>

{/* Hidden print area - always rendered for puppeteer */}
      <div style={{ position: 'fixed', left: 0, top: 0, width: 1, height: 1, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ width: 302 }}>
        <div id="pos-receipt-print-area">
          {cart.length > 0 && (() => {
            const rawTemplate = allTemplates.find(t => t.id === selectedTemplateId);

            // Match Quick Invoice: Merge auto loyalty into discount lines with rate & translation
            const cleanManualDiscounts = discountLines.filter(d => !d._isAuto && !/loyalty/i.test(d.label || ''));
            const loyaltyLabelAr = (window as any).__pos_discount_lines_ar?.find((x: any) => x.label === autoDiscountRule?.label)?.labelAr;
            const previewDiscountLines = (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0)
              ? [
                  ...cleanManualDiscounts,
                  {
                    label: autoDiscountRule.label || 'Loyalty Discount',
                    rate: parseFloat(((autoDiscountAmount / Math.max(subtotal, 1)) * 100).toFixed(2)),
                    amount: autoDiscountAmount,
                    translatedLabel: loyaltyLabelAr,
                    _isAuto: true,
                  },
                ]
              : cleanManualDiscounts;
            const selectedTemplate = translatedTemplate || (rawTemplate ? { ...rawTemplate, elements: rawTemplate.elements.map((el: any) => ({ ...el, arabicContent: undefined })) } : undefined);
            if (!selectedTemplate) return null;
            return (
              <POSReceiptRenderer
                clientNameAr={includeFields.name ? ((window as any).__pos_client_name_ar || undefined) : undefined}
                clientLineAr={includeFields.address ? ((window as any).__pos_client_address_ar || undefined) : undefined}
  template={{
  ...(lastSyncedTemplate || selectedTemplate),
  elements: (lastSyncedElements.length > 0
    ? lastSyncedElements
    : (translatedTemplate || selectedTemplate)?.elements || []
  ).map((el: any) => ({
    ...el,
    ...(translatedTemplate ? {} : { arabicContent: undefined }),
  })),
}}
                clientName={includeFields.name ? (clientName || 'Walk-in Customer') : ''}
                clientEmail={includeFields.email ? clientEmail : ''}
                clientPhone={includeFields.phone ? clientPhone : ''}
                clientAddress={includeFields.address ? clientAddress : ''}
                buyerTaxId={includeFields.taxId ? (clientGSTIN.trim() || undefined) : undefined}
                isB2B={isB2B || Boolean(clientGSTIN.trim())}
                invoiceDate={invoiceDate}
                invoiceTime={invoiceTime}
                notes={notes}
                lineItems={cart.map(item => ({
                  id: item.id,
                  description: item.name,
                  qty: item.qty,
                  rate: item.rate,
                  arabicName: (item as any).arabicName,
                  taxRate: getEffectiveTaxRate(item),
                  taxName: item.taxName,
                  b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
                  b2bTaxName: isB2B ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || item.taxName || customTaxScheme?.name) : undefined,
                  taxInclusive: item.taxInclusive, ...posColValues(item),
                  customColAr: (item as any).customColAr,
                }))}
                taxLines={taxLines}
                discountLines={previewDiscountLines}
                sym={sym}
                shippingCost={shippingCost}
                invoiceStatus={showStatus ? invoiceStatus : undefined}
                translateTargetLang={posTranslateLang}
                isBilingual={!!translatedTemplate}
                dynamicValues={dynamicValues}
                dynamicValuesAr={bilingualEnabled ? translatedValues : undefined}
                dynamicFieldLabelsAr={bilingualEnabled ? dynamicFieldLabelsAr : undefined}
                isThermal={(selectedTemplate?.pageSize || '') === 'THERMAL_80MM'}
                showTokenQRPlaceholder={false}
                extraColumns={posColumns}
                showWalkIn={true}
                showAmountInWords={showAmountInWords}
                amountInWords={showAmountInWords ? amountToWords(grandTotal, activeCurrency) : undefined}
                amountInWordsTranslated={showAmountInWords && translatedTemplate ? (window as any).__pos_amount_words_ar : undefined}
                productTaxTotal={productTaxTotal}
                taxBreakdown={taxBreakdown}
                taxBreakdownAr={translatedTemplate ? (window as any).__pos_tax_breakdown_ar : undefined}
                autoDiscountAmount={autoDiscountRule && autoDiscountRule.visibleOnBill ? autoDiscountAmount : 0}
                autoDiscountLabel={autoDiscountRule?.label}
                netSubtotal={subtotal}
                notesAr={translatedTemplate ? (window as any).__pos_notes_ar : undefined}
                rowLabelsAr={translatedTemplate ? (window as any).__pos_row_labels_ar : undefined}
                taxLinesAr={translatedTemplate ? (window as any).__pos_tax_lines_ar : undefined}
                discountLinesAr={translatedTemplate ? (window as any).__pos_discount_lines_ar : undefined}
              />
            );
          })()}
        </div>
        </div>
      </div>

     {/* ── INLINE: Live Receipt Preview Panel ── */}
      {(isMobile ? mobileTab === 'preview' : showPreview) && (() => {
        const rawTemplate = allTemplates.find(t => t.id === selectedTemplateId);
        const cleanTemplate = rawTemplate ? {
          ...rawTemplate,
          elements: rawTemplate.elements.map((el: any) => ({ ...el, arabicContent: undefined })),
        } : undefined;
        const selectedTemplate = translatedTemplate || cleanTemplate;
        if (!selectedTemplate) return null;
        // A4/Letter renders the real CanvasWorkspace, which has its own zoom/pan —
        // running our previewZoom/panPos transform on top of that double-zooms and
        // fights its own controls, so it's skipped for this case only.
        const isCanvasPreview = selectedTemplate.pageSize === 'A4' || selectedTemplate.pageSize === 'LETTER';
        return (
          <div style={{
  width: isMobile ? '100%' : previewPanelWidthState, minWidth: isMobile ? 0 : 240, maxWidth: isMobile ? '100%' : 900,
  display: 'flex', flexDirection: 'column',
  background: '#f1f5f9', borderLeft: isMobile ? 'none' : '1px solid #e5e7eb',
  flexShrink: 0, overflow: 'hidden',
  position: 'relative',
}}>
  {/* Drag handle */}
  <div
    onMouseDown={(e) => {
      e.preventDefault();
      manualPreviewResizeRef.current = true;
      const startX = e.clientX;
      const startW = previewPanelWidthState;
      const onMove = (mv: MouseEvent) => {
        const newW = Math.min(900, Math.max(240, startW - (mv.clientX - startX)));
        setPreviewPanelWidthState(newW);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }}
    style={{
      position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
      cursor: 'col-resize', zIndex: 10,
      background: 'transparent',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(99,102,241,0.3)')}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
  />
            {/* Header */}
            <div style={{
              padding: '8px 12px', background: '#fff',
              borderBottom: '1px solid #e5e7eb', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            }}>
              {/* Left: title */}
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>👁 Live Preview</span>
                {translatedTemplate && (
                  <span style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700 }}>
                    {posTranslateLang.toUpperCase()}
                  </span>
                )}
              </div>

              {/* Right: controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>

                {/* Print */}
                <button onClick={async () => {
                  const previewTemplate = allTemplates.find(t => t.id === selectedTemplateId);
                  if (previewTemplate?.pageSize === 'A4' || previewTemplate?.pageSize === 'LETTER') {
                    const blob = await buildPOSPdfBlob();
                    if (!blob) return alert('Could not prepare the PDF for printing.');
                    const url = URL.createObjectURL(blob);
                    const iframe = document.createElement('iframe');
                    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
                    iframe.src = url;
                    document.body.appendChild(iframe);
                    let printFired = false;
                    const firePrint = () => {
                      if (printFired) return;
                      printFired = true;
                      iframe.contentWindow?.focus();
                      iframe.contentWindow?.print();
                    };
                    iframe.onload = () => setTimeout(firePrint, 300);
                    setTimeout(firePrint, 1200);
                    setTimeout(() => {
                      if (document.body.contains(iframe)) document.body.removeChild(iframe);
                      URL.revokeObjectURL(url);
                    }, 30000);
                    return;
                  }
                  const el = document.getElementById('pos-receipt-preview-area');
                  if (!el) return;
                  const printWindow = window.open('', '_blank', 'width=420,height=800');
                  if (!printWindow) return alert('Allow pop-ups to print.');
                  printWindow.document.write(`<!DOCTYPE html><html><head><title>Receipt</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#fff;}@media print{@page{size:80mm auto;margin:0;}}</style></head><body>${el.innerHTML}</body></html>`);
                  printWindow.document.close();
                  setTimeout(() => { printWindow.focus(); printWindow.print(); }, 500);
                }} style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', color: '#374151', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                  🖨 <span>Print</span>
                </button>

                {/* Zoom controls */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                  <button onClick={() => setPreviewZoom(z => Math.max(0.3, z - 0.1))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', padding: '3px 7px', fontWeight: 700, lineHeight: 1 }}>−</button>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', minWidth: 30, textAlign: 'center' }}>
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <button onClick={() => setPreviewZoom(z => Math.min(3, z + 0.1))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', padding: '3px 7px', fontWeight: 700, lineHeight: 1 }}>+</button>
                  <button onClick={() => setPreviewZoom(1)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#9ca3af', padding: '3px 6px', borderLeft: '1px solid #e5e7eb' }}>↺</button>
                </div>

                {/* Hand tool */}
                <button onClick={() => { setHandToolActive(h => !h); setPanPos({ x: 0, y: 0 }); }}
                  title={handToolActive ? 'Hand tool active' : 'Pan mode'}
                  style={{ background: handToolActive ? '#e0e7ff' : '#f1f5f9', border: `1px solid ${handToolActive ? '#818cf8' : '#e5e7eb'}`, borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: handToolActive ? '#4f46e5' : '#374151', fontSize: 12 }}>
                  ✋
                </button>

                {/* Close */}
                <button onClick={() => setShowPreview(false)}
                  style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#9ca3af', fontSize: 12, fontWeight: 700 }}>
                  ✕
                </button>
              </div>
            </div>
            {/* Scrollable preview */}
            <div
  style={{
    flex: 1, overflowY: handToolActive ? 'hidden' : 'auto',
    overflowX: 'auto', display: 'flex', justifyContent: 'center',
    padding: 16, paddingBottom: 0, background: '#e2e8f0',
    cursor: handToolActive ? (isPanning.current ? 'grabbing' : 'grab') : 'default',
    userSelect: 'none',
  }}
  onWheel={(e) => {
    if (handToolActive) return;
    e.preventDefault();
    setPreviewZoom(z => Math.min(3, Math.max(0.3, z - e.deltaY * 0.001)));
  }}
  onMouseDown={(e) => {
    if (!handToolActive) return;
    isPanning.current = true;
    panStart.current = { x: e.clientX - panPos.x, y: e.clientY - panPos.y };
  }}
  onMouseMove={(e) => {
    if (!handToolActive || !isPanning.current) return;
    setPanPos({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  }}
  onMouseUp={() => { isPanning.current = false; }}
  onMouseLeave={() => { isPanning.current = false; }}
>
              <div style={{
  width: isCanvasPreview ? '100%' : templateNativeWidth,
  transformOrigin: 'top center',
  transform: isCanvasPreview ? 'none' : `scale(${previewScale * previewZoom}) translate(${panPos.x}px, ${panPos.y}px)`,
  marginBottom: isCanvasPreview ? 0 : -(templateNativeWidth * (1 - previewScale * previewZoom)),
  transition: isPanning.current ? 'none' : 'transform 0.1s',
}}>
              <div id="pos-receipt-preview-area">
                {cart.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                    🧾 Cart is empty<br/>
                    <span style={{ fontSize: 10, fontWeight: 400 }}>Add items to see receipt preview</span>
                  </div>
                ) : (isCanvasPreview && translatedTemplate) ? (
                  <BilingualInvoiceRenderer
                    template={withPosColumns(patchLiveClientBlock(
                      translatedTemplate,
                      includeFields,
                      clientName,
                      clientEmail,
                      clientPhone,
                      clientAddress,
                      clientGSTIN
                    ))}
                    clientName={includeFields.name ? (clientName || 'Walk-in Customer') : ''}
                    clientEmail={includeFields.email ? clientEmail : ''}
                    clientPhone={includeFields.phone ? clientPhone : ''}
                    clientAddress={includeFields.address ? clientAddress : ''}
                    buyerTaxId={includeFields.taxId ? (isB2B ? clientGSTIN : undefined) : undefined}
                    dynamicValuesAr={{ clientName: (window as any).__pos_client_name_ar || clientName }}
                    isB2B={isB2B}
                    taxBreakdown={taxBreakdown}
                    invoiceDate={invoiceDate}
                    invoiceTime={invoiceTime}
                    notes={notes}
                    lineItems={cart.map(item => ({
                      id: item.id,
                      description: item.name,
                      qty: item.qty,
                      rate: item.rate,
                      arabicName: (item as any).arabicName,
                      taxRate: getEffectiveTaxRate(item),
                      taxName: item.taxName,
                      b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
                      b2bTaxName: isB2B ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || item.taxName || customTaxScheme?.name) : undefined,
                      taxInclusive: item.taxInclusive, ...posColValues(item),
                      __customAr: (item as any).customColAr,
                    }))}
                    taxLines={taxLines}
                    discountLines={discountLines}
                    autoDiscountAmount={autoDiscountRule && autoDiscountRule.visibleOnBill ? autoDiscountAmount : 0}
                    autoDiscountLabel={autoDiscountRule?.label || 'Loyalty'}
                    discountLinesAr={(window as any).__pos_discount_lines_ar}
                    taxLinesAr={(window as any).__pos_tax_lines_ar}
                    sym={sym}
                    shippingCost={shippingCost}
                    invoiceStatus={showStatus ? invoiceStatus : undefined}
                    translateTargetLang={posTranslateLang}
                    isBilingual={true}
                    bilingualLayout="side"
                    isThermal={false}
                    showAmountInWords={showAmountInWords}
                    amountInWordsTranslated={showAmountInWords ? (window as any).__pos_amount_words_ar : ''}
                    // Without these, BilingualInvoiceRenderer has no scheme context
                    // and always falls back to the blended "Total Tax" line — same
                    // props QuickInvoiceView already passes into its own instance.
                    taxScheme={posTaxScheme}
                    sellerRegion={sellerRegion}
                    buyerRegion={buyerRegion}
                    taxBreakdownAr={translatedTemplate ? (window as any).__pos_tax_breakdown_ar : undefined}
                    savedElements={
                      placeholderQrDataUrls ? [
                        { id: 'invoice-label-preview', type: 'text', textContent: 'Scan to view invoice' },
                        { id: 'invoice-qr-preview', type: 'image', textContent: placeholderQrDataUrls.invoice },
                        ...(enableTracking ? [
                          { id: 'token-label-preview', type: 'text', textContent: 'Scan to track order' },
                          { id: 'token-qr-preview', type: 'image', textContent: placeholderQrDataUrls.token },
                        ] : []),
                      ] : null
                    }
                    showTrackingQR={enableTracking}
                    invoiceNumber={lastSavedInvoice?.number || invoiceNumberOverride}
                    zoom={previewZoom}
                  />
                ) : (selectedTemplate?.pageSize === 'A4' || selectedTemplate?.pageSize === 'LETTER') ? (() => {
                  const previewPageConfig = PAGE_PRESETS[selectedTemplate.pageSize as 'A4' | 'LETTER'];
                  const rawPreviewElements = (translatedTemplate || selectedTemplate)?.elements || [];
                  // Patch the table with the LIVE cart — the raw template's table
                  // always has empty rows, so without this the canvas preview
                  // never reflects items added/removed from the cart.
                  const previewDiscounts = discountLines.filter(d => d.rate > 0);
                  if (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0) {
                    const effectiveSubtotal = subtotal - discTotal;
                    const impliedRate = effectiveSubtotal > 0 ? (autoDiscountAmount / effectiveSubtotal) * 100 : 0;
                    previewDiscounts.push({ label: autoDiscountRule.label || 'Loyalty Discount', rate: parseFloat(impliedRate.toFixed(2)), _isAuto: true });
                  }
                  const previewElements = rawPreviewElements
                    // Strip the already-saved auto-QR elements before rebuilding
                    // the preview's own invoice/token QR block below. Without
                    // this, once a bill has been saved once, lastSyncedElements
                    // already contains the REAL invoice-qr-<ts>/invoice-label-<ts>/
                    // token-qr-<ts>/token-label-<ts> elements from handleSave — the
                    // code below finds those (to steal their textContent/PNG) and
                    // pushes a SECOND copy as preview-invoice-qr/preview-token-qr
                    // without ever removing the originals it just found, producing
                    // two "Scan to view invoice" / two "Token: X" QR blocks.
                    .filter((el: any) =>
                      !el.id?.startsWith('invoice-qr-') &&
                      !el.id?.startsWith('invoice-label-') &&
                      !el.id?.startsWith('token-qr-') &&
                      !el.id?.startsWith('token-label-')
                    )
                    .map((el: any) => {
                    if (el.type === 'table') {
                      return {
                        ...el,
                        rows: cart.map((item, i) => ({
                          id: `preview-r-${i}`,
                          values: {
                            description: item.name,
                            qty: item.qty,
                            rate: item.rate,
                            total: item.qty * item.rate, ...posColValues(item),
                            arabicName: (item as any).arabicName || '',
                            taxRate: getEffectiveTaxRate(item),
                            taxName: item.taxName,
                            b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
                            b2bTaxName: isB2B ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || item.taxName || customTaxScheme?.name) : undefined,
                            taxInclusive: !!item.taxInclusive,
                          },
                        })),
                        taxes: taxLines.filter(t => t.rate > 0),
                        discounts: previewDiscounts,
                        columns: buildPosColumns(el.columns),
                        shippingCost: shippingCost > 0 ? shippingCost : undefined,
                      };
                    }
                    // Resolve tokens in text elements
                    return resolveElementTokens(el, {
                      invoiceNumber: lastSavedInvoice?.number || invoiceNumberOverride || 'PREVIEW',
                      staffName: waiterName || authUser?.name?.trim() || 'Staff',
                      staffNameAr: (window as any).__pos_staff_name_ar,
                      clientDetailsEn: buildClientBlock(
                        { name: clientName, phone: clientPhone, email: clientEmail, address: clientAddress, taxId: clientGSTIN },
                        includeFields,
                        Boolean(translatedTemplate)
                      ).en,
                      clientDetailsTr: buildClientBlock(
                        { name: clientName, phone: clientPhone, email: clientEmail, address: clientAddress, taxId: clientGSTIN },
                        includeFields,
                        Boolean(translatedTemplate)
                      ).tr,
                      clientNameEn: includeFields.name ? clientName : '',
                      readyBy,
                      notes,
                    });
                  });

                  previewElements.splice(0, previewElements.length, ...tightenTable(previewElements));

                  // A4/Letter QR preview.
                  //
                  // BEFORE SAVE:
                  // show the faint dummy QR codes so the cashier can see
                  // where the real QR codes will appear.
                  //
                  // AFTER SAVE:
                  // use the REAL QR images already stored in lastSyncedElements.
                  // Those QR images were generated from the backend invoice
                  // public_token and the backend tracking token.

                  if (enableTracking) {
                    const qrSize = 120;
                    // Align to the ACTUAL left/right edges of the NOTES box
                    // (its x and x+width), not the raw page margin — matches
                    // the saved-invoice alignment fix.
                    const tableForAlign = previewElements.find((el: any) => el.type === 'table');
                    const fallbackMargin = 10;
                    const boxLeft = tableForAlign ? tableForAlign.x : fallbackMargin;
                    const boxRight = tableForAlign ? tableForAlign.x + tableForAlign.width : previewPageConfig.width - fallbackMargin;
                    const boxMid = (boxLeft + boxRight) / 2;
                    const margin = fallbackMargin;
                    const halfWidth = boxMid; // kept for the rowY calc below; represents the label split point
                    const invoiceQrX = boxLeft;
                    const tokenQrX = boxRight - qrSize;

                    // 1. Read real invoice QR from lastSyncedElements (generated on save)
                    const realInvoiceQr = lastSyncedElements.find(
                      (el: any) =>
                        el.type === 'image' &&
                        el.id?.startsWith('invoice-qr-') &&
                        el.textContent
                    );

                    // 2. Read real tracking QR from lastTokenQR or lastSyncedElements
                    const realTokenQr = lastSyncedElements.find(
                      (el: any) =>
                        el.type === 'image' &&
                        el.id?.startsWith('token-qr-') &&
                        el.textContent
                    );

                    const invoiceQrSrc =
                      realInvoiceQr?.textContent ||
                      placeholderQrDataUrls?.invoice ||
                      '';

                    const tokenQrSrc =
                      lastTokenQR?.qr ||
                      realTokenQr?.textContent ||
                      placeholderQrDataUrls?.token ||
                      '';

                    const invoiceIsReal = Boolean(realInvoiceQr?.textContent);
                    const tokenIsReal = Boolean(lastTokenQR?.qr || realTokenQr?.textContent);

                    // 3. Display real token string (e.g. "Token: V104 — Scan to track order")
                    const tokenText = lastTokenQR
                      ? `Token: ${lastTokenQR.token} — Scan to track order`
                      : (
                          lastSyncedElements.find(
                            (el: any) =>
                              el.type === 'text' &&
                              el.id?.startsWith('token-label-')
                          )?.textContent ||
                          'Scan to track order'
                        );

                    if (invoiceQrSrc || tokenQrSrc) {
                      // Both pairs share ONE row Y — computed once, before either
                      // is pushed — so neither pushes the other one down.
                      const rowY =
                        previewElements.reduce(
                          (max: number, el: any) =>
                            Math.max(
                              max,
                              (el.y || 0) + (el.height || 0)
                            ),
                          0
                        ) + 20;

                      if (invoiceQrSrc) {
                        previewElements.push(
                          {
                            id: 'preview-invoice-label',
                            type: 'text',
                            x: boxLeft,
                            y: rowY + qrSize + 4,
                            width: boxMid - boxLeft,
                            height: 18,
                            zIndex: 99,
                            opacity: invoiceIsReal ? 100 : 55,
                            rotation: 0,
                            textContent: 'Scan to view invoice',
                            fontSize: 10,
                            fontFamily: 'Inter',
                            fontWeight: '600',
                            color: '#374151',
                            textAlign: 'left',
                            lineHeight: 1.3,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                          {
                            id: 'preview-invoice-qr',
                            type: 'image',
                            x: invoiceQrX,
                            y: rowY,
                            width: qrSize,
                            height: qrSize,
                            zIndex: 99,
                            opacity: invoiceIsReal ? 100 : 55,
                            rotation: 0,
                            textContent: invoiceQrSrc,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            borderWidth: 0,
                          }
                        );
                      }

                      if (tokenQrSrc) {
                        previewElements.push(
                          {
                            id: 'preview-token-label',
                            type: 'text',
                            x: boxMid,
                            y: rowY + qrSize + 4,
                            width: boxRight - boxMid,
                            height: 18,
                            zIndex: 100,
                            opacity: tokenIsReal ? 100 : 55,
                            rotation: 0,
                            textContent: tokenText,
                            fontSize: 10,
                            fontFamily: 'Inter',
                            fontWeight: '600',
                            color: '#6366f1',
                            textAlign: 'right',
                            lineHeight: 1.3,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                          {
                            id: 'preview-token-qr',
                            type: 'image',
                            x: tokenQrX,
                            y: rowY,
                            width: qrSize,
                            height: qrSize,
                            zIndex: 100,
                            opacity: tokenIsReal ? 100 : 55,
                            rotation: 0,
                            textContent: tokenQrSrc,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            borderWidth: 0,
                          }
                        );
                      }
                    }
                  }

                  const maxElementBottom = previewElements.reduce(
                    (max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)),
                    0
                  );
                  const dynamicPageHeight = Math.max(previewPageConfig.height, maxElementBottom + 40);

                  const lockedIds = previewElements.map((el: any) => el.id);
                  return (
                    <div style={{ width: '100%', position: 'relative' }}>
                      {/* Full CanvasWorkspace chrome — top toolbar and tip bubble are no
                          longer stripped, so its own zoom={canvasZoom}/setZoom={setCanvasZoom}
                          and pageBehavior controls in the top rail are usable here. */}
                      <CanvasWorkspace
                        readOnlyPreview={true}
                        pageConfig={previewPageConfig}
                        computedHeight={dynamicPageHeight}
                        pageCount={1}
                        pageBehavior={canvasPageBehavior}
                        setPageBehavior={setCanvasPageBehavior}
                        zoom={canvasZoom}
                        setZoom={setCanvasZoom}
                        showGridLines={false}
                        elements={previewElements}
                        selectedElementId={null}
                        inlineEditingId={null}
                        inlineEditText=""
                        setInlineEditText={noopCanvasHandler}
                        lockedElementIds={lockedIds}
                        dragState={null}
                        qrDataUrls={canvasQrDataUrls}
                        isDrawingMode={false}
                        isDrawingActive={false}
                        currentPathPoints={[]}
                        currentPenColor="#000000"
                        currentPenWidth={2}
                        bilingualMode={!!translatedTemplate}
                        bilingualDirection="en-ar"
                        translateTargetLang={posTranslateLang}
                        activeCurrency={activeCurrency}
                        currencyDisplayMode={(localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'}
                        computeTableTotals={(el: CanvasElement) => {
                          const frozen = getFrozenTotals([el]) || {
                            subtotal: 0, productTaxTotal: 0, taxTotal: 0, shipping: 0, autoDiscountAmount: 0, grandTotal: 0,
                          };
                          return {
                            subtotal: frozen.subtotal,
                            productTax: frozen.productTaxTotal,
                            tax: frozen.taxTotal,
                            discount: frozen.autoDiscountAmount,
                            shipping: frozen.shipping,
                            taxBreakdown: taxBreakdown,
                            total: frozen.grandTotal,
                          };
                        }}
                        formatSerialNumber={formatCanvasSerialNumber}
                        canvasRef={canvasPreviewCanvasRef}
                        handleMouseDown={noopCanvasHandler}
                        handleDeleteElement={noopCanvasHandler}
                        handleInlineEditStart={noopCanvasHandler}
                        handleInlineEditSave={noopCanvasHandler}
                        handleElementDoubleClick={noopCanvasHandler}
                        commitCurrentState={noopCanvasHandler}
                        updateElementProps={noopCanvasHandler}
                        handleCanvasMouseDown={noopCanvasHandler}
                        handleCanvasMouseMove={noopCanvasHandler}
                        handleCanvasMouseUp={noopCanvasHandler}
                        onBackgroundClick={noopCanvasHandler}
                      />
                    </div>
                  );
                })() : (() => {
                  const cleanManualDiscounts = discountLines.filter(d => !d._isAuto && !/loyalty/i.test(d.label || ''));
                  const loyaltyLabelAr = (window as any).__pos_discount_lines_ar?.find((x: any) => x.label === autoDiscountRule?.label)?.labelAr;
                  const previewDiscountLines = (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0)
                    ? [
                        ...cleanManualDiscounts,
                        {
                          label: autoDiscountRule.label || 'Loyalty Discount',
                          rate: parseFloat(((autoDiscountAmount / Math.max(subtotal, 1)) * 100).toFixed(2)),
                          amount: autoDiscountAmount,
                          translatedLabel: loyaltyLabelAr,
                          _isAuto: true,
                        },
                      ]
                    : cleanManualDiscounts;

                  return (
                    <POSReceiptRenderer
                      clientNameAr={(window as any).__pos_client_name_ar || undefined}
                      clientLineAr={(window as any).__pos_client_address_ar || undefined}
                      template={(() => {
                        const base = translatedTemplate || selectedTemplate;
                        if (!base) return base;
                        const cashierName = waiterName || authUser?.name?.trim() || 'Staff';
                        return {
                          ...base,
                          elements: base.elements.map((el: any) => {
                            if (el.type === 'signature') {
                              const arStaff = (window as any).__pos_staff_name_ar || cashierName;
                              return {
                                ...el,
                                signatureName: (el.signatureName || cashierName).replace(/\{staff_name\}/gi, cashierName),
                                arabicSignatureName: (el.arabicSignatureName || arStaff).replace(/\{staff_name\}/gi, arStaff),
                              };
                            }
                            if (el.type !== 'text' || !el.textContent) return el;
                            let t = el.textContent.replace(/\{staff_name\}/gi, cashierName);
                            if (tableNumber) t = t.replace(/TABLE:\s*[\w#]+/gi, `TABLE: ${tableNumber}`);
                            if (covers)      t = t.replace(/COVERS:\s*[\w#]+/gi, `COVERS: ${covers}`);
                            if (kotNumber)   t = t.replace(/KOT:\s*[\w#]+/gi, `KOT: #${kotNumber}`);
                            t = t.replace(/Check-in:\s*[\d\/\.\-]+/i, `Check-in: ${new Date(checkInDate + 'T00:00:00').toLocaleDateString()}`);
                            if (roomNumber) t = t.replace(/Room:\s*[\w\d]+/i, `Room: ${roomNumber}`);
                            if (guestName)  t = t.replace(/Guest:\s*\[?[\w\s]*\]?/i, `Guest: ${guestName}`);
                            if (checkOutDate) t = t.replace(/Check-out:\s*_*/i, `Check-out: ${new Date(checkOutDate + 'T00:00:00').toLocaleDateString()}`);
                            t = t.replace(/\{invoice_number\}/g, `PREVIEW`);
                            const posCustName = includeFields.name ? (clientName?.trim() || 'Walk-in Customer') : '';
                            const patientVal = dynamicValues.patient?.trim() || '';
                            t = t.replace(/(Customer|Client):\s*\[Name\]/gi, `$1: ${posCustName}`);
                            t = t.replace(/Patient:\s*\[(?:Name)?\]/gi, patientVal ? `Patient: ${patientVal}` : 'Patient: ______');
                            t = t.replace(/Pet:\s*\[(?:Name)?\]/gi, dynamicValues.pet?.trim() ? `Pet: ${dynamicValues.pet.trim()}` : 'Pet: ______');
                            t = t.replace(/Student:\s*\[(?:Name)?\]/gi, dynamicValues.student?.trim() ? `Student: ${dynamicValues.student.trim()}` : 'Student: ______');
                            if (
                              t.toLowerCase().startsWith('bill to:') ||
                              t.toLowerCase().startsWith('client:') ||
                              t.toLowerCase().startsWith('customer:') ||
                              /customer\s*:/i.test(t) ||
                              /order:.*\ncustomer:/i.test(t)
                            ) {
                              const lines = t.split('\n');
                              t = lines.map((line: string) => {
                                if (/^customer\s*:/i.test(line)) return `Customer: ${clientName || 'Walk-in Customer'}`;
                                if (/^bill\s*to\s*:/i.test(line) || /^client\s*:/i.test(line)) return `${line.split(':')[0]}: ${clientName || 'Walk-in Customer'}`;
                                if (/^ph\s*:/i.test(line)) {
                                  const digits = clientPhone.replace(/\D/g, '');
                                  return digits.length >= 6 ? `Ph: ${clientPhone}` : line;
                                }
                                if (/^email\s*:/i.test(line)) return clientEmail.includes('@') ? `Email: ${clientEmail}` : line;
                                if (/^address\s*:/i.test(line)) return clientAddress ? `Address: ${clientAddress}` : line;
                                if (/^ready\s*by\s*:/i.test(line)) return readyBy ? `Ready By: ${readyBy}` : line;
                                if (/^mob\s*:/i.test(line) || /^mobile\s*:/i.test(line) || /^phone\s*:/i.test(line)) { const digits = clientPhone.replace(/\D/g, ''); return digits.length >= 6 ? `${line.split(':')[0]}: ${clientPhone}` : line; }
                                return line;
                              }).join('\n');
                            }
                            if (!showStatus) t = t.replace(/\b(PAID|UNPAID|DRAFT|OVERDUE|CANCELLED)\b/g, '');
                            let arabicPatched = el.arabicContent || '';
                            if (arabicPatched) {
                              arabicPatched = arabicPatched.replace(/\{invoice_number\}/g, 'PREVIEW');
                            }
                            return { ...el, textContent: t, ...(arabicPatched ? { arabicContent: arabicPatched } : {}) };
                          }),
                        };
                      })()}
                      clientName={includeFields.name ? (clientName || 'Walk-in Customer') : ''}
                      clientEmail={includeFields.email ? clientEmail : ''}
                      clientPhone={includeFields.phone ? clientPhone : ''}
                      clientAddress={includeFields.address ? clientAddress : ''}
                      buyerTaxId={includeFields.taxId ? (isB2B ? clientGSTIN : undefined) : undefined}
                      isB2B={isB2B}
                      invoiceDate={invoiceDate}
                      invoiceTime={invoiceTime}
                      notes={notes}
                      lineItems={cart.map(item => ({
                        id: item.id,
                        description: item.name,
                        qty: item.qty,
                        rate: item.rate,
                        arabicName: (item as any).arabicName,
                        taxRate: getEffectiveTaxRate(item),
                        taxName: item.taxName,
                        b2bTaxRate: isB2B ? item.b2bTaxRate : undefined,
                        b2bTaxName: isB2B ? (item.b2bTaxName || (customTaxScheme as any)?.b2b_name || item.taxName || customTaxScheme?.name) : undefined,
                        taxInclusive: item.taxInclusive, ...posColValues(item),
                        customColAr: (item as any).customColAr,
                      }))}
                      taxLines={taxLines}
                      discountLines={previewDiscountLines}
                      sym={sym}
                      shippingCost={shippingCost}
                      invoiceStatus={showStatus ? invoiceStatus : undefined}
                      translateTargetLang={posTranslateLang}
                      isBilingual={!!translatedTemplate}
                      isThermal={(selectedTemplate?.pageSize || '') === 'THERMAL_80MM'}
                      dynamicValues={dynamicValues}
                      dynamicValuesAr={bilingualEnabled ? translatedValues : undefined}
                      dynamicFieldLabelsAr={bilingualEnabled ? dynamicFieldLabelsAr : undefined}
                      showTokenQRPlaceholder={enableTracking}
                      extraColumns={posColumns}
                      showWalkIn={true}
                      showAmountInWords={showAmountInWords}
                      amountInWords={showAmountInWords ? amountToWords(grandTotal, activeCurrency) : undefined}
                      amountInWordsTranslated={showAmountInWords && translatedTemplate ? (window as any).__pos_amount_words_ar : undefined}
                      productTaxTotal={productTaxTotal}
                      taxBreakdown={taxBreakdown}
                      taxBreakdownAr={translatedTemplate ? (window as any).__pos_tax_breakdown_ar : undefined}
                      netSubtotal={subtotal}
                      notesAr={translatedTemplate ? (window as any).__pos_notes_ar : undefined}
                      rowLabelsAr={translatedTemplate ? (window as any).__pos_row_labels_ar : undefined}
                      taxLinesAr={translatedTemplate ? (window as any).__pos_tax_lines_ar : undefined}
                      discountLinesAr={translatedTemplate ? (window as any).__pos_discount_lines_ar : undefined}
                    />
                  );
                })()}
              </div>
            </div>
          </div>
          </div>
        );
      })()}
</div>
{/* Payment-wait modal for card/UPI — QR + live polling */}
{paymentWaitModal && (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <style>{`@keyframes posPaySpin { to { transform: rotate(360deg); } }`}</style>
    <div style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', maxWidth: 360, width: '100%', textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>
      {paymentFailed ? (
        <>
          <div style={{ fontSize: 36, marginBottom: 6 }}>❌</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#dc2626', marginBottom: 4 }}>
            Payment Failed — {paymentWaitModal.invoiceNumber}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 18 }}>
            Nothing was printed or sent. Retry with this customer, or discard and move on.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPaymentFailed(false)}
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              🔄 Retry
            </button>
            <button onClick={discardPendingInvoice}
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#fee2e2', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              🗑 Discard
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>
            Waiting for payment — {paymentWaitModal.invoiceNumber}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16 }}>
            Ask the customer to scan and pay. Nothing prints or sends until the gateway confirms.
          </div>
          <img src={paymentWaitModal.qr} alt="Payment QR" style={{ width: 220, height: 220, borderRadius: 14, border: '2px solid #e2e8f0', margin: '0 auto 14px', display: 'block', opacity: regeneratingQR ? 0.4 : 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: '#6366f1', fontWeight: 700, marginBottom: 18, background: '#eef2ff', borderRadius: 99, padding: '6px 0' }}>
            <div style={{ width: 12, height: 12, border: '2px solid #c7d2fe', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'posPaySpin 0.8s linear infinite' }} />
            {paymentPolling ? 'Checking for payment...' : 'Connecting...'}
          </div>

          {/* Primary actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <button onClick={async () => { setPaymentWaitModal(null); await confirmAndPrintRef.current(); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '14px 8px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(22,163,74,0.3)' }}>
              <CheckCircle size={16} /> {paymentMode === 'upi' ? 'UPI Received' : 'Card Approved'}
            </button>
            <button onClick={switchToCashAndConfirm}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '14px 8px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(59,130,246,0.3)' }}>
              <Banknote size={16} /> Switch to Cash
            </button>
          </div>

          {/* Secondary actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            <button onClick={() => { setEditingPendingInvoice(paymentWaitModal); setPaymentWaitModal(null); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 4px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
              <Plus size={14} /> Add Item
            </button>
            <button onClick={() => navigator.clipboard.writeText(paymentWaitModal.link)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 4px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#f8fafc', color: '#374151', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
              🔗 Copy Link
            </button>
            <button onClick={regeneratePaymentQR} disabled={regeneratingQR}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 4px', borderRadius: 12, border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', fontSize: 10, fontWeight: 700, cursor: regeneratingQR ? 'not-allowed' : 'pointer', opacity: regeneratingQR ? 0.6 : 1 }}>
              🔄 {regeneratingQR ? 'Regenerating' : 'New QR'}
            </button>
            <button
              onClick={() => window.open(outletDisplayToken ? `/display?token=${outletDisplayToken}&register_id=${registerId}` : '#', '_blank')}
              disabled={!outletDisplayToken}
              title={outletDisplayToken ? 'Open Counter Display' : 'No display token found for this outlet'}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 4px', borderRadius: 12, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 10, fontWeight: 700, cursor: outletDisplayToken ? 'pointer' : 'not-allowed', opacity: outletDisplayToken ? 1 : 0.5 }}>
              🖥 Display
            </button>
          </div>

          {/* Footer actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <button onClick={() => setPaymentWaitModal(null)}
              style={{ padding: '11px 0', borderRadius: 12, border: 'none', background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✓ Close (pay later)
            </button>
            <button onClick={discardPendingInvoice}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', borderRadius: 12, border: 'none', background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Trash2 size={13} /> Discard
            </button>
          </div>
          <div style={{ fontSize: 9, color: '#cbd5e1', textAlign: 'center' }}>"Close (pay later)" keeps it unpaid in Bills. "Discard" removes it entirely.</div>
        </>
      )}
    </div>
  </div>
)}

{/* Token QR Modal for restaurants */}
{lastTokenQR && (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
    backdropFilter: 'blur(4px)', zIndex: 200,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  }} onClick={() => setLastTokenQR(null)}>
    <div style={{
  background: '#fff', borderRadius: 24, padding: '28px 20px', maxWidth: 480,
  width: '100%', textAlign: 'center',
  boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
  boxSizing: 'border-box',
}} onClick={e => e.stopPropagation()}>
      <div style={{ fontSize: 64, fontWeight: 900, color: '#6366f1', letterSpacing: 6, marginBottom: 4 }}>
        {lastTokenQR.token}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 14 }}>
        Order Token — Ask customer to scan
      </div>
      <img src={lastTokenQR.qr} alt="QR" style={{ width: 260, height: 260, borderRadius: 14, border: '2px solid #e2e8f0', margin: '8px auto', display: 'block' }} />
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '6px 10px' }}>
        Customer scans → gets notified when ready
      </div>
      {/* Phone input for WhatsApp */}
<div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'center' }}>
  <PhoneInput
    key={lastTokenQR?.token}
    value={clientPhone}
    onChange={setClientPhone}
    defaultCountry={outletPhoneCountry}
    inputStyle={{ flex: 1, padding: '8px 12px', borderRadius: '0 10px 10px 0', border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 600, outline: 'none', color: '#1e293b' }}
  />
</div>
{/* Email fallback — for a customer who forgot their phone, or prefers email */}
<div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
  <input
    type="email"
    value={clientEmail}
    onChange={e => setClientEmail(e.target.value)}
    placeholder="Email address (optional)"
    style={{ width: '100%', maxWidth: 320, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 600, outline: 'none', color: '#1e293b', boxSizing: 'border-box' }}
  />
</div>

<div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>

  {/* Row 1: WhatsApp + Email */}
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={async () => {
  const cleanPhoneModal = getCleanE164Phone(clientPhone, outletPhoneCountry || 'IN');
  const full = cleanPhoneModal.replace(/\D/g, '');
  if (!full) return alert('Enter a valid phone number');
  if (!lastSavedInvoice) return alert('No saved invoice found.');
  const waitUrl = `${APP_URL}/wait?token=${lastTokenQR!.token}`;
  const activeBrand = companyName || authUser?.name || 'Our Company';
  const caption = `🎟 Your order token: *${lastTokenQR!.token}*\n\nTrack your order here:\n${waitUrl}\n\nWe'll notify you when it's ready! 🔔\n\n— ${activeBrand}`;
  try {
    const token = getStoredToken();
    const pdfBlob = await buildPOSPdfBlob();
    if (!pdfBlob) throw new Error('PDF build failed');
    const base64 = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onloadend = () => resolve((reader.result as string).split(',')[1]); reader.onerror = reject; reader.readAsDataURL(pdfBlob); });
    const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ phone: full, base64, filename: `Receipt_${lastSavedInvoice.number}.pdf`, caption }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || data?.detail || `WhatsApp send failed (${res.status})`);
    }
    postLog('POS_TOKEN_WHATSAPP_SENT', `Order token ${lastTokenQR!.token} + receipt PDF sent via WhatsApp to ${clientName || 'Walk-in'} | ${clientPhone}`);
    alert('✅ Sent!'); setLastTokenQR(null); clearCart();
  } catch (e: any) { alert(`⚠️ WhatsApp not sent: ${e?.message || 'unknown error'}`); }
}} style={{ flex: 2, background: '#22c55e', color: '#fff', border: 'none', borderRadius: 14, padding: '16px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 14px rgba(34,197,94,0.3)' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      Send on WhatsApp
    </button>
    <button onClick={async () => {
  if (!clientEmail.includes('@')) return alert('Enter a valid email address');
  if (!lastSavedInvoice) return alert('No saved invoice found.');
  const waitUrl = `${APP_URL}/wait?token=${lastTokenQR!.token}`;
  try {
    const tkn = getStoredToken();
    const pdfBlob = await buildPOSPdfBlob();
    if (!pdfBlob) throw new Error('PDF build failed');
    const pdfBase64 = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onloadend = () => resolve((reader.result as string).split(',')[1]); reader.onerror = reject; reader.readAsDataURL(pdfBlob); });
    const activeBrand = companyName || authUser?.name || 'Our Company';
    const res = await fetch(`${BACKEND}/email/send-invoice`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) }, body: JSON.stringify({ invoice_id: Number(lastSavedInvoice.id), to_email: clientEmail, attach_pdf: true, pdf_base64: pdfBase64, pdf_filename: `${lastSavedInvoice.number}.pdf`, subject: `Your Order Token: ${lastTokenQR!.token} — ${activeBrand}`, message: `Your order token is: ${lastTokenQR!.token}\n\nTrack your order here: ${waitUrl}\n\nWe'll notify you when it's ready!\n\n— ${activeBrand}` }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || data?.detail || `Email send failed (${res.status})`);
    }
    postLog('POS_TOKEN_EMAIL_SENT', `Order token ${lastTokenQR!.token} + receipt PDF emailed to ${clientName || 'Walk-in'} | ${clientEmail}`);
    alert('✅ Email sent with receipt attached!');
  } catch (e: any) { alert(`⚠️ Email not sent: ${e?.message || 'unknown error'}`); }
}} style={{ flex: 1, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 14, padding: '16px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 4px 14px rgba(59,130,246,0.3)' }}>
      ✉ Email
    </button>
  </div>

  {/* Row 2: Print + Save QR + Add Items */}
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
    <button onClick={() => setShowPrinterPicker(true)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
      🖨 Print <ChevronDown size={13} strokeWidth={2.5} />
    </button>
    <button onClick={() => { const a = document.createElement('a'); a.href = lastTokenQR!.qr; a.download = `token_${lastTokenQR!.token}.png`; a.click(); }}
      style={{ flex: 1, background: '#f5f3ff', color: '#6366f1', border: 'none', borderRadius: 12, padding: '13px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>⬇ Save QR</button>
    <button onClick={() => { setLastTokenQR(null); setTimeout(() => searchRef.current?.focus(), 100); }}
      style={{ flex: 1, background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: 12, padding: '13px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Add Items</button>
  </div>

  {/* Row 3: Done + Discard */}
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={() => { setLastTokenQR(null); clearCart(); }}
      style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '13px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>✓ Done</button>
   {perms.canPOSDiscardBill && (
    <button onClick={async () => {
  if (!lastSavedInvoice) return;
  const reason = window.prompt('Reason for discarding this bill? (required)');
  if (reason === null) return; // user hit cancel on the prompt
  if (!reason.trim()) { alert('A reason is required to discard a bill.'); return; }
  try {
    const tkn = getStoredToken();
    const res = await fetch(`${BACKEND}/invoices/${lastSavedInvoice.id}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
      body: JSON.stringify({ reason: reason.trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Cancel failed');
    }
    const cancelled = await res.json();
    // Update the bill in state as Cancelled, don't remove it — it's a permanent record now.
    setBills(prev => prev.map(b => b.id === lastSavedInvoice.id ? { ...b, status: 'cancelled' as any } : b));
    postLog('POS_BILL_CANCELLED', `POS receipt ${lastSavedInvoice.number} cancelled by ${authUser?.name || 'Staff'}. Reason: ${reason.trim()}`);

    // Toast — same style as the save-success toast
    const discardToast = document.createElement('div');
    discardToast.innerText = `🗑 Bill ${lastSavedInvoice.number} discarded`;
    discardToast.style.cssText = `position:fixed;bottom:28px;left:50%;transform:translateX(-50%);background:#dc2626;color:#fff;padding:12px 24px;border-radius:12px;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 8px 24px rgba(220,38,38,0.4);`;
    document.body.appendChild(discardToast);
    setTimeout(() => {
      discardToast.style.opacity = '0';
      discardToast.style.transition = 'opacity 0.4s';
      setTimeout(() => document.body.removeChild(discardToast), 400);
    }, 3000);
  } catch (e: any) {
    alert(`Failed to discard: ${e.message}`);
    return;
  }
  setLastTokenQR(null); setLastSavedInvoice(null); clearCart();
}} style={{ flex: 1, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 12, padding: '13px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🗑 Discard</button>
  )}
</div>

</div>
</div></div>
)}
      {/* Printer connection picker — opens on Print instead of jumping straight to the OS dialog */}
      {showPrinterPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowPrinterPicker(false)}>
          <div style={{ background: '#fff', borderRadius: 20, padding: '24px 22px', maxWidth: 340, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Print Receipt</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>Choose a connection, or use the dialog instead.</div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button onClick={() => { setUseUSBPrinter(false); localStorage.setItem('cloudairambo_pos_usb_print', 'false'); }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 6px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: !useUSBPrinter ? '#eef2ff' : '#fff', borderColor: !useUSBPrinter ? '#6366f1' : '#e5e7eb', color: !useUSBPrinter ? '#4f46e5' : '#6b7280' }}>
                <span style={{ fontSize: 18 }}>📶</span> WiFi
              </button>
              <button onClick={() => { setUseUSBPrinter(true); localStorage.setItem('cloudairambo_pos_usb_print', 'true'); }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 6px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: useUSBPrinter ? '#eef2ff' : '#fff', borderColor: useUSBPrinter ? '#6366f1' : '#e5e7eb', color: useUSBPrinter ? '#4f46e5' : '#6b7280' }}>
                <span style={{ fontSize: 18 }}>🔌</span> USB
              </button>
            </div>

            {useUSBPrinter && (
              <>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
                  {usbConnected ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected'}
                </div>
                {!usbConnected && (
                  <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }}
                    style={{ width: '100%', fontSize: 11, fontWeight: 700, padding: '8px 8px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', marginBottom: 10 }}>
                    🔌 Connect USB Printer
                  </button>
                )}
              </>
            )}

            <button onClick={async () => { setShowPrinterPicker(false); await executeTokenModalPrint(); }}
              style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', marginBottom: 8 }}>
              🖨 Print
            </button>
            <button onClick={async () => { setShowPrinterPicker(false); await executeTokenModalPrint(true); }}
              style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Print via Dialog instead
            </button>
          </div>
        </div>
      )}
      {/* Scanner modal */}
      {showScanner && (
        <QuickScannerModal
          onAddItem={(desc, qty, rate) => handleScanAdd(desc, qty, rate)}
          onClose={() => setShowScanner(false)}
          onSessionStart={(sessionId) => setActiveScanSession(sessionId)}
        />
      )}

      {/* Tax ID Legal Disclaimer Modal */}
      {showTaxDisclaimerModal && (
        <div 
          onClick={() => setShowTaxDisclaimerModal(false)} 
          style={{ 
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', 
            backdropFilter: 'blur(3px)', zIndex: 9999, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 
          }}
        >
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ 
              background: '#fff', borderRadius: 16, padding: '22px 24px', 
              maxWidth: 440, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' 
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>⚖️</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Tax ID Verification Notice</span>
              </div>
              <button 
                onClick={() => setShowTaxDisclaimerModal(false)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#991b1b', marginBottom: 4 }}>
                ⚠️ Do NOT rely on or trust this badge for legal or tax compliance:
              </div>
              <ul style={{ fontSize: 11, color: '#7f1d1d', lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                <li><strong>Format check only:</strong> It only tests character structure and length. It has <strong>no connection</strong> to government databases.</li>
                <li><strong>Filtering can be wrong:</strong> Automated filters can make mistakes, pass fake numbers, or misidentify edge cases.</li>
                <li><strong>Not a legal verification:</strong> A green badge does <strong>not</strong> mean the company is real, active, or legally authorized to charge taxes.</li>
              </ul>
            </div>

            <p style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginBottom: 16 }}>
              You must independently authenticate and verify all buyer tax numbers on the <strong>official government tax portal</strong> (e.g., GST Portal, IRS, HMRC, VIES) before claiming tax credits or exemptions.
            </p>

            <button 
              onClick={() => setShowTaxDisclaimerModal(false)} 
              style={{ 
                width: '100%', padding: '10px 0', background: '#3b82f6', color: '#fff', 
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' 
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* Spin animation */}
<style>{`
  @keyframes spin { to { transform: rotate(360deg); } }
  .pos-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .pos-scroll::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    display: none;
  }
`}</style>
    </div>
  );
}
